// GENERATED by scripts/gen-client.py from the tool manifest — do not edit by hand. `make gen-client` regenerates it;
// CI fails when this file drifts from the backend. Session and manifest helpers live in runtime.ts.
import { callTool, getJson, postJson } from "./runtime";

/** Options every write tool takes beside its idempotency key: `reason` is the human reason sent as X-TA-Reason (an
 * audited override is refused without it; any other write may give one, and the ledger keeps it). */
export interface CallOpts {
  reason?: string;
}

export interface ManifestTool {
  name: string;
  title: string;
  description: string;
  inputSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown>;
  annotations: { readOnlyHint: boolean; destructiveHint: boolean; idempotentHint: boolean; openWorldHint: boolean };
  _meta: Record<string, unknown>;
}
export interface Manifest {
  tools: ManifestTool[];
  actor: { person_id: string; roles: string[]; view_as: string | null; on_behalf_of: string | null };
}
export const getManifest = () => getJson<Manifest>("/api/tools");

export interface Me {
  person_id: string;
  name: string;
  email: string;
  roles: string[];
  role_labels: string[];
  account: string | null;
  on_behalf_of: { person_id: string; name: string } | null;
  view_as: { person_id: string; name: string } | null;
  env: string;
  assistant_enabled: boolean;
  session_idle_minutes: number;
  timezone: string;
  role_words: string[];
  business_now: string | null;
}
export const getMe = () => getJson<Me>("/api/session/me");
export const login = (username: string, password: string) => postJson<{ ok: true }>("/api/session/login", { username, password });
export const logout = () => postJson<{ ok: true }>("/api/session/logout", {});

export type AccessApproveRequestInput = {
  requisition_id: string;
  access_request_id: string;
  scope?: "read" | "cover";
  days?: number;
  reason?: string | null;
};

export type AccessApproveRequestOutput = {
  requisition_id: string;
  id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Approve a pending access request, choosing read or cover and how long it lasts. */
export const accessApproveRequest = (input: AccessApproveRequestInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AccessApproveRequestOutput>("access.approve_request", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AccessDeclineInput = {
  requisition_id: string;
  access_request_id: string;
  reason?: string | null;
};

export type AccessDeclineOutput = {
  requisition_id: string;
  id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Decline a pending access request with a reason. */
export const accessDecline = (input: AccessDeclineInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AccessDeclineOutput>("access.decline", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AccessExpireInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type AccessExpireOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** H-56: the grant expired — archived, both sides told (validity was already evaluated at decision time). */
export const accessExpire = (input: AccessExpireInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AccessExpireOutput>("access.expire", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AccessExtendInput = {
  requisition_id: string;
  grant_id: string;
  scope?: "read" | "cover" | null;
  days?: number;
  reason: string;
};

export type AccessExtendOutput = {
  requisition_id: string;
  id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Extend or narrow somebody's access: a new grant that supersedes the old one, up to 30 days from today. Grants are never edited. */
export const accessExtend = (input: AccessExtendInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AccessExtendOutput>("access.extend", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AccessGrantInput = {
  requisition_id: string;
  grantee_id: string;
  scope?: "read" | "cover";
  days?: number;
  reason: string;
  request_id?: string | null;
};

export type AccessGrantOutput = {
  requisition_id: string;
  id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Give another Recruiter time-boxed access — read, or cover (the owner's writes too) — for up to 30 days. The owner's own act, or Admin's: access is never passed on by somebody covering. */
export const accessGrant = (input: AccessGrantInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AccessGrantOutput>("access.grant", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AccessListInput = {
  requisition_id: string;
};

export type GrantOut = {
  id: string;
  grantee_id: string;
  grantee_name: string;
  granted_by_id: string;
  granted_by_name: string;
  scope: string;
  scope_label: string;
  supersedes_id: string | null;
  superseded_by_id: string | null;
  screens_opened: number;
  reason: string;
  granted_at: string;
  expires_at: string;
  revoked_at: string | null;
  revoke_reason: string | null;
  live: boolean;
};

export type RequestOut = {
  id: string;
  requester_id: string;
  requester_name: string;
  reason: string;
  requested_at: string;
  status: string;
  scope: string;
  scope_label: string;
  granted_scope: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
};

export type AccessListOutput = {
  requisition_id: string;
  owner_id: string | null;
  grants: GrantOut[];
  requests: RequestOut[];
  mine: Record<string, unknown>;
};

/** Grants and requests on a requisition (the owner or Admin). */
export const accessList = (input: AccessListInput = {} as AccessListInput) => callTool<AccessListOutput>("access.list", input);

export type AccessListGlobalInput = {
  limit?: number;
  cursor?: string | null;
};

export type ListOutput = {
  requisition_id: string;
  owner_id: string | null;
  grants: GrantOut[];
  requests: RequestOut[];
  mine: Record<string, unknown>;
};

export type AccessListGlobalOutput = {
  items: ListOutput[];
  next_cursor?: string | null;
};

/** Admin's paged requisitions with retained access requests or grants. */
export const accessListGlobal = (input: AccessListGlobalInput = {} as AccessListGlobalInput) => callTool<AccessListGlobalOutput>("access.list_global", input);

export type AccessRequestInput = {
  requisition_id: string;
  reason: string;
  scope?: "read" | "cover";
};

export type AccessRequestOutput = {
  requisition_id: string;
  id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Ask the owning Recruiter for time-boxed access to their requisition, with a reason. */
export const accessRequest = (input: AccessRequestInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AccessRequestOutput>("access.request", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AccessRevokeInput = {
  requisition_id: string;
  grant_id: string;
  reason: string;
};

export type AccessRevokeOutput = {
  requisition_id: string;
  id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** End a grant with a reason — the owner, Admin, or the person handing their own access back; queued work re-checks the grant when it executes. */
export const accessRevoke = (input: AccessRevokeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AccessRevokeOutput>("access.revoke", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ActionCenterListInput = {
  tab?: "open" | "history";
  kinds?: ("reqs" | "candidates" | "scheduling" | "packets" | "scorecards" | "panels" | "approvals" | "offers" | "closeout")[] | null;
  categories?: ("reqs" | "candidates" | "scheduling" | "packets" | "scorecards" | "panels" | "approvals" | "offers" | "closeout")[] | null;
  tiers?: ("breach" | "near_breach" | "reminder" | "due" | "blocked")[] | null;
  due?: "overdue" | "today" | "week" | null;
  requisition_id?: string | null;
  hiring_manager_id?: string | null;
  sort?: "priority" | "due" | "requisition";
  limit?: number;
  cursor?: string | null;
};

export type ActionRow = {
  id: string;
  category: string;
  tier: string;
  tier_word: string | null;
  verb: string | null;
  title: string;
  route: string | null;
  requisition_id: string | null;
  requisition_title?: string | null;
  request_id?: string | null;
  request_title?: string | null;
  request_detail?: string | null;
  due_at: string | null;
  opened_at: string;
  closed_at?: string | null;
  closed_reason?: string | null;
  closed_by_name?: string | null;
  for_person_name?: string | null;
  pool_role?: string | null;
  object_word?: string | null;
  owner_word?: string | null;
  candidate_no?: string | null;
  due_phrase?: string | null;
  due_calendar?: string | null;
  respond?: RespondOut | null;
  responses?: ResponsesOut | null;
  urgent?: boolean;
};

export type RespondOut = {
  meeting_event_id: string;
  response: "none" | "accepted" | "tentative" | "declined";
};

export type ResponsesOut = {
  answered: number;
  of: number;
};

export type ActionCenterListOutput = {
  items: ActionRow[];
  next_cursor: string | null;
  counts: Record<string, number>;
};

/** Action Center: Prioritized actions (open, by tier, due and requisition) or History (closed, newest first), filtered by kind, tier, due, requisition and Hiring Manager; own items, the role pools held within scope, delegated cards. */
export const actionCenterList = (input: ActionCenterListInput = {} as ActionCenterListInput) => callTool<ActionCenterListOutput>("action_center.list", input);

export type AdminAdaptersInput = Record<string, unknown>;

export type AdapterRow = {
  port: string;
  adapter: string;
  mode: string;
  simulated: boolean;
  health: Record<string, unknown>;
  controls: string[];
};

export type AdminAdaptersOutput = {
  items: AdapterRow[];
};

/** Report the configured adapter and safe health metadata for each of the eight operational ports. */
export const adminAdapters = (input: AdminAdaptersInput = {} as AdminAdaptersInput) => callTool<AdminAdaptersOutput>("admin.adapters", input);

export type AdminArchiveRequisitionInput = {
  requisition_id: string;
  reason: string;
};

export type AdminArchiveRequisitionOutput = {
  requisition_id: string;
  snapshot_id: string;
  archived_at: string;
  content_hash: string;
  receipt_id?: number | null;
};

/** Archive a completed search as retained evidence; open worklists and legal holds refuse it. */
export const adminArchiveRequisition = (input: AdminArchiveRequisitionInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminArchiveRequisitionOutput>("admin.archive_requisition", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminArchiveSearchInput = {
  query?: string | null;
  level?: string | null;
  department?: string | null;
  owner_id?: string | null;
  archived_from?: string | null;
  archived_to?: string | null;
  closed_reason?: "filled" | "cancelled" | null;
  limit?: number;
  cursor?: string | null;
};

export type AdminArchiveSearchOutput = {
  items: Record<string, unknown>[];
  next_cursor: string | null;
  pending?: Record<string, unknown>[];
  legal_holds?: Record<string, unknown>[];
};

/** Search archived requisitions within the seats held when each snapshot was taken or a live archive scope; filter by closed reason and archive date. */
export const adminArchiveSearch = (input: AdminArchiveSearchInput = {} as AdminArchiveSearchInput) => callTool<AdminArchiveSearchOutput>("admin.archive_search", input);

export type AdminFilesListInput = {
  status?: "quarantined" | "scanned_clean" | "rejected" | "abandoned" | null;
  requisition_id?: string | null;
  limit?: number;
  cursor?: string | null;
};

export type AdminFilesListOutput = {
  items: Record<string, unknown>[];
  next_cursor: string | null;
  max_upload_bytes: number;
};

/** Inspect retained quarantined, rejected and abandoned uploads and their reasons. */
export const adminFilesList = (input: AdminFilesListInput = {} as AdminFilesListInput) => callTool<AdminFilesListOutput>("admin.files_list", input);

export type AdminIntegrationOpenInput = {
  integration_sync_id: string;
};

export type CallDetail = {
  id: string;
  port: string;
  op: string;
  status: string;
  attempts: number;
  error_kind: string | null;
  http_status: number | null;
  correlation_id: string;
  next_attempt_at: string | null;
  subject_type: string | null;
  subject_id: string | null;
  adapter: string;
  requisition_id: string | null;
  request_id: string | null;
  actor_id: string | null;
  provider_ref: string | null;
  outbox_message_id: string | null;
  last_error: string | null;
  requested_at: string | null;
  completed_at: string | null;
  resolved_by_id: string | null;
  route: string;
};

export type ReceiptRow = {
  id: number;
  at: string;
  tool: string;
  actor_id: string | null;
  summary: string | null;
  changes: Record<string, unknown> | null;
  has_reason: boolean;
};

export type AdminIntegrationOpenOutput = {
  row: CallDetail;
  receipts: ReceiptRow[];
  outbox_history: Record<string, unknown>[];
};

/** Open one integration call: its status, attempts, error details and correlation id, its receipts and, for a write, its delivery attempts. */
export const adminIntegrationOpen = (input: AdminIntegrationOpenInput = {} as AdminIntegrationOpenInput) => callTool<AdminIntegrationOpenOutput>("admin.integration_open", input);

export type AdminIntegrationResolveInput = {
  integration_sync_id: string;
  reason?: string | null;
};

export type AdminIntegrationResolveOutput = {
  integration_sync_id: string;
  port: string;
  op: string;
  status: string;
  attempts: number;
  correlation_id: string;
  error_kind: string | null;
  http_status: number | null;
  next_attempt_at: string | null;
  receipt_id?: number | null;
};

/** Mark a failed integration call recovered after it was fixed outside the application, with a reason; its recovery task closes. */
export const adminIntegrationResolve = (input: AdminIntegrationResolveInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminIntegrationResolveOutput>("admin.integration_resolve", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminIntegrationRetryInput = {
  integration_sync_id: string;
};

export type AdminIntegrationRetryOutput = {
  integration_sync_id: string;
  port: string;
  op: string;
  status: string;
  attempts: number;
  correlation_id: string;
  error_kind: string | null;
  http_status: number | null;
  next_attempt_at: string | null;
  receipt_id?: number | null;
};

/** Retry a failed integration call with a fresh correlation id; when it goes through, its recovery task closes. A call that already went through is not repeated. */
export const adminIntegrationRetry = (input: AdminIntegrationRetryInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminIntegrationRetryOutput>("admin.integration_retry", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminIntegrationsInput = {
  port?: string | null;
};

export type OpenCall = {
  id: string;
  port: string;
  op: string;
  status: string;
  attempts: number;
  error_kind: string | null;
  http_status: number | null;
  correlation_id: string;
  next_attempt_at: string | null;
  subject_type: string | null;
  subject_id: string | null;
};

export type PortHealth = {
  port: string;
  adapter: string;
  counts: Record<string, number>;
  last_success_at: string | null;
  oldest_open_failure_age_s: number | null;
  outbox?: Record<string, number> | null;
};

export type AdminIntegrationsOutput = {
  ports: PortHealth[];
  open: OpenCall[];
};

/** Integration health per port — mock or live, counts by status, last success, the oldest open failure — and the open failed or unknown calls with their correlation ids; never a name. */
export const adminIntegrations = (input: AdminIntegrationsInput = {} as AdminIntegrationsInput) => callTool<AdminIntegrationsOutput>("admin.integrations", input);

export type AdminOrgScopesListInput = {
  person_id?: string | null;
  kind?: "hrbp_supported_org" | "coordinator_pool" | "ta_manager_team" | "compensation_scope" | "hr_ops_scope" | null;
  include_revoked?: boolean;
};

export type OrgScopeRow = {
  id: string;
  person_id: string;
  kind: string;
  department: string | null;
  member_id: string | null;
  granted_by_id: string;
  granted_at: string;
  revoked_at: string | null;
  revoked_by_id: string | null;
  reason: string;
};

export type AdminOrgScopesListOutput = {
  items: OrgScopeRow[];
};

/** The department and pool scopes people hold, live by default. */
export const adminOrgScopesList = (input: AdminOrgScopesListInput = {} as AdminOrgScopesListInput) => callTool<AdminOrgScopesListOutput>("admin.org_scopes_list", input);

export type AdminOutboxFaultNextInput = {
  channel: "mail" | "calendar" | "ats" | "hris" | "calendar_api" | "meetings" | "rooms";
  outcome: "failed" | "unknown" | "retry_after";
  count?: number;
  op?: "create" | "update" | "cancel" | "read_responses" | "reserve" | "release" | null;
  subject_id?: string | null;
};

export type AdminOutboxFaultNextOutput = {
  channel: string;
  outcome: string;
  remaining: number;
  op?: string | null;
  subject_id?: string | null;
  simulated?: boolean;
  receipt_id?: number | null;
};

/** Development/test only: inject a labelled deterministic failure on the next simulated sends (optionally only those about one record), or on the next simulated calendar, Teams meeting or room calls. */
export const adminOutboxFaultNext = (input: AdminOutboxFaultNextInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminOutboxFaultNextOutput>("admin.outbox.fault_next", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminOutboxMarkHandledInput = {
  id: string;
  reason: string;
};

export type AdminOutboxMarkHandledOutput = {
  id: string;
  channel: string;
  kind: string;
  subject_type: string | null;
  subject_id: string | null;
  requisition_id: string | null;
  state: string;
  label: string;
  attempts: number;
  next_attempt_at: string | null;
  last_error: string | null;
  simulated: boolean;
  created_at: string;
  accepted_at: string | null;
  provider_ref: string | null;
  handled_at: string | null;
  handled_by_id: string | null;
  handled_reason: string | null;
  suppressed_reason: string | null;
  notification: Record<string, unknown>;
  receipt_id?: number | null;
};

/** Retain a failed/dead delivery but stop automatic retries and unresolved counts. */
export const adminOutboxMarkHandled = (input: AdminOutboxMarkHandledInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminOutboxMarkHandledOutput>("admin.outbox.mark_handled", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminOutboxRetryInput = {
  id: string;
};

export type AdminOutboxRetryOutput = {
  id: string;
  channel: string;
  kind: string;
  subject_type: string | null;
  subject_id: string | null;
  requisition_id: string | null;
  state: string;
  label: string;
  attempts: number;
  next_attempt_at: string | null;
  last_error: string | null;
  simulated: boolean;
  created_at: string;
  accepted_at: string | null;
  provider_ref: string | null;
  handled_at: string | null;
  handled_by_id: string | null;
  handled_reason: string | null;
  suppressed_reason: string | null;
  notification: Record<string, unknown>;
  receipt_id?: number | null;
};

/** Retry a failed/dead delivery with its original provider key, retaining every prior attempt. */
export const adminOutboxRetry = (input: AdminOutboxRetryInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminOutboxRetryOutput>("admin.outbox.retry", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminOutboxListInput = {
  attention_only?: boolean;
  channel?: string | null;
  state?: string | null;
  kind?: string | null;
  requisition_id?: string | null;
  from?: string | null;
  to?: string | null;
  limit?: number;
  cursor?: string | null;
};

export type OutboxRow = {
  id: string;
  channel: string;
  kind: string;
  subject_type: string | null;
  subject_id: string | null;
  requisition_id: string | null;
  state: string;
  label: string;
  attempts: number;
  next_attempt_at: string | null;
  last_error: string | null;
  simulated: boolean;
  created_at: string;
  accepted_at: string | null;
  provider_ref: string | null;
  handled_at: string | null;
  handled_by_id: string | null;
  handled_reason: string | null;
  suppressed_reason: string | null;
  notification: Record<string, unknown>;
};

export type AdminOutboxListOutput = {
  items: OutboxRow[];
  next_cursor: string | null;
};

/** Filter simulated deliveries and retained failures; candidate identity is replaced with tokens. */
export const adminOutboxList = (input: AdminOutboxListInput = {} as AdminOutboxListInput) => callTool<AdminOutboxListOutput>("admin.outbox_list", input);

export type AdminOutboxOpenInput = {
  id: string;
};

export type AdminOutboxOpenOutput = {
  row: OutboxRow;
  attempt_history: Record<string, unknown>[];
  simulated_notice: string;
};

/** Open a simulated delivery, its notification and retained attempt metadata. */
export const adminOutboxOpen = (input: AdminOutboxOpenInput = {} as AdminOutboxOpenInput) => callTool<AdminOutboxOpenOutput>("admin.outbox_open", input);

export type AdminPeopleListInput = {
  query?: string | null;
  capability?: string | null;
  status?: string | null;
  include_departed?: boolean;
  limit?: number;
  cursor?: string | null;
};

export type PersonRow = {
  person_id: string;
  name: string;
  account: string | null;
  capabilities: string[];
  employment_status: string;
  department: string | null;
  title: string | null;
  last_sign_in_at: string | null;
  seats_held: Record<string, number>;
  active_grants: number;
  active_delegations: number;
};

export type AdminPeopleListOutput = {
  items: PersonRow[];
  next_cursor?: string | null;
};

/** Read the people roster, standing capabilities and employment status; no candidate data. */
export const adminPeopleList = (input: AdminPeopleListInput = {} as AdminPeopleListInput) => callTool<AdminPeopleListOutput>("admin.people_list", input);

export type AdminPersonOpenInput = {
  person_id: string;
};

export type AdminPersonOpenOutput = {
  person_id: string;
  name: string;
  account: string | null;
  capabilities: string[];
  employment_status: string;
  department: string | null;
  title: string | null;
  last_sign_in_at: string | null;
  seats_held: Record<string, number>;
  active_grants: number;
  active_delegations: number;
  capability_history: Record<string, unknown>[];
  seats: Record<string, unknown>[];
  grants: Record<string, unknown>[];
  stand_ins: Record<string, unknown>[];
  receipts: Record<string, unknown>[];
};

/** Open a person's capability history, seats, access and receipt references without receipt content. */
export const adminPersonOpen = (input: AdminPersonOpenInput = {} as AdminPersonOpenInput) => callTool<AdminPersonOpenOutput>("admin.person_open", input);

export type AdminPlaceLegalHoldInput = {
  entity_type: "requisition" | "application" | "candidate" | "person";
  entity_id: string;
  order_reference: string;
  reason: string;
};

export type AdminPlaceLegalHoldOutput = {
  id: string;
  entity_type: string;
  entity_id: string;
  order_reference: string;
  reason: string;
  placed_at: string;
  placed_by_id: string;
  released_at: string | null;
  released_by_id: string | null;
  release_order_reference: string | null;
  release_reason: string | null;
  receipt_id?: number | null;
};

/** Place retention metadata against a written order; prevents archive detachment and reopening. */
export const adminPlaceLegalHold = (input: AdminPlaceLegalHoldInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminPlaceLegalHoldOutput>("admin.place_legal_hold", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminReleaseLegalHoldInput = {
  hold_id: string;
  release_order_reference: string;
  reason: string;
};

export type AdminReleaseLegalHoldOutput = {
  id: string;
  entity_type: string;
  entity_id: string;
  order_reference: string;
  reason: string;
  placed_at: string;
  placed_by_id: string;
  released_at: string | null;
  released_by_id: string | null;
  release_order_reference: string | null;
  release_reason: string | null;
  receipt_id?: number | null;
};

/** Release a legal hold by written order, retaining the original hold and its release stamps. */
export const adminReleaseLegalHold = (input: AdminReleaseLegalHoldInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminReleaseLegalHoldOutput>("admin.release_legal_hold", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminResetWorldInput = {
  confirmation: "RESET WORLD";
};

export type AdminResetWorldOutput = {
  reset?: boolean;
  sign_in_required?: boolean;
  receipt_id?: number | null;
};

/** Reset and reseed the synthetic development world after typing RESET WORLD; all sessions end. */
export const adminResetWorld = (input: AdminResetWorldInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminResetWorldOutput>("admin.reset_world", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminSchedulingSimulateResponseInput = {
  meeting_event_id: string;
  response: "accepted" | "tentative" | "declined";
  person_id: string;
};

export type AdminSchedulingSimulateResponseOutput = {
  scheduling_request_id: string;
  meeting_event_id: string;
  person_id: string;
  response: string;
  responded_at?: string | null;
  channel?: string | null;
  receipt_id?: number | null;
};

/** Development/test only: record an attendee's answer as if it came from their calendar, on the simulated calendar. */
export const adminSchedulingSimulateResponse = (input: AdminSchedulingSimulateResponseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminSchedulingSimulateResponseOutput>("admin.scheduling.simulate_response", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminSecretsInput = Record<string, unknown>;

export type SecretRow = {
  name: string;
  set: boolean;
  source: string;
};

export type AdminSecretsOutput = {
  items: SecretRow[];
};

/** Report only secret names, configured presence and source; never unwrap or inspect a value. */
export const adminSecrets = (input: AdminSecretsInput = {} as AdminSecretsInput) => callTool<AdminSecretsOutput>("admin.secrets", input);

export type AdminSetCapabilitiesInput = {
  person_id: string;
  capabilities: string[];
  reason: string;
};

export type AdminSetCapabilitiesOutput = {
  person_id: string;
  name: string;
  account: string | null;
  capabilities: string[];
  employment_status: string;
  department: string | null;
  title: string | null;
  last_sign_in_at: string | null;
  seats_held: Record<string, number>;
  active_grants: number;
  active_delegations: number;
  receipt_id?: number | null;
};

/** Revoke standing capabilities by stamps and re-grant by new rows; never remove the last active Admin. The Admin
    assigns any of the eight roles to someone else, never to themselves; a code the B12 fold retired is unknown. */
export const adminSetCapabilities = (input: AdminSetCapabilitiesInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminSetCapabilitiesOutput>("admin.set_capabilities", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminSetHrbpRoutingInput = {
  routing: Record<string, string>;
  effective_from: string;
  note: string;
  hotfix?: boolean;
  hotfix_reason?: string | null;
};

export type AdminSetHrbpRoutingOutput = {
  id: string;
  kind: string;
  key: string;
  version: number;
  status: "draft" | "approved" | "effective" | "superseded" | "rejected";
  label: "Current" | "Scheduled" | "Superseded" | "Draft" | "Rejected";
  effective_from: string;
  note: string | null;
  published_by_id: string | null;
  published_by_name?: string | null;
  published_at: string | null;
  approved_by_id: string | null;
  approved_by_name?: string | null;
  approved_at: string | null;
  superseded_at: string | null;
  supersedes_id: string | null;
  rejected_by_id: string | null;
  rejected_by_name?: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  hotfix: boolean;
  hotfix_reason: string | null;
  payload?: Record<string, unknown> | null;
  receipt_id?: number | null;
};

/** Propose department routing for a second Admin's approval; this never publishes a routing change. */
export const adminSetHrbpRouting = (input: AdminSetHrbpRoutingInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminSetHrbpRoutingOutput>("admin.set_hrbp_routing", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminSetOrgScopeInput = {
  person_id: string;
  kind: "hrbp_supported_org" | "coordinator_pool" | "ta_manager_team" | "compensation_scope" | "hr_ops_scope";
  department?: string | null;
  member_id?: string | null;
  revoke?: boolean;
  reason: string;
};

export type AdminSetOrgScopeOutput = {
  items: OrgScopeRow[];
  receipt_id?: number | null;
};

/** Grant or revoke a person's department or pool scope (append-and-revoke, with a reason). */
export const adminSetOrgScope = (input: AdminSetOrgScopeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminSetOrgScopeOutput>("admin.set_org_scope", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminSystemInput = Record<string, unknown>;

export type AdminSystemOutput = {
  env: string;
  adapters: Record<string, string>;
  tools: number;
  worker_lag_seconds: number | null;
  oldest_pending_seconds: number | null;
  outbox_dead: number;
  outbox_failed?: number;
  timers_dead?: number;
  app_version?: string;
  image_digest?: string | null;
  seed_version?: string;
  migration_head?: string | null;
  readiness?: string;
  business_now: string;
  last_tick_at?: string | null;
  tick_interval_s?: number;
  outbox_oldest_queued_age_s?: number | null;
  dead_timers?: number;
  lock_held?: boolean;
  stalled_on?: string | null;
};

/** Admin › System: adapters (mock/live), worker health, dead letters, the business clock. */
export const adminSystem = (input: AdminSystemInput = {} as AdminSystemInput) => callTool<AdminSystemOutput>("admin.system", input);

export type AdminTickInput = {
  max_occurrences?: number;
};

export type AdminTickOutput = {
  requested?: boolean;
  stale?: number;
  claimed?: number;
  done?: number;
  no_op?: number;
  failed?: number;
  dead?: number;
  outbox?: Record<string, number>;
  receipts?: number[];
  skipped_locked?: boolean;
  receipt_id?: number | null;
};

/** Request one worker pass now (development). The pass runs after this request's receipt has committed. */
export const adminTick = (input: AdminTickInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminTickOutput>("admin.tick", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminTimeTravelInput = {
  to?: string | null;
  advance_hours?: number | null;
  reset?: boolean;
  note?: string | null;
};

export type AdminTimeTravelOutput = {
  business_now: string;
  system_now: string;
  receipt_id?: number | null;
};

/** Move the business clock (development and test only). The offset is persisted in the same transaction as its receipt. */
export const adminTimeTravel = (input: AdminTimeTravelInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminTimeTravelOutput>("admin.time_travel", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminTimersMarkHandledInput = {
  occurrence_id: string;
  reason: string;
};

export type AdminTimersMarkHandledOutput = {
  id: string;
  state: string;
  handled_at?: string | null;
  requested?: boolean;
  receipt_id?: number | null;
};

/** Retain a dead/stale/failed occurrence and exclude it from automatic claims and unresolved counts. */
export const adminTimersMarkHandled = (input: AdminTimersMarkHandledInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminTimersMarkHandledOutput>("admin.timers.mark_handled", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminTimersRearmInput = {
  requisition_id: string;
  reason: string;
};

export type AdminTimersRearmOutput = {
  superseded: number;
  armed: number;
  receipt_id?: number | null;
};

/** Replace live timer generations using current configuration, preserving their original basis. */
export const adminTimersRearm = (input: AdminTimersRearmInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminTimersRearmOutput>("admin.timers.rearm", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminTimersRunInput = {
  occurrence_id: string;
};

export type AdminTimersRunOutput = {
  id: string;
  state: string;
  handled_at?: string | null;
  requested?: boolean;
  receipt_id?: number | null;
};

/** Retry exactly one stale, dead or failed occurrence through its original system-tool idempotency key. */
export const adminTimersRun = (input: AdminTimersRunInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminTimersRunOutput>("admin.timers.run", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminVerifyLedgerInput = {
  from_id?: number | null;
  to_id?: number | null;
};

export type AdminVerifyLedgerOutput = {
  rows_checked: number;
  first_bad_id: number | null;
  intact: boolean;
  from_id?: number | null;
  to_id?: number | null;
};

/** Recompute the audit hash chain. */
export const adminVerifyLedger = (input: AdminVerifyLedgerInput = {} as AdminVerifyLedgerInput) => callTool<AdminVerifyLedgerOutput>("admin.verify_ledger", input);

export type AdminViewAsStartInput = {
  person_id: string;
  reason: string;
};

export type AdminViewAsStartOutput = {
  view_as_id: string | null;
  name: string | null;
  roles: string[];
  started_at: string | null;
  expires_at: string | null;
  session_rotated: boolean;
  receipt_id?: number | null;
};

/** Start a 30-minute read-only lens; the door rotates the session and CSRF cookies. */
export const adminViewAsStart = (input: AdminViewAsStartInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminViewAsStartOutput>("admin.view_as.start", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminViewAsStopInput = Record<string, unknown>;

export type AdminViewAsStopOutput = {
  view_as_id: string | null;
  name: string | null;
  roles: string[];
  started_at: string | null;
  expires_at: string | null;
  session_rotated: boolean;
  receipt_id?: number | null;
};

/** Stop the lens, including an expired lens, and rotate the session back to the operator. */
export const adminViewAsStop = (input: AdminViewAsStopInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<AdminViewAsStopOutput>("admin.view_as.stop", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type AdminWorkerStatusInput = {
  limit_ticks?: number;
};

export type OccurrenceRow = {
  occurrence_id: string;
  definition: string;
  subject_type: string;
  subject_id: string;
  scheduled_at: string;
  attempts: number;
  detail: string | null;
  late_by_seconds: number;
  state: string;
};

export type AdminWorkerStatusOutput = {
  last_tick_at: string | null;
  tick_interval_s: number;
  oldest_due_age_s: number | null;
  outbox_oldest_queued_age_s: number | null;
  dead_timers: number;
  dead_outbox: number;
  lock_held: boolean;
  counts_by_state: Record<string, number>;
  recent_ticks: Record<string, unknown>[];
  stale: OccurrenceRow[];
  dead: OccurrenceRow[];
};

/** Worker health, the last ten passes, and unhandled stale/dead occurrences without candidate content. */
export const adminWorkerStatus = (input: AdminWorkerStatusInput = {} as AdminWorkerStatusInput) => callTool<AdminWorkerStatusOutput>("admin.worker_status", input);

export type ArchiveOpenInput = {
  entity_type: string;
  entity_id: string;
};

export type ArchiveOpenOutput = {
  snapshot_id: string;
  entity_type: string;
  entity_id: string;
  taken_at: string;
  taken_by_id: string | null;
  taken_by_name: string | null;
  reason: string;
  content_hash: string;
  projection: string;
  payload: Record<string, unknown>;
};

/** Open immutable archive evidence narrowed to the seat held at archive time or a live archive scope (allow-list projections). */
export const archiveOpen = (input: ArchiveOpenInput = {} as ArchiveOpenInput) => callTool<ArchiveOpenOutput>("archive.open", input);

export type AuditQueryInput = {
  tool?: string | null;
  actor_id?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  outcome?: string | null;
  on_behalf_of_id?: string | null;
  refusal_reason?: string | null;
  view_as_id?: string | null;
  related_requisition_id?: string | null;
  related_candidate_id?: string | null;
  receipt_kind?: "admin_override" | null;
  source?: string | null;
  since?: string | null;
  until?: string | null;
  cursor?: number | null;
  limit?: number;
  format?: "json" | "csv";
};

export type AuditRow = {
  id: number;
  at: string;
  actor_id: string | null;
  actor_name?: string | null;
  on_behalf_of_id: string | null;
  on_behalf_of_name?: string | null;
  view_as_id?: string | null;
  view_as_name?: string | null;
  acting_for_name?: string | null;
  acting_for_id?: string | null;
  via_delegation_id?: string | null;
  via_grant_id?: string | null;
  tool: string;
  tool_kind: string;
  entity_type: string | null;
  entity_id: string | null;
  outcome: string;
  refusal_reason: string | null;
  effective_at: string | null;
  changes: Record<string, unknown> | null;
  summary: string | null;
  trace_id: string | null;
  invocation_id: string | null;
  reason?: string | null;
  evidence_ref?: string | null;
  source?: string | null;
  related_requisition_id?: string | null;
  related_candidate_id?: string | null;
  receipt_kind?: string | null;
  override_role?: string | null;
  override_role_label?: string | null;
  admitted_by?: string | null;
};

export type AuditQueryOutput = {
  version_id?: string | null;
  receipt_id?: string | null;
  classification?: string | null;
  items: AuditRow[];
  next_cursor?: number | null;
  csv?: string | null;
};

/** Query the ledger by actor, attribution, entity, outcome and time; export the same page safely. */
export const auditQuery = (input: AuditQueryInput = {} as AuditQueryInput) => callTool<AuditQueryOutput>("audit.query", input);

export type CandidateAbandonParseInput = {
  parse_id: string;
  expected_version: number;
};

export type CandidateAbandonParseOutput = {
  parse_id: string;
  status: "pending" | "parsed" | "failed" | "confirmed" | "abandoned";
  failure_code?: string | null;
  version: number;
  file_id: string;
  owner_id?: string | null;
  requisition_id?: string | null;
  candidate_id?: string | null;
  receipt_id?: number | null;
};

/** Close a parsed or failed resume that will not become a profile; the file and the proposed values are kept. */
export const candidateAbandonParse = (input: CandidateAbandonParseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateAbandonParseOutput>("candidate.abandon_parse", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CreateDecision = {
  kind: "use_existing" | "create_distinct" | "refer";
  match_ref?: string | null;
  reason?: string | null;
};

export type CandidateAddInput = {
  requisition_id: string;
  display_name: string;
  source_channel: "applied" | "sourced" | "referral" | "internal" | "agency";
  resume_file_id?: string | null;
  email?: string | null;
  phone?: string | null;
  ats_id?: string | null;
  referrer_id?: string | null;
  internal_employee_ref?: string | null;
  decision?: CreateDecision | null;
  seen_match_refs?: string[];
};

export type ApplicationCard = {
  id: string;
  token: string;
  display_name: string | null;
  stage: string;
  stage_label: string;
  furthest_stage: string;
  furthest_stage_label: string;
  source_channel: string;
  ats_id: string | null;
  ats_verified: boolean;
  internal: boolean;
  batch_no: number | null;
  recruiter_note: string | null;
  requirements_met: Record<string, unknown>[] | null;
  entered_pipeline_at: string;
  stage_entered_at: string | null;
  waiting_business_days: number;
  holder: string | null;
  held_from_stage: string | null;
  hold_reason: string | null;
  disposition_status: string;
  keep_warm: boolean;
  last_decision: Record<string, unknown> | null;
  can: Record<string, boolean>;
  slate_state?: string | null;
  held_days?: number | null;
  hold_reason_label?: string | null;
};

export type ApplicationRound = {
  id: string;
  round_def_id: string;
  round_kind: string;
  label: string;
  status: string;
  start_at: string | null;
  held_at: string | null;
  interviewer: string | null;
  packet_status: string | null;
  scorecard_status: string;
};

export type CandidateAddOutput = {
  projection: string;
  candidate_no?: string | null;
  requisition_status: string;
  state_archive?: string;
  archived_at?: string | null;
  card: ApplicationCard;
  email?: string | null;
  phone?: string | null;
  resume_file_id: string | null;
  resume_version?: number | null;
  resume_pinned_at?: string | null;
  resume_versions?: Record<string, unknown>[];
  stage_history: Record<string, unknown>[];
  decisions: Record<string, unknown>[];
  holds: Record<string, unknown>[];
  withdrawals: Record<string, unknown>[];
  messages: Record<string, unknown>[];
  rounds: ApplicationRound[];
  disposition: Record<string, unknown> | null;
  receipt_id?: number | null;
};

/** Add one candidate by hand: display name, source channel, optional ATS id (unverified chip), contact details; the application enters Sourced. */
export const candidateAdd = (input: CandidateAddInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateAddOutput>("candidate.add", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateAskRecruiterInput = {
  requisition_id: string;
  application_id: string;
  question: string;
};

export type CandidateAskRecruiterOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Ask the Recruiter a question about a candidate; the card is held until answered. */
export const candidateAskRecruiter = (input: CandidateAskRecruiterInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateAskRecruiterOutput>("candidate.ask_recruiter", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateAttachResumeInput = {
  requisition_id: string;
  application_id: string;
  resume_file_id: string;
};

export type CandidateAttachResumeOutput = {
  projection: string;
  candidate_no?: string | null;
  requisition_status: string;
  state_archive?: string;
  archived_at?: string | null;
  card: ApplicationCard;
  email?: string | null;
  phone?: string | null;
  resume_file_id: string | null;
  resume_version?: number | null;
  resume_pinned_at?: string | null;
  resume_versions?: Record<string, unknown>[];
  stage_history: Record<string, unknown>[];
  decisions: Record<string, unknown>[];
  holds: Record<string, unknown>[];
  withdrawals: Record<string, unknown>[];
  messages: Record<string, unknown>[];
  rounds: ApplicationRound[];
  disposition: Record<string, unknown> | null;
  receipt_id?: number | null;
};

/** Put a résumé on a candidate: the uploaded file becomes their current version (a later one is a new version, and nothing is overwritten). Applications already sent for review keep the version their batch carried. */
export const candidateAttachResume = (input: CandidateAttachResumeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateAttachResumeOutput>("candidate.attach_resume", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateBatchAssembleInput = {
  requisition_id: string;
  reads: Record<string, unknown>[];
};

export type CandidateBatchAssembleOutput = {
  requisition_id: string;
  batch_no: number;
  application_ids: string[];
  effective_at: string;
  receipt_id?: number | null;
};

/** Assemble a batch: a one-line read per qualified candidate against the must-haves (below-the-bar needs a reason). */
export const candidateBatchAssemble = (input: CandidateBatchAssembleInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateBatchAssembleOutput>("candidate.batch_assemble", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateBatchDecideInput = {
  requisition_id: string;
  batch_no: number;
  decisions: Record<string, unknown>[];
  feedback?: string | null;
};

export type CandidateBatchDecideOutput = {
  requisition_id: string;
  batch_no: number;
  application_ids: string[];
  effective_at: string;
  receipt_id?: number | null;
};

/** Send the batch's decisions: a decision and reason on every candidate still in review; the review timer stops; the Recruiter is told. */
export const candidateBatchDecide = (input: CandidateBatchDecideInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateBatchDecideOutput>("candidate.batch_decide", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateBatchOpenInput = {
  requisition_id: string;
  batch_no: number;
};

export type CandidateBatchOpenOutput = {
  requisition_id: string;
  batch_no: number;
  application_ids: string[];
  effective_at: string;
  receipt_id?: number | null;
};

/** The Hiring Manager opens the batch: an explicit, receipted acknowledgment (viewed by/at) — the phase report attributes waiting time from here. */
export const candidateBatchOpen = (input: CandidateBatchOpenInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateBatchOpenOutput>("candidate.batch_open", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateBatchSendInput = {
  requisition_id: string;
  application_ids: string[];
};

export type CandidateBatchSendOutput = {
  requisition_id: string;
  batch_no: number;
  application_ids: string[];
  effective_at: string;
  receipt_id?: number | null;
};

/** Send the batch for review: stage → in review; the first batch stamps the Sourcing checkpoint; the Hiring Manager is notified; 3-business-day review timer (D-24). */
export const candidateBatchSend = (input: CandidateBatchSendInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateBatchSendOutput>("candidate.batch_send", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateCloseAtSourceInput = {
  requisition_id: string;
  application_id: string;
  reason_code: "scope_mismatch" | "experience_gap" | "location" | "comp_out_of_range" | "not_a_fit_for_level" | "internal_policy" | "duplicate" | "resume_review_pass" | "screen_pass" | "passed_from_hold" | "other";
  note?: string | null;
};

export type CandidateCloseAtSourceOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Not qualified — closed at source with a reason code; the disposition obligation opens at once. */
export const candidateCloseAtSource = (input: CandidateCloseAtSourceInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateCloseAtSourceOutput>("candidate.close_at_source", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateCommunityAddInput = {
  candidate_id: string;
  reason_added: string;
  segment?: string | null;
  tags?: string[];
  prior_application_id?: string | null;
  last_contact_at?: string | null;
  notes?: string | null;
};

export type CommunityOut = {
  membership_id: string;
  reason_added: string;
  added_at: string;
  segment?: string | null;
  tags?: string[];
  prior_requisition?: LoopFact | null;
  prior_application_id?: string | null;
  last_contact_at?: string | null;
  notes?: string | null;
};

export type LoopFact = {
  requisition_id: string;
  title: string;
  owner_name: string | null;
  state: string;
  application_id?: string | null;
};

export type CandidateCommunityAddOutput = {
  candidate_no: string;
  membership_id: string;
  action: "added" | "touched" | "removed";
  community?: CommunityOut | null;
  receipt_id?: number | null;
};

/** Add a candidate to the talent community on purpose: one of the five reasons (finalist, alternate, silver medalist, timing-related decline or withdrawal), with segment, tags, prior requisition, last contact and notes. Never automatic. */
export const candidateCommunityAdd = (input: CandidateCommunityAddInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateCommunityAddOutput>("candidate.community_add", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateCommunityRemoveInput = {
  candidate_id: string;
  reason: string;
};

export type CandidateCommunityRemoveOutput = {
  candidate_no: string;
  membership_id: string;
  action: "added" | "touched" | "removed";
  community?: CommunityOut | null;
  receipt_id?: number | null;
};

/** Take a candidate out of the talent community, with a reason; the membership history stays. */
export const candidateCommunityRemove = (input: CandidateCommunityRemoveInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateCommunityRemoveOutput>("candidate.community_remove", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateCommunityTouchInput = {
  candidate_id?: string | null;
  ref?: string | null;
  last_contact_at?: string | null;
  notes?: string | null;
};

export type CandidateCommunityTouchOutput = {
  candidate_no: string;
  membership_id: string;
  action: "added" | "touched" | "removed";
  community?: CommunityOut | null;
  receipt_id?: number | null;
};

/** Record the last contact with a talent-community member (and, with the profile in full, new notes); a rediscovered member is named by your search ref. */
export const candidateCommunityTouch = (input: CandidateCommunityTouchInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateCommunityTouchOutput>("candidate.community_touch", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type EmploymentRow = {
  employer?: string | null;
  title?: string | null;
  start?: string | null;
  end?: string | null;
};

export type ParsedEdits = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  location?: string | null;
  current_employer?: string | null;
  employment_history?: EmploymentRow[] | null;
  linkedin_url?: string | null;
};

export type CandidateCreateInput = {
  display_name?: string | null;
  source_channel: string;
  owner_id?: string | null;
  email?: string | null;
  secondary_email?: string | null;
  phone?: string | null;
  linkedin_url?: string | null;
  location?: string | null;
  current_employer?: string | null;
  no_email_reason?: string | null;
  requisition_id?: string | null;
  decision?: CreateDecision | null;
  seen_match_refs?: string[];
  parse_id?: string | null;
  confirm?: ("name" | "email" | "phone" | "location" | "current_employer" | "employment_history" | "linkedin_url")[];
  edits?: ParsedEdits | null;
};

export type CandidateCreateOutput = {
  candidate_id: string;
  candidate_no: string;
  created: boolean;
  application_id: string | null;
  match_ids: string[];
  receipt_id?: number | null;
};

/** Save a candidate profile after the potential matches were shown: a decision (use existing, create distinct with a reason, or a Coordinator's referral) is required when a strong match exists; no requisition needed. From a parsed resume every value is confirmed or edited first, and the original file becomes the candidate's resume. */
export const candidateCreate = (input: CandidateCreateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateCreateOutput>("candidate.create", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateDecideAdvanceInput = {
  requisition_id: string;
  application_id: string;
  value: "advance" | "hold" | "pass";
  reason_text: string;
  reason_code?: "scope_mismatch" | "experience_gap" | "location" | "comp_out_of_range" | "not_a_fit_for_level" | "internal_policy" | "duplicate" | "resume_review_pass" | "screen_pass" | "passed_from_hold" | "other" | null;
};

export type CandidateDecideAdvanceOutput = {
  application_id: string;
  stage: string;
  interviews_created: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** After the Hiring Manager's own scorecard: advance to the panel (one planned interview per onsite round; the exercise step opens), hold, or pass — the Recruiter is told of each; refused when any submitted scorecard binds No Hire under the requisition's rule (VP and above, or two 2s before it) or the planned loop exceeds the touchpoint cap. */
export const candidateDecideAdvance = (input: CandidateDecideAdvanceInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateDecideAdvanceOutput>("candidate.decide_advance", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateDecideResumeInput = {
  requisition_id: string;
  application_id: string;
  value: "advance" | "hold" | "pass";
  reason_code?: "scope_mismatch" | "experience_gap" | "location" | "comp_out_of_range" | "not_a_fit_for_level" | "internal_policy" | "duplicate" | "resume_review_pass" | "screen_pass" | "passed_from_hold" | "other" | null;
  reason_text?: string | null;
  hold_reason?: string | null;
};

export type CandidateDecideResumeOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** One resume decision: advance to screen · hold · pass, with a reason. */
export const candidateDecideResume = (input: CandidateDecideResumeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateDecideResumeOutput>("candidate.decide_resume", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateDispositionEarlyInput = {
  requisition_id: string;
  application_id: string;
  reason_code: "scope_mismatch" | "experience_gap" | "location" | "comp_out_of_range" | "not_a_fit_for_level" | "internal_policy" | "duplicate" | "resume_review_pass" | "screen_pass" | "passed_from_hold" | "other";
  note?: string | null;
};

export type CandidateDispositionEarlyOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Not selected before the fill (M10): stage not_selected with a reason code; the disposition obligation opens now. */
export const candidateDispositionEarly = (input: CandidateDispositionEarlyInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateDispositionEarlyOutput>("candidate.disposition_early", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateDuplicateOpenInput = {
  candidate_match_id: string;
};

export type DuplicateSide = {
  side: "probe" | "matched";
  candidate_no: string;
  status: "Active" | "Archived" | "Merged";
  projection: "full" | "masked";
  candidate_id?: string | null;
  display_name?: string | null;
  owner_name?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedin_url?: string | null;
  applications: LoopFact[];
};

export type CandidateDuplicateOpenOutput = {
  candidate_match_id: string;
  status: "open" | "kept_distinct" | "dismissed" | "merged";
  strength: "strong" | "weak";
  reasons: string[];
  origin: string;
  decided_at: string | null;
  decision_reason: string | null;
  reviewed_at: string | null;
  sides: DuplicateSide[];
  may_resolve: boolean;
};

/** Open a duplicate review: which attributes matched and both profiles side by side, the other profile masked where the reader may not open it. */
export const candidateDuplicateOpen = (input: CandidateDuplicateOpenInput = {} as CandidateDuplicateOpenInput) => callTool<CandidateDuplicateOpenOutput>("candidate.duplicate_open", input);

export type CandidateHoldInput = {
  requisition_id: string;
  application_id: string;
  reason: string;
  reason_text?: string | null;
};

export type CandidateHoldOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Hold a candidate with a reason; a five-business-day review timer keeps nobody parked silently (D-25). */
export const candidateHold = (input: CandidateHoldInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateHoldOutput>("candidate.hold", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ImportDecision = {
  kind: "use_existing" | "create_distinct" | "refer";
  match_ref?: string | null;
  reason?: string | null;
  seen_match_refs?: string[];
};

export type ImportRow = {
  ats_id: string;
  display_name: string;
  email?: string | null;
  source_channel?: "applied" | "sourced" | "referral" | "internal" | "agency";
  resume_ref?: string | null;
};

export type CandidateImportInput = {
  requisition_id: string;
  source?: "ats_applicants" | "ats_sourced" | "csv";
  rows?: ImportRow[];
  preview?: boolean;
  decisions?: Record<string, ImportDecision>;
};

export type CandidateImportOutput = {
  requisition_id: string;
  imported: Record<string, unknown>[];
  skipped: Record<string, unknown>[];
  preview: boolean;
  receipt_id?: number | null;
};

/** Import candidates from the ATS mock (applicants against the REQ, sourced profiles) or a CSV; preview marks duplicates; each import becomes an application in Sourced. */
export const candidateImport = (input: CandidateImportInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateImportOutput>("candidate.import", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateLinkToRequisitionInput = {
  requisition_id: string;
  candidate_id?: string | null;
  match_ref?: string | null;
  source_channel?: string | null;
};

export type CandidateLinkToRequisitionOutput = {
  candidate_no: string;
  candidate_id: string | null;
  application_id: string;
  requisition_id: string;
  prior_application_id: string | null;
  receipt_id?: number | null;
};

/** Put an existing candidate profile on another requisition under the same Candidate ID: the application enters Sourced; a returning person's new application points at the last one; never a second profile. */
export const candidateLinkToRequisition = (input: CandidateLinkToRequisitionInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateLinkToRequisitionOutput>("candidate.link_to_requisition", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateListInput = {
  requisition_id: string;
  stage?: string | null;
  batch_no?: number | null;
};

export type HrbpApplicationCard = {
  id: string;
  token: string;
  stage: string;
  stage_label: string;
  furthest_stage: string;
  furthest_stage_label: string;
  entered_pipeline_at: string;
  stage_entered_at: string | null;
  waiting_business_days: number;
  can: Record<string, boolean>;
  held_days?: number | null;
};

export type HrbpCandidateList = {
  projection?: "hrbp";
  state_archive: string;
  archived_at: string | null;
  requisition_title: string;
  requisition_id: string;
  items: HrbpApplicationCard[];
  counts: Record<string, number>;
};

export type CandidateListListOutput = {
  state_archive?: string;
  archived_at?: string | null;
  requisition_title: string;
  requisition_id: string;
  projection: string;
  items: ApplicationCard[];
  counts: Record<string, number>;
  batches: Record<string, unknown>[];
};

export type CandidateListOutput = CandidateListListOutput | HrbpCandidateList;

/** The pipeline for one requisition by stage; names for the Recruiter, the Hiring Manager (from the first batch) and Admin; tokens and counts for the HR Business Partner. */
export const candidateList = (input: CandidateListInput = {} as CandidateListInput) => callTool<CandidateListOutput>("candidate.list", input);

export type CandidateLogMessageInput = {
  requisition_id: string;
  application_id: string;
  kind: "invitation" | "itinerary" | "exercise_brief" | "closing_note" | "other";
  sent_at: string;
  reference?: string | null;
  note?: string | null;
  interview_id?: string | null;
  reply_logged?: boolean;
  reply_to_message_id?: string | null;
};

export type CandidateLogMessageOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Record that a message was sent to the candidate from the ATS (invitation · itinerary · exercise brief · closing note · other); an invitation starts the candidate-reply timer (D-27). */
export const candidateLogMessage = (input: CandidateLogMessageInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateLogMessageOutput>("candidate.log_message", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateMarkInternalInput = {
  requisition_id: string;
  application_id: string;
  internal?: boolean;
  referrer_id?: string | null;
  internal_employee_ref?: string | null;
};

export type CandidateMarkInternalOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Flag an internal candidate or referral (the intake policy applies); the Hiring Manager and HR Business Partner are told. */
export const candidateMarkInternal = (input: CandidateMarkInternalInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateMarkInternalOutput>("candidate.mark_internal", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateMatchInput = {
  display_name?: string | null;
  email?: string | null;
  secondary_email?: string | null;
  phone?: string | null;
  linkedin_url?: string | null;
  ats_id?: string | null;
  requisition_id?: string | null;
};

export type HitOut = {
  match_ref: string;
  strength: "strong" | "weak";
  reasons: string[];
  candidate_no: string;
  projection: "full" | "masked";
  candidate_id?: string | null;
  display_name?: string | null;
  owner_name?: string | null;
  applications: LoopFact[];
};

export type CandidateMatchOutput = {
  hits: HitOut[];
  strong: number;
  weak: number;
};

/** Potential matches before save: compares email, phone, LinkedIn, ATS id, name and history; hits are trimmed to the caller. */
export const candidateMatch = (input: CandidateMatchInput = {} as CandidateMatchInput) => callTool<CandidateMatchOutput>("candidate.match", input);

export type MergeKeep = {
  display_name?: "survivor" | "merged";
  primary_email?: "survivor" | "merged";
  phone?: "survivor" | "merged";
  linkedin_url?: "survivor" | "merged";
};

export type CandidateMergeInput = {
  candidate_id: string;
  merged_id: string;
  expected_version: number;
  merged_expected_version: number;
  reason?: string;
  keep?: MergeKeep;
};

export type EligibilityOut = {
  application_id: string;
  basis: string;
};

export type InternalOut = {
  application_id: string;
  version?: number | null;
  department?: string | null;
  title?: string | null;
  location?: string | null;
  mobility_type?: string | null;
  leader_name?: string | null;
  grade?: string | null;
  attested?: boolean;
  statement_version?: number | null;
  statement?: string | null;
  attested_at?: string | null;
};

export type NoEmailOut = {
  reason: string;
  at: string | null;
};

export type ProfileApplication = {
  requisition_id: string;
  title: string;
  owner_name: string | null;
  state: string;
  application_id?: string | null;
  stage?: string | null;
  screen_status?: string | null;
  screen_outcome?: string | null;
};

export type ProfileOut = {
  candidate_id: string | null;
  candidate_no: string;
  projection: "full" | "token" | "masked";
  status: "Active" | "Archived" | "Merged";
  version: number;
  merged_into_no?: string | null;
  display_name?: string | null;
  owner_id?: string | null;
  owner_name?: string | null;
  source_channel?: string | null;
  email?: string | null;
  secondary_emails?: string[];
  phone?: string | null;
  linkedin_url?: string | null;
  location?: string | null;
  current_employer?: string | null;
  employment_history?: EmploymentRow[];
  no_email_exception?: NoEmailOut | null;
  applications?: ProfileApplication[];
  community?: CommunityOut | null;
  eligibility?: EligibilityOut[];
  internal?: InternalOut[];
  resume_versions?: ResumeVersionOut[];
  recovery?: RecoveryOut | null;
};

export type RecoveryOut = {
  merge_event_id: string;
  survivor_id: string;
  survivor_no: string;
  survivor_version: number;
  merged_version: number;
};

export type ResumeVersionOut = {
  file_id: string;
  version: number;
  candidate_no: string;
  uploaded_at: string | null;
  uploaded_by_name: string | null;
  original_name: string | null;
  parsed?: boolean;
  confirmed?: boolean;
};

export type CandidateMergeOutput = {
  merge_event_id: string;
  kind: "merge" | "unmerge";
  survivor: ProfileOut;
  merged: ProfileOut;
  receipt_id?: number | null;
};

/** Merge one candidate profile into another by pointer: nothing moves (applications, résumés, messages and history keep their records), the survivor adopts the contacts chosen, and an append-only merge event records it. Admin only. */
export const candidateMerge = (input: CandidateMergeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateMergeOutput>("candidate.merge", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateNotifyParseResultInput = {
  parse_id: string;
};

export type CandidateNotifyParseResultOutput = {
  no_op: boolean;
  status?: string | null;
  recipients?: number;
  receipt_id?: number | null;
};

/** After the worker parses a resume: tell the uploader, and the profile's owner, that parsing is complete (review the values) or failed (and why). A no-op unless the parse is parsed or failed. */
export const candidateNotifyParseResult = (input: CandidateNotifyParseResultInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateNotifyParseResultOutput>("candidate.notify_parse_result", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateNotifyResultsLateInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type CandidateNotifyResultsLateOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** D-28: the screen scorecard is in but the results have not gone to the Hiring Manager after a business day. */
export const candidateNotifyResultsLate = (input: CandidateNotifyResultsLateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateNotifyResultsLateOutput>("candidate.notify_results_late", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateOpenInput = {
  requisition_id: string;
  application_id: string;
};

export type ApplicationFull = {
  projection: string;
  candidate_no?: string | null;
  requisition_status: string;
  state_archive?: string;
  archived_at?: string | null;
  card: ApplicationCard;
  email?: string | null;
  phone?: string | null;
  resume_file_id: string | null;
  resume_version?: number | null;
  resume_pinned_at?: string | null;
  resume_versions?: Record<string, unknown>[];
  stage_history: Record<string, unknown>[];
  decisions: Record<string, unknown>[];
  holds: Record<string, unknown>[];
  withdrawals: Record<string, unknown>[];
  messages: Record<string, unknown>[];
  rounds: ApplicationRound[];
  disposition: Record<string, unknown> | null;
};

export type HrbpApplication = {
  projection?: "hrbp";
  requisition_status: string;
  state_archive: string;
  archived_at: string | null;
  card: HrbpApplicationCard;
  stage_history: Record<string, unknown>[];
};

export type CandidateOpenOutput = ApplicationFull | HrbpApplication;

/** One application record: identity where authorised, stage history, decisions, holds, messages, rounds, disposition. */
export const candidateOpen = (input: CandidateOpenInput = {} as CandidateOpenInput) => callTool<CandidateOpenOutput>("candidate.open", input);

export type CandidateParseOpenInput = {
  parse_id: string;
};

export type ProposedValue = {
  value?: unknown;
  found: boolean;
};

export type CandidateParseOpenOutput = {
  parse_id: string;
  status: "pending" | "parsed" | "failed" | "confirmed" | "abandoned";
  failure_code?: string | null;
  version: number;
  file_id: string;
  owner_id?: string | null;
  requisition_id?: string | null;
  candidate_id?: string | null;
  owner_name?: string | null;
  uploaded_by_id: string;
  parser_version: string;
  proposed?: Record<string, ProposedValue> | null;
  confirmed?: Record<string, unknown> | null;
  text?: string | null;
  candidate_no?: string | null;
};

/** Review a parsed resume: the seven proposed values, which were found, the values confirmed at save, and the resume's text beside them. */
export const candidateParseOpen = (input: CandidateParseOpenInput = {} as CandidateParseOpenInput) => callTool<CandidateParseOpenOutput>("candidate.parse_open", input);

export type CandidateParseResumeInput = {
  file_id: string;
  owner_id?: string | null;
  requisition_id?: string | null;
};

export type CandidateParseResumeOutput = {
  parse_id: string;
  status: "pending" | "parsed" | "failed" | "confirmed" | "abandoned";
  failure_code?: string | null;
  version: number;
  file_id: string;
  owner_id?: string | null;
  requisition_id?: string | null;
  candidate_id?: string | null;
  receipt_id?: number | null;
};

/** Parse a resume you staged (PDF or DOCX) into proposed values for review: the worker reads it off the transaction and tells you, and the profile's owner, when parsing is complete or fails. */
export const candidateParseResume = (input: CandidateParseResumeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateParseResumeOutput>("candidate.parse_resume", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateProfileInput = {
  candidate_id: string | null;
  ref?: string | null;
};

export type CandidateProfileOutput = {
  candidate_id: string | null;
  candidate_no: string;
  projection: "full" | "token" | "masked";
  status: "Active" | "Archived" | "Merged";
  version: number;
  merged_into_no?: string | null;
  display_name?: string | null;
  owner_id?: string | null;
  owner_name?: string | null;
  source_channel?: string | null;
  email?: string | null;
  secondary_emails?: string[];
  phone?: string | null;
  linkedin_url?: string | null;
  location?: string | null;
  current_employer?: string | null;
  employment_history?: EmploymentRow[];
  no_email_exception?: NoEmailOut | null;
  applications?: ProfileApplication[];
  community?: CommunityOut | null;
  eligibility?: EligibilityOut[];
  internal?: InternalOut[];
  resume_versions?: ResumeVersionOut[];
  recovery?: RecoveryOut | null;
};

/** Open a candidate profile by token or by your search ref: Candidate ID, status, contacts and profile values as the reader's field classes allow, talent-community membership and eligibility, and the person's applications (loop facts only where the reader is not seated); a rediscovered talent-community member reads masked. */
export const candidateProfile = (input: CandidateProfileInput = {} as CandidateProfileInput) => callTool<CandidateProfileOutput>("candidate.profile", input);

export type CandidatePromptHeldReviewInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type CandidatePromptHeldReviewOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** D-25: a held candidate's five-business-day review — advance, pass or keep held with a note. */
export const candidatePromptHeldReview = (input: CandidatePromptHeldReviewInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidatePromptHeldReviewOutput>("candidate.prompt_held_review", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateQualifyInput = {
  requisition_id: string;
  application_id: string;
  note: string;
  requirements_met?: Record<string, unknown>[];
};

export type CandidateQualifyOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Qualified: the Recruiter's read against the must-haves; the application joins the next batch (D-23 batch-ready timer). */
export const candidateQualify = (input: CandidateQualifyInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateQualifyOutput>("candidate.qualify", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateRecordAttestationInput = {
  requisition_id: string;
  application_id: string;
  attested?: boolean;
  statement_version?: number;
  source?: "app" | "verbal" | "mail" | "paper";
  source_file_id?: string | null;
  effective_at?: string | null;
};

export type CandidateRecordAttestationOutput = {
  application_id: string;
  internal: InternalOut;
  receipt_id?: number | null;
};

/** Record the internal candidate's self-attestation that their current manager was informed (how it reached you; paper with its file). Required before the slate; no one is notified. */
export const candidateRecordAttestation = (input: CandidateRecordAttestationInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateRecordAttestationOutput>("candidate.record_attestation", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateReinstateInput = {
  requisition_id: string;
  application_id: string;
  reason: string;
};

export type CandidateReinstateOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Reinstate a withdrawn candidate to the stage they left; the automatic disposition is superseded; the Hiring Manager is told. */
export const candidateReinstate = (input: CandidateReinstateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateReinstateOutput>("candidate.reinstate", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateReleaseInput = {
  requisition_id: string;
  application_id: string;
  outcome?: "release" | "pass";
  reason_text?: string | null;
};

export type CandidateReleaseOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Release a held candidate back to the stage they were held from, or pass. */
export const candidateRelease = (input: CandidateReleaseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateReleaseOutput>("candidate.release", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateRemindAdvanceDecisionInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type CandidateRemindAdvanceDecisionOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** D-31: the Hiring Manager's scorecard is in but the advance decision is not, after two business days. */
export const candidateRemindAdvanceDecision = (input: CandidateRemindAdvanceDecisionInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateRemindAdvanceDecisionOutput>("candidate.remind_advance_decision", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateRemindBatchReviewInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type CandidateRemindBatchReviewOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** D-24: the batch is unreviewed after three business days — the card is re-raised and a nudge is drafted for the Recruiter. */
export const candidateRemindBatchReview = (input: CandidateRemindBatchReviewInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateRemindBatchReviewOutput>("candidate.remind_batch_review", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateRemindScreenDecisionInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type CandidateRemindScreenDecisionOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** D-29: the Hiring Manager has not decided on the screen results after three business days. */
export const candidateRemindScreenDecision = (input: CandidateRemindScreenDecisionInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateRemindScreenDecisionOutput>("candidate.remind_screen_decision", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateResolveDuplicateInput = {
  candidate_match_id: string;
  resolution: "kept_distinct" | "dismissed";
  reason: string;
};

export type CandidateResolveDuplicateOutput = {
  candidate_match_id: string;
  status: "open" | "kept_distinct" | "dismissed" | "merged";
  strength: "strong" | "weak";
  reasons: string[];
  origin: string;
  decided_at: string | null;
  decision_reason: string | null;
  reviewed_at: string | null;
  sides: DuplicateSide[];
  may_resolve: boolean;
  receipt_id?: number | null;
};

/** Resolve a duplicate review with a reason: keep the two profiles distinct, or dismiss the match. It closes the review items; nothing is merged here. */
export const candidateResolveDuplicate = (input: CandidateResolveDuplicateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateResolveDuplicateOutput>("candidate.resolve_duplicate", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateReverseDecisionInput = {
  requisition_id: string;
  application_id: string;
  reason: string;
  told_check: boolean;
};

export type CandidateReverseDecisionOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Reverse the last decision before the next stage's booking; a reason and the told-check (nothing reached the candidate) are recorded. */
export const candidateReverseDecision = (input: CandidateReverseDecisionInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateReverseDecisionOutput>("candidate.reverse_decision", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateSearchInput = {
  q?: string | null;
  internal?: boolean | null;
  status?: "Active" | "Archived" | "Merged" | null;
  owner_id?: string | null;
  source?: string | null;
  community_only?: boolean;
  reason_added?: ("finalist" | "alternate" | "silver_medalist" | "timing_decline" | "timing_withdrawal")[];
  segment?: string | null;
  tags?: string[];
  prior_requisition_id?: string | null;
  last_contact_before?: string | null;
  last_contact_after?: string | null;
  sort?: "name" | "last_activity" | "date_added" | "last_contact";
  limit?: number;
  offset?: number;
};

export type SearchCommunity = {
  reason_added: string;
  added_at: string;
  segment?: string | null;
  tags?: string[];
  prior_requisition?: LoopFact | null;
  last_contact_at?: string | null;
};

export type SearchRow = {
  ref: string;
  candidate_no: string;
  projection: "full" | "token" | "masked";
  status: "Active" | "Archived" | "Merged";
  candidate_id?: string | null;
  display_name?: string | null;
  current_employer?: string | null;
  owner_name?: string | null;
  source_channel?: string | null;
  internal?: boolean | null;
  community?: SearchCommunity | null;
  applications?: LoopFact[];
};

export type CandidateSearchOutput = {
  rows: SearchRow[];
  more: boolean;
  total: number;
};

/** Search candidates and talent-community members across requisitions with plain filters (name, email, phone, LinkedIn or Candidate ID; internal; status; owner; source; reason added; segment; tags; prior requisition; last contact) and a sort; each row is trimmed to what you may see. */
export const candidateSearch = (input: CandidateSearchInput = {} as CandidateSearchInput) => callTool<CandidateSearchOutput>("candidate.search", input);

export type CandidateSetInternalProfileInput = {
  requisition_id: string;
  application_id: string;
  expected_version?: number | null;
  department?: string | null;
  title?: string | null;
  leader_name?: string | null;
  location?: string | null;
  grade?: string | null;
  mobility_type?: string | null;
};

export type CandidateSetInternalProfileOutput = {
  application_id: string;
  internal: InternalOut;
  receipt_id?: number | null;
};

/** Record the internal candidate's current department, title, leader, location, grade and mobility type on this application (versioned); the leader is never notified. */
export const candidateSetInternalProfile = (input: CandidateSetInternalProfileInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateSetInternalProfileOutput>("candidate.set_internal_profile", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateSlateOpenInput = {
  requisition_id: string;
  batch_no: number;
};

export type SlateRowNamed = {
  application_id: string;
  token: string;
  display_name: string | null;
  stage: string;
  stage_label: string;
  held_days: number | null;
  hold_reason_label: string | null;
};

export type SlateRowStatus = {
  application_id: string;
  stage: string;
  stage_label: string;
  held_days: number | null;
};

export type SlateRowToken = {
  application_id: string;
  token: string;
  stage: string;
  stage_label: string;
  held_days: number | null;
};

export type CandidateSlateOpenOutput = {
  requisition_id: string;
  batch_no: number;
  projection: "names" | "tokens" | "status";
  state: string;
  state_label: string;
  episode: number;
  submitted_at: string | null;
  opened_at: string | null;
  completed_at: string | null;
  overdue_at: string | null;
  due_at: string | null;
  late: boolean;
  rows: (SlateRowNamed | SlateRowToken | SlateRowStatus)[];
};

/** Open one candidate slate: its state (Draft Batch, Submitted, In HM Review, Completed or Overdue), the submission, response and completion times, the HM review due date and each candidate's stage and held age — names for the Recruiter and the Hiring Manager, tokens for the HR Business Partner, status only for TA leadership and the Coordinator. */
export const candidateSlateOpen = (input: CandidateSlateOpenInput = {} as CandidateSlateOpenInput) => callTool<CandidateSlateOpenOutput>("candidate.slate_open", input);

export type CandidateTimelineInput = {
  candidate_id: string;
  requisition_id?: string | null;
  cursor?: string | null;
  limit?: number;
};

export type ArtifactRef = {
  type: string;
  id: string;
};

export type StageClock = {
  application_id: string;
  requisition_id: string;
  stage: string;
  held_from_stage: string | null;
  sourced_at: string;
  stage_since: string;
  in_stage_days: number | null;
  last_sentence: string | null;
  last_at: string | null;
};

export type TimelineRow = {
  key: string;
  kind: string;
  at: string;
  recorded_at: string | null;
  actor_id: string | null;
  actor_name: string | null;
  entered_by_name: string | null;
  source: string;
  sentence: string;
  channel: string;
  candidate_id: string;
  candidate_no: string;
  application_id: string | null;
  requisition_id: string | null;
  facts: Record<string, unknown>;
  artifact_refs: ArtifactRef[];
};

export type CandidateTimelineOutput = {
  candidate_id: string;
  candidate_no: string;
  rows: TimelineRow[];
  total: number;
  next_cursor: string | null;
  applications: StageClock[];
};

/** Read one candidate's timeline across every requisition you may see, newest first: each line carries the Candidate ID, the application and requisition, the actor, the date and time, the source and its channel, the line's sentence, codes-only facts and the linked records your seat may open; lines of a requisition you are not seated on are absent. Each application whose stage lines you see carries its stage, when it was sourced and entered the stage, its Active Time in the stage and its newest line. */
export const candidateTimeline = (input: CandidateTimelineInput = {} as CandidateTimelineInput) => callTool<CandidateTimelineOutput>("candidate.timeline", input);

export type CandidateUnmergeInput = {
  candidate_id: string;
  merge_event_id: string;
  expected_version: number;
  merged_expected_version: number;
  reason?: string;
};

export type CandidateUnmergeOutput = {
  merge_event_id: string;
  kind: "merge" | "unmerge";
  survivor: ProfileOut;
  merged: ProfileOut;
  receipt_id?: number | null;
};

/** Recover an incorrect merge from its merge event: the merged profile is active again with everything it kept, the contacts the survivor adopted are retired and its own restored, and an append-only recovery event names the merge. Admin only. */
export const candidateUnmerge = (input: CandidateUnmergeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateUnmergeOutput>("candidate.unmerge", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ProfileChanges = {
  display_name?: string | null;
  primary_email?: string | null;
  secondary_email?: string | null;
  phone?: string | null;
  linkedin_url?: string | null;
  location?: string | null;
  current_employer?: string | null;
  employment_history?: EmploymentRow[] | null;
  owner_id?: string | null;
};

export type CandidateUpdateProfileInput = {
  candidate_id: string;
  expected_version: number;
  changes: ProfileChanges;
  reason?: string | null;
};

export type CandidateUpdateProfileOutput = {
  candidate_id: string | null;
  candidate_no: string;
  projection: "full" | "token" | "masked";
  status: "Active" | "Archived" | "Merged";
  version: number;
  merged_into_no?: string | null;
  display_name?: string | null;
  owner_id?: string | null;
  owner_name?: string | null;
  source_channel?: string | null;
  email?: string | null;
  secondary_emails?: string[];
  phone?: string | null;
  linkedin_url?: string | null;
  location?: string | null;
  current_employer?: string | null;
  employment_history?: EmploymentRow[];
  no_email_exception?: NoEmailOut | null;
  applications?: ProfileApplication[];
  community?: CommunityOut | null;
  eligibility?: EligibilityOut[];
  internal?: InternalOut[];
  resume_versions?: ResumeVersionOut[];
  recovery?: RecoveryOut | null;
  receipt_id?: number | null;
};

/** Edit a candidate profile with a field revision per change and a reason when a recorded value changes; an email change keeps the Candidate ID (the old contact is retired, never deleted). TA Manager or Admin hands a profile to another Recruiter. */
export const candidateUpdateProfile = (input: CandidateUpdateProfileInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateUpdateProfileOutput>("candidate.update_profile", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateWithdrawInput = {
  requisition_id: string;
  application_id: string;
  reason_code: string;
  reason_text?: string | null;
  effective_at?: string | null;
};

export type CandidateWithdrawOutput = {
  requisition_id: string;
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** The candidate withdrew: booked rounds cancelled, interviewers told, the automatic candidate_withdrew disposition written at the effective date. */
export const candidateWithdraw = (input: CandidateWithdrawInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateWithdrawOutput>("candidate.withdraw", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CandidateWriteSourcingFeedbackInput = {
  requisition_id: string;
  batch_no: number;
  feedback: string;
};

export type CandidateWriteSourcingFeedbackOutput = {
  requisition_id: string;
  batch_no: number;
  application_ids: string[];
  effective_at: string;
  receipt_id?: number | null;
};

/** Feedback on the batch for the Recruiter's sourcing. */
export const candidateWriteSourcingFeedback = (input: CandidateWriteSourcingFeedbackInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CandidateWriteSourcingFeedbackOutput>("candidate.write_sourcing_feedback", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CloseoutOpenInput = {
  requisition_id: string;
};

export type ApplicationOut = {
  application_id: string;
  candidate_token: string;
  display_name?: string | null;
  opening_index?: number | null;
  stage: string;
  stage_label: string;
  checklist: ItemStateOut[];
  missing: string[];
  ready_for_hris_at?: string | null;
  hris?: HrisOut | null;
  evidence?: EvidenceOut | null;
  final_terms?: FinalTermsOut | null;
  can: CanOut;
};

export type ArchiveOut = {
  snapshot_id: string;
  archived_at: string;
  content_hash: string;
};

export type CanOut = {
  record_item?: boolean;
  validate?: boolean;
  confirm?: boolean;
  correct?: boolean;
  designate?: boolean;
  override?: boolean;
};

export type ClockOut = {
  key: string;
  label: string;
  start?: string | null;
  stop?: string | null;
  calendar_days?: number | null;
  active_days?: number | null;
  hold_days?: number | null;
  unavailable_reason?: string | null;
  opening_index?: number | null;
};

export type EvidenceOut = {
  hris_evidence_id: string;
  outcome: string;
  hire_date?: string | null;
  start_date?: string | null;
  position_no?: string | null;
};

export type FinalTermsOut = {
  start_date?: string | null;
  base?: unknown;
  currency?: string | null;
  bonus_target?: unknown;
  sign_on?: unknown;
  relocation?: unknown;
  lti_present?: boolean | null;
};

export type HrisOut = {
  confirmation_id: string;
  employee_id?: string | null;
  hire_date?: string | null;
  start_date?: string | null;
  source?: string | null;
  hire_kind?: string | null;
  evidence_ref?: string | null;
  correlation_id?: string | null;
};

export type ItemStateOut = {
  key: string;
  required: boolean;
  state: string | null;
  value_date: string | null;
  value_text: string | null;
  source: string | null;
  evidence_file_id: string | null;
};

export type OpeningDatesOut = {
  opening_index: number;
  application_id?: string | null;
  accepted_at?: string | null;
  background_check_at?: string | null;
  ready_for_hris_at?: string | null;
  hris_hired_at?: string | null;
  start_date?: string | null;
  started_at?: string | null;
};

export type StatusWordOut = {
  key: string;
  word: string;
  n?: number | null;
  of?: number | null;
};

export type CloseoutOpenOutput = {
  requisition_id: string;
  status: string;
  status_word: StatusWordOut;
  openings_approved: number;
  archived: boolean;
  archive?: ArchiveOut | null;
  applications: ApplicationOut[];
  dates?: OpeningDatesOut[];
  filled_at?: string | null;
  archived_at?: string | null;
  clocks?: ClockOut[];
};

/** Open a requisition's disposition and handoff: each accepted hire's checklist, HRIS entry and dates. */
export const closeoutOpen = (input: CloseoutOpenInput = {} as CloseoutOpenInput) => callTool<CloseoutOpenOutput>("closeout.open", input);

export type CloseoutReachStartInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type CloseoutReachStartOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** G-56 lapse: the start date has come, so the opening reaches Started (also after archive). */
export const closeoutReachStart = (input: CloseoutReachStartInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CloseoutReachStartOutput>("closeout.reach_start", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompApproveInput = {
  requisition_id: string;
  application_id: string;
  package_version: number;
  decision: "approve" | "rejected" | "revision_required";
  note?: string | null;
  requirement?: "hm" | "hrbp" | null;
};

export type CompApproveOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  offer_id: string | null;
  offer_status: string | null;
  comp_status: string | null;
  reopened_by?: string | null;
  reopened_at?: string | null;
  level: string;
  approved_range?: Record<string, unknown> | null;
  recommendation?: Record<string, unknown> | null;
  range_source?: string | null;
  salary_grade?: string | null;
  market?: Record<string, unknown> | null;
  working_notes?: string | null;
  expectation?: Record<string, unknown> | null;
  internal_comparison?: Record<string, unknown> | null;
  package?: Record<string, unknown> | null;
  history: Record<string, unknown>[];
  approvals?: Record<string, unknown>[];
  readiness?: Record<string, unknown> | null;
  gates: Record<string, unknown>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** The Hiring Manager's or the HR Business Partner's decision on the sent package version: approve, reject or ask for a revision (a note says why). Bound to that exact version; when every applicable approval is given the version is Ready for Verbal Offer and the Recruiter is told. */
export const compApprove = (input: CompApproveInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompApproveOutput>("comp.approve", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompApproveOutOfRangeInput = {
  requisition_id: string;
  application_id: string;
  package_version: number;
  rationale: string;
};

export type CompApproveOutOfRangeOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  offer_id: string | null;
  offer_status: string | null;
  comp_status: string | null;
  reopened_by?: string | null;
  reopened_at?: string | null;
  level: string;
  approved_range?: Record<string, unknown> | null;
  recommendation?: Record<string, unknown> | null;
  range_source?: string | null;
  salary_grade?: string | null;
  market?: Record<string, unknown> | null;
  working_notes?: string | null;
  expectation?: Record<string, unknown> | null;
  internal_comparison?: Record<string, unknown> | null;
  package?: Record<string, unknown> | null;
  history: Record<string, unknown>[];
  approvals?: Record<string, unknown>[];
  readiness?: Record<string, unknown> | null;
  gates: Record<string, unknown>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** The HR Business Partner's approval of a package outside the approved range — rationale required, bound to this exact version. */
export const compApproveOutOfRange = (input: CompApproveOutOfRangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompApproveOutOfRangeOutput>("comp.approve_out_of_range", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompApproveTotalRewardsInput = {
  requisition_id: string;
  application_id: string;
  package_version: number;
  approved_amount: number | string;
  approved_wording: string;
  note?: string | null;
};

export type CompApproveTotalRewardsOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  offer_id: string | null;
  offer_status: string | null;
  comp_status: string | null;
  reopened_by?: string | null;
  reopened_at?: string | null;
  level: string;
  approved_range?: Record<string, unknown> | null;
  recommendation?: Record<string, unknown> | null;
  range_source?: string | null;
  salary_grade?: string | null;
  market?: Record<string, unknown> | null;
  working_notes?: string | null;
  expectation?: Record<string, unknown> | null;
  internal_comparison?: Record<string, unknown> | null;
  package?: Record<string, unknown> | null;
  history: Record<string, unknown>[];
  approvals?: Record<string, unknown>[];
  readiness?: Record<string, unknown> | null;
  gates: Record<string, unknown>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Compensation, as Total Rewards, approves the LTI amount and its wording on this package version, before the Hiring Manager and HR Business Partner approve. */
export const compApproveTotalRewards = (input: CompApproveTotalRewardsInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompApproveTotalRewardsOutput>("comp.approve_total_rewards", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompOpenWorksheetInput = {
  requisition_id: string;
  application_id: string;
};

export type CompOpenWorksheetOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  offer_id: string | null;
  offer_status: string | null;
  comp_status: string | null;
  reopened_by?: string | null;
  reopened_at?: string | null;
  level: string;
  approved_range?: Record<string, unknown> | null;
  recommendation?: Record<string, unknown> | null;
  range_source?: string | null;
  salary_grade?: string | null;
  market?: Record<string, unknown> | null;
  working_notes?: string | null;
  expectation?: Record<string, unknown> | null;
  internal_comparison?: Record<string, unknown> | null;
  package?: Record<string, unknown> | null;
  history: Record<string, unknown>[];
  approvals?: Record<string, unknown>[];
  readiness?: Record<string, unknown> | null;
  gates: Record<string, unknown>;
  can: Record<string, boolean>;
};

/** The compensation worksheet: the range the package is priced against, the candidate's expectation, level, the internal comparison, every package version, its approvals and the readiness gate, and who did what (interviewers never). The Hiring Manager reads the range, the figures and the status, and of the internal equity, working notes and approval evidence only their own acts. */
export const compOpenWorksheet = (input: CompOpenWorksheetInput = {} as CompOpenWorksheetInput) => callTool<CompOpenWorksheetOutput>("comp.open_worksheet", input);

export type CompProposeInput = {
  requisition_id: string;
  application_id: string;
  base: number | string;
  bonus_target?: number | string | null;
  commission?: number | string | null;
  equity?: number | string | null;
  sign_on?: number | string | null;
  relocation?: number | string | null;
  lti?: number | string | null;
  lti_wording?: string | null;
  currency?: string;
  start_date: string;
  for_person?: string | null;
  source?: "paper" | "verbal" | "mail" | "call" | null;
  evidence_ref?: string | null;
  happened_at?: string | null;
};

export type CompProposeOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  offer_id: string | null;
  offer_status: string | null;
  comp_status: string | null;
  reopened_by?: string | null;
  reopened_at?: string | null;
  level: string;
  approved_range?: Record<string, unknown> | null;
  recommendation?: Record<string, unknown> | null;
  range_source?: string | null;
  salary_grade?: string | null;
  market?: Record<string, unknown> | null;
  working_notes?: string | null;
  expectation?: Record<string, unknown> | null;
  internal_comparison?: Record<string, unknown> | null;
  package?: Record<string, unknown> | null;
  history: Record<string, unknown>[];
  approvals?: Record<string, unknown>[];
  readiness?: Record<string, unknown> | null;
  gates: Record<string, unknown>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Prepare the offer package — base, bonus target, commission, sign-on, relocation, LTI and its wording, start date — as a new version, priced against the finalized Compensation recommendation (or, for a requisition from before the review, its own range). The Recruiter prepares; the Hiring Manager and the HR Business Partner approve once it is sent. */
export const compPropose = (input: CompProposeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompProposeOutput>("comp.propose", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompRecordInternalComparisonInput = {
  requisition_id: string;
  application_id: string;
  note: string;
};

export type CompRecordInternalComparisonOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  offer_id: string | null;
  offer_status: string | null;
  comp_status: string | null;
  reopened_by?: string | null;
  reopened_at?: string | null;
  level: string;
  approved_range?: Record<string, unknown> | null;
  recommendation?: Record<string, unknown> | null;
  range_source?: string | null;
  salary_grade?: string | null;
  market?: Record<string, unknown> | null;
  working_notes?: string | null;
  expectation?: Record<string, unknown> | null;
  internal_comparison?: Record<string, unknown> | null;
  package?: Record<string, unknown> | null;
  history: Record<string, unknown>[];
  approvals?: Record<string, unknown>[];
  readiness?: Record<string, unknown> | null;
  gates: Record<string, unknown>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** The HR Business Partner's internal comparison — participation, not an approval; non-blocking for the proposal. */
export const compRecordInternalComparison = (input: CompRecordInternalComparisonInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompRecordInternalComparisonOutput>("comp.record_internal_comparison", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompRecordLeaderApprovalInput = {
  requisition_id: string;
  application_id: string;
  package_version: number;
  approver_name: string;
  evidence_file_id?: string | null;
  evidence_ref?: string | null;
  note?: string | null;
};

export type CompRecordLeaderApprovalOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  offer_id: string | null;
  offer_status: string | null;
  comp_status: string | null;
  reopened_by?: string | null;
  reopened_at?: string | null;
  level: string;
  approved_range?: Record<string, unknown> | null;
  recommendation?: Record<string, unknown> | null;
  range_source?: string | null;
  salary_grade?: string | null;
  market?: Record<string, unknown> | null;
  working_notes?: string | null;
  expectation?: Record<string, unknown> | null;
  internal_comparison?: Record<string, unknown> | null;
  package?: Record<string, unknown> | null;
  history: Record<string, unknown>[];
  approvals?: Record<string, unknown>[];
  readiness?: Record<string, unknown> | null;
  gates: Record<string, unknown>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** The Hiring Manager records the organizational leader's approval of this package version, naming the leader and attaching the evidence (a file or its reference), before the Hiring Manager and HR Business Partner approve. */
export const compRecordLeaderApproval = (input: CompRecordLeaderApprovalInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompRecordLeaderApprovalOutput>("comp.record_leader_approval", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompRecordPackageInput = {
  requisition_id: string;
  application_id: string;
  package_version: number;
};

export type CompRecordPackageOutput = {
  application_id: string;
  offer_id: string;
  package_version: number | null;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Send the prepared package version for approval: the applicable approvals are resolved from its terms and kept on the version (the Hiring Manager and the HR Business Partner always; the organizational leader for a sign-on or relocation; Total Rewards for an LTI), approvals the change did not affect are carried from the version it replaces, and each applicable approver is asked. A version from before B8 is recorded as V1 recorded it. */
export const compRecordPackage = (input: CompRecordPackageInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompRecordPackageOutput>("comp.record_package", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompRemindAllInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type CompRemindAllOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** G-51: the package is not recorded two business days after the outcome — re-raised on the Recruiter, the Hiring Manager and the HR Business Partner. */
export const compRemindAll = (input: CompRemindAllInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompRemindAllOutput>("comp.remind_all", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompReopenInput = {
  requisition_id: string;
  application_id: string;
  reason: string;
};

export type CompReopenOutput = {
  application_id: string;
  offer_id: string;
  package_version: number | null;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Reopen the negotiation: the history stays; the next prepared version is sent for approval again and every check runs again — nothing may go out on the version that was reopened. */
export const compReopen = (input: CompReopenInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompReopenOutput>("comp.reopen", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompReviewAssignReviewerInput = {
  request_id: string;
  expected_version: number;
  reviewer_id: string;
  reason?: string | null;
};

export type CompReviewAssignReviewerOutput = {
  review_id: string;
  request_id: string;
  requisition_id?: string | null;
  status: string;
  reviewer_id?: string | null;
  current_version: number;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** The TA Manager names the Compensation reviewer of a request's review — required on a confidential request, which no Compensation pool opens; replacing a named reviewer needs a reason. */
export const compReviewAssignReviewer = (input: CompReviewAssignReviewerInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompReviewAssignReviewerOutput>("comp_review.assign_reviewer", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompReviewFetchMarketDataInput = {
  request_id: string;
  expected_version: number;
};

export type CompReviewFetchMarketDataOutput = {
  review_id: string;
  request_id: string;
  requisition_id?: string | null;
  status: string;
  reviewer_id?: string | null;
  current_version: number;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Compensation fetches the job's market data from the compensation-data source into a new draft version (labelled simulated when the source is); when the source cannot answer, enter the figures by hand. */
export const compReviewFetchMarketData = (input: CompReviewFetchMarketDataInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompReviewFetchMarketDataOutput>("comp_review.fetch_market_data", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompReviewFinalizeInput = {
  request_id: string;
  expected_version: number;
  approval_evidence?: string | null;
  reason?: string | null;
};

export type CompReviewFinalizeOutput = {
  review_id: string;
  request_id: string;
  requisition_id?: string | null;
  status: string;
  reviewer_id?: string | null;
  current_version: number;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Compensation finalizes the official recommendation and recommended range (In Review → Finalized); once the requisition exists its range becomes the finalized one. Compensation alone finalizes. */
export const compReviewFinalize = (input: CompReviewFinalizeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompReviewFinalizeOutput>("comp_review.finalize", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompReviewOpenInput = {
  request_id: string;
};

export type GradeOption = {
  key: string;
  label: string;
  level: string;
  min: string;
  mid: string;
  max: string;
  currency: string;
};

export type ReviewCan = {
  start?: boolean;
  save?: boolean;
  fetch_market_data?: boolean;
  finalize?: boolean;
  request_change?: boolean;
  assign_reviewer?: boolean;
};

export type VersionOut = {
  version_no: number;
  state: string;
  salary_grade?: string | null;
  market_data?: Record<string, unknown> | null;
  internal_range_min?: string | null;
  internal_range_max?: string | null;
  positioning?: string | null;
  recommendation?: string | null;
  recommended_range_min?: string | null;
  recommended_range_max?: string | null;
  currency?: string | null;
  commission_structure?: string | null;
  working_notes?: string | null;
  approval_evidence?: string | null;
  positioning_level?: string | null;
  bonus_target_pct?: string | null;
  has_commission?: boolean | null;
  data_source: string;
  port_fetched_at?: string | null;
  port_simulated?: boolean | null;
  reason?: string | null;
  author_id: string;
  effective_at: string;
};

export type CompReviewOpenOutput = {
  review_id: string;
  request_id: string;
  requisition_id?: string | null;
  status: string;
  reviewer_id?: string | null;
  started_at?: string | null;
  finalized_at?: string | null;
  finalized_by_id?: string | null;
  current_version: number;
  row_version: number;
  confidential: boolean;
  level: string;
  job_code?: string | null;
  title?: string | null;
  locations?: string[];
  department?: string | null;
  hiring_manager_id?: string | null;
  hiring_manager_name?: string | null;
  target_comp_min?: string | null;
  target_comp_max?: string | null;
  target_currency?: string | null;
  finalized?: VersionOut | null;
  versions?: VersionOut[];
  grades?: GradeOption[];
  level_grade?: string | null;
  can: ReviewCan;
};

/** Open a request's Compensation review: its status and the finalized recommendation for everyone seated on it, each field as the field policy shows it to you; drafts and every version for Compensation only. */
export const compReviewOpen = (input: CompReviewOpenInput = {} as CompReviewOpenInput) => callTool<CompReviewOpenOutput>("comp_review.open", input);

export type CompReviewQueueInput = {
  status?: "pending" | "in_review" | "finalized" | null;
  page_size?: number;
};

export type QueueRow = {
  review_id: string;
  request_id: string;
  requisition_id?: string | null;
  status: string;
  reviewer_id?: string | null;
  confidential: boolean;
  title: string;
  level: string;
  department?: string | null;
  opened_at: string;
};

export type CompReviewQueueOutput = {
  items: QueueRow[];
  total: number;
};

/** Compensation's queue: the reviews you may work — the ones naming you, and the non-confidential ones your scope covers; ids, status and the job, never a figure. */
export const compReviewQueue = (input: CompReviewQueueInput = {} as CompReviewQueueInput) => callTool<CompReviewQueueOutput>("comp_review.queue", input);

export type CompReviewRequestChangeInput = {
  request_id: string;
  reason: string;
};

export type CompReviewRequestChangeOutput = {
  review_id: string;
  request_id: string;
  requisition_id?: string | null;
  status: string;
  reviewer_id?: string | null;
  current_version: number;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** The Recruiter or the HR Business Partner asks Compensation to change a finalized recommendation, with a reason: the review returns to In Review from the finalized version, which is kept. */
export const compReviewRequestChange = (input: CompReviewRequestChangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompReviewRequestChangeOutput>("comp_review.request_change", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompReviewSaveInput = {
  request_id: string;
  expected_version: number;
  salary_grade?: string | null;
  market_data?: Record<string, unknown> | null;
  positioning_level?: "below_mid" | "at_mid" | "above_mid" | null;
  positioning?: string | null;
  recommended_range_min?: number | string | null;
  recommended_range_max?: number | string | null;
  bonus_target_pct?: number | string | null;
  has_commission?: boolean | null;
  commission_structure?: string | null;
  working_notes?: string | null;
  approval_evidence?: string | null;
  reason?: string | null;
};

export type CompReviewSaveOutput = {
  review_id: string;
  request_id: string;
  requisition_id?: string | null;
  status: string;
  reviewer_id?: string | null;
  current_version: number;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Compensation saves a draft version of the review: salary grade, market reference, positioning level and note, recommended range, bonus target, commission, working notes and approval evidence; the internal range, currency and recommendation line follow from the grade and the range. */
export const compReviewSave = (input: CompReviewSaveInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompReviewSaveOutput>("comp_review.save", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CompReviewStartInput = {
  request_id: string;
  expected_version: number;
};

export type CompReviewStartOutput = {
  review_id: string;
  request_id: string;
  requisition_id?: string | null;
  status: string;
  reviewer_id?: string | null;
  current_version: number;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Compensation starts the review (Pending → In Review); a reviewer who starts an unnamed review becomes its named reviewer. */
export const compReviewStart = (input: CompReviewStartInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CompReviewStartOutput>("comp_review.start", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ConductAcknowledgeInput = {
  concern_id: string;
  note?: string | null;
};

export type ConductAcknowledgeOutput = {
  concern_id: string;
  status: string;
  outcome: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Acknowledge a Code of Conduct concern: you have read it and you are handling it. Stops the acknowledgement reminder; the debrief stays blocked until it is cleared. */
export const conductAcknowledge = (input: ConductAcknowledgeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ConductAcknowledgeOutput>("conduct.acknowledge", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ConductClearInput = {
  concern_id: string;
  outcome: string;
  outcome_text: string;
};

export type ConductClearOutput = {
  concern_id: string;
  status: string;
  outcome: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Close a Code of Conduct concern with its outcome (no action · note on record · candidate withdrawn · passed) and what you decided. The debrief is unblocked; the Summary Sheet shows the flag and the outcome, never the words. */
export const conductClear = (input: ConductClearInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ConductClearOutput>("conduct.clear", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ConductDiscloseToHmInput = {
  concern_id: string;
  reason: string;
};

export type ConductDiscloseToHmOutput = {
  concern_id: string;
  status: string;
  outcome: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Tell the Hiring Manager now that a Code of Conduct concern exists, rather than waiting for the debrief. They are told the status only — never the words, never who it is about — and never at all when the concern is about them. */
export const conductDiscloseToHm = (input: ConductDiscloseToHmInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ConductDiscloseToHmOutput>("conduct.disclose_to_hm", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ConductEscalateInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type ConductEscalateOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** F-45 (second occurrence): the concern is still unacknowledged — it is re-routed to an Admin who is neither its subject nor the person who raised it. */
export const conductEscalate = (input: ConductEscalateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ConductEscalateOutput>("conduct.escalate", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ConductListInput = {
  requisition_id?: string | null;
  include_cleared?: boolean;
  limit?: number;
};

export type ListRow = {
  concern_id: string;
  requisition_id: string;
  requisition_title: string;
  status: string;
  blocks_debrief: boolean;
  handled_by: string | null;
  outcome: string | null;
  raised_at: string;
  age_days: number;
  acknowledged: boolean;
  routed_to_me: boolean;
};

export type ConductListOutput = {
  items: ListRow[];
  open_count: number;
};

/** Concerns waiting on you, and every open concern for Admin: status, requisition and age — never the content. */
export const conductList = (input: ConductListInput = {} as ConductListInput) => callTool<ConductListOutput>("conduct.list", input);

export type ConductListMineInput = {
  requisition_id?: string | null;
  include_cleared?: boolean;
  limit?: number;
};

export type MineRow = {
  concern_id: string;
  requisition_id: string;
  requisition_title: string;
  status: string;
  blocks_debrief: boolean;
  handled_by: string | null;
  outcome: string | null;
  raised_at: string;
  age_days: number;
  acknowledged: boolean;
  relation: string;
  raised_by_me: boolean;
  routed_to_me: boolean;
};

export type ConductListMineOutput = {
  items: MineRow[];
  raised_by_me: number;
  routed_to_me: number;
  open_count: number;
};

/** The Code of Conduct concerns you raised and the ones routed to you — status, requisition and age, never the content. The durable way back to one, without a notification. */
export const conductListMine = (input: ConductListMineInput = {} as ConductListMineInput) => callTool<ConductListMineOutput>("conduct.list_mine", input);

export type ConductOpenInput = {
  concern_id: string;
};

export type ConcernOut = {
  concern_id: string;
  exists: boolean;
  requisition_id: string;
  requisition_title: string;
  status: string;
  blocks_debrief: boolean;
  handled_by: string | null;
  outcome: string | null;
  raised_at: string;
  acknowledged_at: string | null;
  cleared_at: string | null;
  disclosed_to_hm_at: string | null;
  projection?: "full";
  concern_text?: string | null;
  subject_role?: string | null;
  subject_name?: string | null;
  candidate_token?: string | null;
  round_label?: string | null;
  raised_by?: string | null;
  routed_to?: string | null;
  alternate_route_reason?: string | null;
  outcome_text?: string | null;
  disclosed_reason?: string | null;
  events?: EventOut[];
  can?: Record<string, boolean>;
};

export type ConcernStatus = {
  concern_id: string;
  exists: boolean;
  requisition_id: string;
  requisition_title: string;
  status: string;
  blocks_debrief: boolean;
  handled_by: string | null;
  outcome: string | null;
  raised_at: string;
  acknowledged_at: string | null;
  cleared_at: string | null;
  disclosed_to_hm_at: string | null;
  projection?: "status";
};

export type EventOut = {
  kind: string;
  at: string;
  by: string | null;
  note: string | null;
};

export type ConductOpenOutput = ConcernOut | ConcernStatus;

/** Open one Code of Conduct concern: the whole record for the person it is routed to, its author and Admin; the process status for the requisition's owner, the Hiring Manager and the named HR Business Partner; nothing at all for the person it is about. */
export const conductOpen = (input: ConductOpenInput = {} as ConductOpenInput) => callTool<ConductOpenOutput>("conduct.open", input);

export type ConductRemindAcknowledgeInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type ConductRemindAcknowledgeOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** F-45 (first occurrence): a Code of Conduct concern is unacknowledged — the person it was routed to is reminded. */
export const conductRemindAcknowledge = (input: ConductRemindAcknowledgeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ConductRemindAcknowledgeOutput>("conduct.remind_acknowledge", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ConductRerouteInput = {
  concern_id: string;
  to_person_id: string;
  reason: string;
};

export type ConductRerouteOutput = {
  concern_id: string;
  status: string;
  outcome: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Hand a Code of Conduct concern to somebody else who can take it — an HR Business Partner or administrator outside this requisition. This is also how a concern nobody could receive is finally placed. */
export const conductReroute = (input: ConductRerouteInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ConductRerouteOutput>("conduct.reroute", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ConfigApproveInput = {
  proposal_id: string;
};

export type ConfigApproveOutput = {
  id: string;
  kind: string;
  key: string;
  version: number;
  status: "draft" | "approved" | "effective" | "superseded" | "rejected";
  label: "Current" | "Scheduled" | "Superseded" | "Draft" | "Rejected";
  effective_from: string;
  note: string | null;
  published_by_id: string | null;
  published_by_name?: string | null;
  published_at: string | null;
  approved_by_id: string | null;
  approved_by_name?: string | null;
  approved_at: string | null;
  superseded_at: string | null;
  supersedes_id: string | null;
  rejected_by_id: string | null;
  rejected_by_name?: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  hotfix: boolean;
  hotfix_reason: string | null;
  payload?: Record<string, unknown> | null;
  receipt_id?: number | null;
};

/** Approve another Admin's draft; due versions take effect and future versions stay scheduled. */
export const configApprove = (input: ConfigApproveInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ConfigApproveOutput>("config.approve", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ConfigListInput = {
  kind?: string | null;
  key?: string | null;
  status?: "draft" | "approved" | "effective" | "superseded" | "rejected" | null;
  limit?: number;
  cursor?: string | null;
};

export type ProposalRow = {
  id: string;
  kind: string;
  key: string;
  version: number;
  status: "draft" | "approved" | "effective" | "superseded" | "rejected";
  label: "Current" | "Scheduled" | "Superseded" | "Draft" | "Rejected";
  effective_from: string;
  note: string | null;
  published_by_id: string | null;
  published_by_name?: string | null;
  published_at: string | null;
  approved_by_id: string | null;
  approved_by_name?: string | null;
  approved_at: string | null;
  superseded_at: string | null;
  supersedes_id: string | null;
  rejected_by_id: string | null;
  rejected_by_name?: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  hotfix: boolean;
  hotfix_reason: string | null;
  payload?: Record<string, unknown> | null;
};

export type ConfigListOutput = {
  rows: ProposalRow[];
  next_cursor: string | null;
};

/** List configuration versions by key, newest first, without their payloads. */
export const configList = (input: ConfigListInput = {} as ConfigListInput) => callTool<ConfigListOutput>("config.list", input);

export type ConfigOpenInput = {
  id: string;
  diff_against?: string | null;
};

export type ConfigOpenOutput = {
  id: string;
  kind: string;
  key: string;
  version: number;
  status: "draft" | "approved" | "effective" | "superseded" | "rejected";
  label: "Current" | "Scheduled" | "Superseded" | "Draft" | "Rejected";
  effective_from: string;
  note: string | null;
  published_by_id: string | null;
  published_by_name?: string | null;
  published_at: string | null;
  approved_by_id: string | null;
  approved_by_name?: string | null;
  approved_at: string | null;
  superseded_at: string | null;
  supersedes_id: string | null;
  rejected_by_id: string | null;
  rejected_by_name?: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  hotfix: boolean;
  hotfix_reason: string | null;
  payload?: Record<string, unknown> | null;
  diff: Record<string, unknown>;
  pinned_requisitions: number;
  pinned_by_version: Record<string, number>;
};

/** Read a configuration payload, its field diff and the live requisitions keeping an earlier pin. */
export const configOpen = (input: ConfigOpenInput = {} as ConfigOpenInput) => callTool<ConfigOpenOutput>("config.open", input);

export type ConfigOptionsInput = {
  kind: "reasons" | "taxonomies" | "status_labels";
  name: string;
};

export type OptionOut = {
  slug: string;
  label: string;
  requires_note?: boolean;
  parent?: string | null;
  tone?: string | null;
  order?: number | null;
};

export type ConfigOptionsOutput = {
  key: string;
  config_version_id: string;
  options: OptionOut[];
};

/** The active choices of one published reason list, taxonomy or status-label set, with the version a record stamps; never a threshold, a template or a band. */
export const configOptions = (input: ConfigOptionsInput = {} as ConfigOptionsInput) => callTool<ConfigOptionsOutput>("config.options", input);

export type ConfigProposeInput = {
  kind: string;
  key: string;
  payload: Record<string, unknown>;
  effective_from: string;
  note: string;
  hotfix?: boolean;
  hotfix_reason?: string | null;
};

export type ConfigProposeOutput = {
  id: string;
  kind: string;
  key: string;
  version: number;
  status: "draft" | "approved" | "effective" | "superseded" | "rejected";
  label: "Current" | "Scheduled" | "Superseded" | "Draft" | "Rejected";
  effective_from: string;
  note: string | null;
  published_by_id: string | null;
  published_by_name?: string | null;
  published_at: string | null;
  approved_by_id: string | null;
  approved_by_name?: string | null;
  approved_at: string | null;
  superseded_at: string | null;
  supersedes_id: string | null;
  rejected_by_id: string | null;
  rejected_by_name?: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  hotfix: boolean;
  hotfix_reason: string | null;
  payload?: Record<string, unknown> | null;
  receipt_id?: number | null;
};

/** Propose a configuration version for a different Admin to approve; hard rules stay locked. */
export const configPropose = (input: ConfigProposeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ConfigProposeOutput>("config.propose", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ConfigRejectInput = {
  proposal_id: string;
  reason: string;
};

export type ConfigRejectOutput = {
  id: string;
  kind: string;
  key: string;
  version: number;
  status: "draft" | "approved" | "effective" | "superseded" | "rejected";
  label: "Current" | "Scheduled" | "Superseded" | "Draft" | "Rejected";
  effective_from: string;
  note: string | null;
  published_by_id: string | null;
  published_by_name?: string | null;
  published_at: string | null;
  approved_by_id: string | null;
  approved_by_name?: string | null;
  approved_at: string | null;
  superseded_at: string | null;
  supersedes_id: string | null;
  rejected_by_id: string | null;
  rejected_by_name?: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  hotfix: boolean;
  hotfix_reason: string | null;
  payload?: Record<string, unknown> | null;
  receipt_id?: number | null;
};

/** Reject another Admin's draft with a reason; retain the proposal and its payload. */
export const configReject = (input: ConfigRejectInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ConfigRejectOutput>("config.reject", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ConfigValidateInput = {
  kind: string;
  key: string;
  payload: Record<string, unknown>;
  effective_from?: string | null;
  hotfix?: boolean;
};

export type ValidateIssue = {
  field: string;
  code: string;
  detail: string;
};

export type ConfigValidateOutput = {
  kind: string;
  key: string;
  valid: boolean;
  issues: ValidateIssue[];
  current_version: number | null;
  diff: Record<string, unknown>;
};

/** Check a proposed configuration version without saving it: every rule a proposal must meet (its shape, its key, the locked keys, the published values it may not remove, the effective date), each failure named, and the change against the version in effect. */
export const configValidate = (input: ConfigValidateInput = {} as ConfigValidateInput) => callTool<ConfigValidateOutput>("config.validate", input);

export type CoordinatorAssignInput = {
  request_id?: string | null;
  requisition_id?: string | null;
  coordinator_id: string;
  expected_version?: number | null;
  reason?: string | null;
};

export type CoordinatorAssignOutput = {
  request_id: string;
  requisition_id?: string | null;
  coordinator_id: string;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Name the Coordinator who creates and posts the requisition (before or after it is created). */
export const coordinatorAssign = (input: CoordinatorAssignInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<CoordinatorAssignOutput>("coordinator.assign", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefAdvanceAgendaInput = {
  requisition_id: string;
  cycle_id: string;
  item: "compiled_scorecards" | "strengths_and_gaps" | "averages_and_recommendations" | "alignment" | "reference_check" | "decision";
  notes?: Record<string, unknown> | null;
};

export type DebriefLevel = {
  band: string;
  offered: string[];
  hire_like: string[];
};

export type MemberOut = {
  application_id: string;
  candidate: string;
  stage: string;
  loop_status: string;
  sheet_status: string;
  withdrawn: boolean;
  scorecards_in: number;
  scorecards_due: number;
  divergences_open: number;
  divergences: Record<string, unknown>[];
  alignments: Record<string, unknown>[];
  my_scorecard: Record<string, unknown> | null;
  reference_election: Record<string, unknown> | null;
  outcome: Record<string, unknown> | null;
  reversal: Record<string, unknown> | null;
  can: Record<string, boolean>;
  concern_flags?: RoomConcernFlag[];
  bound_no_hire?: boolean;
};

export type Proposal = {
  start: string;
  end: string;
};

export type RoomAlternate = {
  application_id: string;
  rank: number;
  cycle_id?: string | null;
  designated_at: string;
};

export type RoomConcernFlag = {
  kind: string;
  band: string;
  binding: boolean;
  scorecard_id: string;
  revision_no: number;
  triggering?: unknown[];
  words?: string | null;
};

export type RoomExceptionRequest = {
  id: string;
  application_id: string;
  competency_type: string;
  competency_key: string;
  reason: string;
  owner_id: string;
  status: string;
  hm_agreed_at?: string | null;
  recruiter_agreed_at?: string | null;
};

export type RoomExtras = {
  exception_requests?: RoomExceptionRequest[];
  alternates?: RoomAlternate[];
  room_revisions?: RoomRevisionView[];
  search_outcome?: RoomSearchOutcome | null;
  preparation_note?: RoomPreparationNote | null;
  band?: "below_vp" | "vp_plus" | "legacy";
};

export type RoomPreparationNote = {
  id: string;
  version: number;
  body: string;
  source_refs?: SourceRef[];
  state: string;
  author_id?: string | null;
  at?: string | null;
};

export type RoomRevisionView = {
  id: string;
  cycle_id: string;
  application_id: string;
  scorecard_id: string;
  original_revision_id: string;
  revision_id: string;
  rationale: string;
  author_id: string;
  at: string;
};

export type RoomSearchOutcome = {
  id: string;
  kind: string;
  rationale: string;
  source: string;
  at: string;
};

export type SourceRef = {
  scorecard_id: string;
  revision_id?: string | null;
};

export type DebriefAdvanceAgendaOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** Tick an agenda item (the guide's order) and keep the notes per criterion. */
export const debriefAdvanceAgenda = (input: DebriefAdvanceAgendaInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefAdvanceAgendaOutput>("debrief.advance_agenda", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefAgreeAdditionalInterviewInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  request_id: string;
};

export type DebriefAgreeAdditionalInterviewOutput = {
  id: string;
  application_id: string;
  competency_type: string;
  competency_key: string;
  reason: string;
  owner_id: string;
  status: string;
  hm_agreed_at: string | null;
  recruiter_agreed_at: string | null;
  awaiting_person_id?: string | null;
  receipt_id?: number | null;
};

/** Agree, as the other of the Hiring Manager and the Recruiter, to an additional interview. */
export const debriefAgreeAdditionalInterview = (input: DebriefAgreeAdditionalInterviewInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefAgreeAdditionalInterviewOutput>("debrief.agree_additional_interview", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefCancelInput = {
  requisition_id: string;
  cycle_id: string;
  reason: string;
};

export type DebriefCancelOutput = {
  cycle_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Cancel a cycle before the reveal; nothing froze; the sheets are discarded as views. */
export const debriefCancel = (input: DebriefCancelInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefCancelOutput>("debrief.cancel", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefCloseInput = {
  requisition_id: string;
  cycle_id: string;
};

export type DebriefCloseOutput = {
  cycle_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Close the room once every member has an outcome and the roll is complete; the export unlocks for the Recruiter and the Hiring Manager. */
export const debriefClose = (input: DebriefCloseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefCloseOutput>("debrief.close", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefConfirmAttendanceInput = {
  requisition_id: string;
  cycle_id: string;
};

export type DebriefConfirmAttendanceOutput = {
  cycle_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Confirm you will attend. */
export const debriefConfirmAttendance = (input: DebriefConfirmAttendanceInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefConfirmAttendanceOutput>("debrief.confirm_attendance", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefConfirmOutcomeInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
};

export type DebriefConfirmOutcomeOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** Confirm the outcome someone wrote down for you. Only the Hiring Manager themselves — never a stand-in, never the person who typed it. The confirmation supersedes the provisional record and opens the offer work. */
export const debriefConfirmOutcome = (input: DebriefConfirmOutcomeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefConfirmOutcomeOutput>("debrief.confirm_outcome", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefCountersignReversalInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  told_check?: boolean;
};

export type DebriefCountersignReversalOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** The Recruiter's second signature on a reversal — never the same person who asked for it. It takes the outcome back: the candidate returns to the debrief, a compensation worksheet that had opened is withdrawn, and the room decides again. */
export const debriefCountersignReversal = (input: DebriefCountersignReversalInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefCountersignReversalOutput>("debrief.countersign_reversal", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefCreateInput = {
  requisition_id: string;
  application_ids?: string[] | null;
  early_start_reason?: string | null;
  excluded?: Record<string, string>[];
};

export type DebriefCreateOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** Create a cycle over the member applications (default: every panel application with a complete loop, and every candidate a room continued, which makes the cycle reconvened); an incomplete loop or an excluded complete-loop application needs its exception in the same call; sheets compile sealed; required attendees derived. */
export const debriefCreate = (input: DebriefCreateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefCreateOutput>("debrief.create", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefDeclineAttendanceInput = {
  requisition_id: string;
  cycle_id: string;
  reason: string;
};

export type DebriefDeclineAttendanceOutput = {
  cycle_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** You cannot attend: the written-input obligation opens (due at the debrief's start, F-48); the Recruiter is told. */
export const debriefDeclineAttendance = (input: DebriefDeclineAttendanceInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefDeclineAttendanceOutput>("debrief.decline_attendance", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefLapseReversalInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type DebriefLapseReversalOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** H-59: five business days passed without the second signature — the reversal lapses and the recorded outcome stands; both people are told. */
export const debriefLapseReversal = (input: DebriefLapseReversalInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefLapseReversalOutput>("debrief.lapse_reversal", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefListMineInput = {
  requisition_id?: string | null;
};

export type CycleRow = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  status: string;
  start: string | null;
  candidates: string[];
  candidates_named: boolean;
  level: string;
  my_role: string;
  my_attendance: string | null;
  my_attendance_label: string;
  my_scorecards_due: number;
  my_scorecards?: OwnScorecardRow[];
};

export type OwnScorecardRow = {
  scorecard_id: string;
  application_id: string;
  scorecard_status: string | null;
  due_at: string | null;
};

export type DebriefListMineOutput = {
  items: CycleRow[];
};

/** My debriefs: cycles I facilitate, attend or hold a member scorecard on — the requisition and its level band, the stage, date, candidates (tokens until the reveal), my ownership, my attendance (Required · Attended · Not attended) and my scorecards due. */
export const debriefListMine = (input: DebriefListMineInput = {} as DebriefListMineInput) => callTool<DebriefListMineOutput>("debrief.list_mine", input);

export type DebriefNotifyOverdueInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type DebriefNotifyOverdueOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** F-47: the last member scorecard landed three business days ago and the cycle has not revealed. */
export const debriefNotifyOverdue = (input: DebriefNotifyOverdueInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefNotifyOverdueOutput>("debrief.notify_overdue", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefOpenInput = {
  requisition_id: string;
  cycle_id: string;
};

export type DebriefOpenOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
};

/** The room: attendees and the roll, members with sheet status, the reveal gates in plain words, the agenda, alignment rows (values after the reveal), the reference election and the outcome; its status on the chain Not Ready, Scheduled, Roll Call, Reveal, Discussion, Alignment, Outcome Recorded or Blocked. The Coordinator reads the status only; the TA Manager reads the room once it is revealed and the Admin reads the room, and each such read is receipted. */
export const debriefOpen = (input: DebriefOpenInput = {} as DebriefOpenInput) => callTool<DebriefOpenOutput>("debrief.open", input);

export type DebriefOrderAlternatesInput = {
  requisition_id: string;
  application_ids: string[];
  for_person?: string | null;
  source?: "paper" | "verbal" | "mail" | "call" | null;
  evidence_ref?: string | null;
};

export type AlternateRow = {
  application_id: string;
  rank: number;
  cycle_id: string;
  designated_at: string;
};

export type DebriefOrderAlternatesOutput = {
  requisition_id: string;
  alternates: AlternateRow[];
  receipt_id?: number | null;
};

/** Rank the active alternates (rank 1 first; every active alternate exactly once). The Hiring Manager's own order; a Recruiter keys in the order the Hiring Manager gave them, with how it came in. */
export const debriefOrderAlternates = (input: DebriefOrderAlternatesInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefOrderAlternatesOutput>("debrief.order_alternates", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefRecordAbsenceInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type DebriefRecordAbsenceOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** F-48: a declined attendee's written input did not arrive by the debrief's start — recorded absent; the Recruiter is told. */
export const debriefRecordAbsence = (input: DebriefRecordAbsenceInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefRecordAbsenceOutput>("debrief.record_absence", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefRecordAttendanceInput = {
  requisition_id: string;
  cycle_id: string;
  attendance: Record<string, unknown>[];
  held_offline?: boolean;
  offline_reason?: string | null;
  held_at?: string | null;
};

export type DebriefRecordAttendanceOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** Take the roll: present · absent with written input · absent, per required attendee (stand-ins attributed). */
export const debriefRecordAttendance = (input: DebriefRecordAttendanceInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefRecordAttendanceOutput>("debrief.record_attendance", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefRecordHrbpNoteInput = {
  requisition_id: string;
  cycle_id: string;
  application_id?: string | null;
  text: string;
};

export type DebriefRecordHrbpNoteOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** The HR Business Partner's note in the room (only when there is something to raise). */
export const debriefRecordHrbpNote = (input: DebriefRecordHrbpNoteInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefRecordHrbpNoteOutput>("debrief.record_hrbp_note", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefRecordOutcomeInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  outcome: "extend_offer" | "hold_as_alternate" | "additional_round" | "pass" | "continue";
  rationale: string;
  reason_code?: string | null;
  for_person?: string | null;
  source?: "paper" | "verbal" | "call" | "mail" | "in_room" | null;
  source_file_id?: string | null;
  evidence_ref?: string | null;
  happened_at?: string | null;
};

export type DebriefRecordOutcomeOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** The Hiring Manager's outcome per candidate: extend an offer · hold as alternate · run an additional round · pass · continue. Every outcome needs every required attendee present (or covered by a live stand-in) and every required scorecard turned in. Extend/alternate need every alignment on Hire (H2), every value scored (H3), no binding No Hire (H1 and the level rule), every divergence resolved and the reference election recorded. A Recruiter or Admin may write down the outcome the Hiring Manager gave them (for_person, how it reached them and the evidence; in the room, with the Hiring Manager present, no evidence) — that record is provisional until the Hiring Manager confirms it. */
export const debriefRecordOutcome = (input: DebriefRecordOutcomeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefRecordOutcomeOutput>("debrief.record_outcome", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefRecordReferenceDecisionInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  elected: boolean;
  references_to_collect?: string | null;
  reason_when_no?: string | null;
};

export type DebriefRecordReferenceDecisionOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** Reference check? Yes with the references to collect · No with a reason — never left blank. */
export const debriefRecordReferenceDecision = (input: DebriefRecordReferenceDecisionInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefRecordReferenceDecisionOutput>("debrief.record_reference_decision", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefRecordSearchOutcomeInput = {
  requisition_id: string;
  cycle_id: string;
  rationale: string;
};

export type DebriefRecordSearchOutcomeOutput = {
  id: string;
  requisition_id: string;
  cycle_id: string;
  kind: string;
  source: string;
  at: string;
  item_key: string;
  receipt_id?: number | null;
};

/** Reopen the search from the debrief room: one per debrief, with its reason. Opens the Recruiter's Reopen task on the requisition and tells the Hiring Manager and the Coordinator; nothing else changes state. */
export const debriefRecordSearchOutcome = (input: DebriefRecordSearchOutcomeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefRecordSearchOutcomeOutput>("debrief.record_search_outcome", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefRequestAdditionalInterviewInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  competency_type: "value" | "skill";
  competency_key: string;
  reason?: string;
  owner_id?: string | null;
};

export type DebriefRequestAdditionalInterviewOutput = {
  id: string;
  application_id: string;
  competency_type: string;
  competency_key: string;
  reason: string;
  owner_id: string;
  status: string;
  hm_agreed_at: string | null;
  recruiter_agreed_at: string | null;
  awaiting_person_id?: string | null;
  receipt_id?: number | null;
};

/** Ask for an additional interview on one unassessed competency, with a reason and an owner. */
export const debriefRequestAdditionalInterview = (input: DebriefRequestAdditionalInterviewInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefRequestAdditionalInterviewOutput>("debrief.request_additional_interview", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefRescheduleInput = {
  requisition_id: string;
  cycle_id: string;
  start?: string | null;
  duration_minutes?: number;
  link?: string | null;
};

export type DebriefRescheduleOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members?: MemberOut[];
  agenda?: Record<string, unknown>[];
  reveal_gaps?: string[];
  notes?: Record<string, unknown>[];
  hrbp_notes?: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** Rebook the room at a new time. */
export const debriefReschedule = (input: DebriefRescheduleInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefRescheduleOutput>("debrief.reschedule", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefResolveDivergenceInput = {
  requisition_id: string;
  cycle_id: string;
  divergence_id: string;
  resolution_note: string;
};

export type DebriefResolveDivergenceOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** Record the room's resolution note on a flagged spread. */
export const debriefResolveDivergence = (input: DebriefResolveDivergenceInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefResolveDivergenceOutput>("debrief.resolve_divergence", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefRevealInput = {
  requisition_id: string;
  cycle_id: string;
};

export type DebriefRevealOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** The one gated transition: every gate must pass (concerns cleared, HRBP present, scorecards in or excepted, rounds held or excepted, values covered or excepted, loops complete or excepted); membership and evidence freeze; sheets reveal; members enter the debrief stage. */
export const debriefReveal = (input: DebriefRevealInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefRevealOutput>("debrief.reveal", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefReverseOutcomeInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  reason: string;
  source_file_id?: string | null;
};

export type DebriefReverseOutcomeOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** Ask for a recorded outcome to be taken back (reason required). It takes two signatures: yours and the Recruiter's, within five business days — after that the outcome stands. It is not available once a verbal offer has gone out. */
export const debriefReverseOutcome = (input: DebriefReverseOutcomeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefReverseOutcomeOutput>("debrief.reverse_outcome", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefSavePreparationNotesInput = {
  body?: string | null;
  source_refs?: SourceRef[];
  state?: "active" | "dismissed";
  expected_version?: number | null;
  requisition_id: string;
  cycle_id: string;
};

export type DebriefSavePreparationNotesOutput = {
  subject_type: string;
  subject_id: string;
  kind: string;
  note: Record<string, unknown>;
  versions: number;
  receipt_id?: number | null;
};

/** Save the facilitator's debrief preparation as a new version citing the members' scorecards (edit, correct or dismiss); never generated. */
export const debriefSavePreparationNotes = (input: DebriefSavePreparationNotesInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefSavePreparationNotesOutput>("debrief.save_preparation_notes", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefScheduleInput = {
  requisition_id: string;
  cycle_id: string;
  start?: string | null;
  duration_minutes?: number;
  link?: string | null;
};

export type DebriefScheduleOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members?: MemberOut[];
  agenda?: Record<string, unknown>[];
  reveal_gaps?: string[];
  notes?: Record<string, unknown>[];
  hrbp_notes?: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** Book the room: all required attendees from the calendar's free/busy (proposals without a start; one booking with a start); the invitation carries no sheet, scores or attachments. */
export const debriefSchedule = (input: DebriefScheduleInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefScheduleOutput>("debrief.schedule", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefScheduleAdditionalRoundInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  interviewer_id: string;
  duration_minutes?: number;
  format?: "virtual" | "in_person";
  values?: string[];
  skill_numbers?: number[];
  label?: string | null;
};

export type DebriefScheduleAdditionalRoundOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** After an additional-round outcome: add the extra round for this candidate only, name its interviewer and put the occurrence on the board to be booked. The Recruiter or the Hiring Manager (or the stand-in they gave approve to) adds it; the round it produces is debriefed in a NEW cycle. */
export const debriefScheduleAdditionalRound = (input: DebriefScheduleAdditionalRoundInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefScheduleAdditionalRoundOutput>("debrief.schedule_additional_round", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefSetAlignmentInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  value: "hire" | "hire_with_reservations" | "no_hire" | "strong_hire" | "consider_other_role";
};

export type DebriefSetAlignmentOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** Your own alignment on a candidate in the revealed room, from the options the requisition offers (Hire · No Hire under the level rule; Hire · Hire with Reservations · No Hire on a requisition pinned before it); every change is kept; when a scorecard on this candidate binds No Hire every participant's row is bound to No Hire and cannot move. */
export const debriefSetAlignment = (input: DebriefSetAlignmentInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefSetAlignmentOutput>("debrief.set_alignment", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefSubmitWrittenInputInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  text: string;
  alignment?: "hire" | "hire_with_reservations" | "no_hire" | "strong_hire" | "consider_other_role" | null;
  file_id?: string | null;
};

export type DebriefSubmitWrittenInputOutput = {
  cycle_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Your written input for one candidate after you have said you cannot attend: what you would have said in the room, and optionally the alignment you would have given. The Recruiter reads it into the room and it is printed beside your row as written input — it is never a binding alignment: yours stays missing unless you set it yourself. Sending it again supersedes what you sent before. */
export const debriefSubmitWrittenInput = (input: DebriefSubmitWrittenInputInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefSubmitWrittenInputOutput>("debrief.submit_written_input", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefWithdrawAdditionalInterviewInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  request_id: string;
  reason?: string;
};

export type DebriefWithdrawAdditionalInterviewOutput = {
  id: string;
  application_id: string;
  competency_type: string;
  competency_key: string;
  reason: string;
  owner_id: string;
  status: string;
  hm_agreed_at: string | null;
  recruiter_agreed_at: string | null;
  awaiting_person_id?: string | null;
  receipt_id?: number | null;
};

/** Withdraw an additional interview request before the outcome uses it. */
export const debriefWithdrawAdditionalInterview = (input: DebriefWithdrawAdditionalInterviewInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefWithdrawAdditionalInterviewOutput>("debrief.withdraw_additional_interview", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DebriefWithdrawReversalInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  reason: string;
};

export type DebriefWithdrawReversalOutput = {
  cycle_id: string;
  requisition_id: string;
  requisition_title: string;
  sequence: number;
  kind: string;
  status: string;
  projection: string;
  facilitator: string;
  booking: Record<string, unknown> | null;
  proposals: Proposal[];
  required_attendees: Record<string, unknown>[];
  members: MemberOut[];
  agenda: Record<string, unknown>[];
  reveal_gaps: string[];
  notes: Record<string, unknown>[];
  hrbp_notes: Record<string, unknown>[];
  revealed_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  can: Record<string, boolean>;
  room_status?: string;
  blocked_reasons?: string[];
  held_offline?: boolean;
  offline_reason?: string | null;
  room?: RoomExtras | null;
  level?: DebriefLevel | null;
  receipt_id?: number | null;
};

/** Take back your own request for a reversal before the Recruiter has countersigned it; the recorded outcome stands. */
export const debriefWithdrawReversal = (input: DebriefWithdrawReversalInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DebriefWithdrawReversalOutput>("debrief.withdraw_reversal", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DelegationApproveProposalInput = {
  delegation_id: string;
};

export type DelegationApproveProposalOutput = {
  id: string;
  status: string;
  seat: string;
  requisition_id: string | null;
  request_id: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Accept a suggested stand-in for your seat. The checks run again before it takes effect. */
export const delegationApproveProposal = (input: DelegationApproveProposalInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DelegationApproveProposalOutput>("delegation.approve_proposal", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DelegationExpireInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type DelegationExpireOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** H-57: the stand-in's last day has passed — archived, both sides told (the window was already read at decision time). */
export const delegationExpire = (input: DelegationExpireInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DelegationExpireOutput>("delegation.expire", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DelegationGrantInput = {
  seat: "hiring_manager" | "named_hrbp" | "hrbp_seat";
  grantee_id: string;
  capabilities: ("attend" | "intake" | "approve" | "decide")[];
  requisition_id?: string | null;
  request_id?: string | null;
  valid_from?: string | null;
  valid_to: string;
  reason?: string | null;
};

export type DelegationGrantOutput = {
  id: string;
  status: string;
  seat: string;
  requisition_id: string | null;
  request_id: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Name a stand-in for your seat while you are away: what they may do (attend · work on the intake · give your approvals · make your decisions) and for how long, up to 60 days. They act under their own name, and your own action always wins. */
export const delegationGrant = (input: DelegationGrantInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DelegationGrantOutput>("delegation.grant", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DelegationListInput = {
  requisition_id?: string | null;
  person_id?: string | null;
};

export type DelegationOut = {
  id: string;
  seat: string;
  seat_label: string;
  status: string;
  requisition_id: string | null;
  requisition_title: string | null;
  request_id: string | null;
  grantor_id: string;
  grantor_name: string;
  grantee_id: string;
  grantee_name: string;
  proposed_by_id: string | null;
  proposed_by_name: string | null;
  capabilities: string[];
  capability_labels: string[];
  valid_from: string;
  valid_to: string;
  reason: string | null;
  revoked_at: string | null;
  live: boolean;
};

export type DirectoryPerson = {
  person_id: string;
  name: string;
  roles: string[];
  exclusions: Record<string, string | null>;
};

export type DelegationListOutput = {
  directory?: DirectoryPerson[];
  delegations: DelegationOut[];
  mine: Record<string, unknown>;
};

/** Stand-ins: the ones on a requisition, or the ones a person has given and received. */
export const delegationList = (input: DelegationListInput = {} as DelegationListInput) => callTool<DelegationListOutput>("delegation.list", input);

export type DelegationListGlobalInput = {
  limit?: number;
  cursor?: string | null;
  seat?: string | null;
  grantor?: string | null;
  grantee?: string | null;
  requisition?: string | null;
  state?: string | null;
};

export type DelegationListGlobalOutput = {
  items: DelegationOut[];
  next_cursor?: string | null;
};

/** Admin's paged and filtered retained stand-ins, without a people-directory join. */
export const delegationListGlobal = (input: DelegationListGlobalInput = {} as DelegationListGlobalInput) => callTool<DelegationListGlobalOutput>("delegation.list_global", input);

export type DelegationProposeInput = {
  requisition_id: string;
  seat: "hiring_manager" | "named_hrbp" | "hrbp_seat";
  grantee_id: string;
  capabilities: ("attend" | "intake" | "approve" | "decide")[];
  valid_from?: string | null;
  valid_to: string;
  reason: string;
};

export type DelegationProposeOutput = {
  id: string;
  status: string;
  seat: string;
  requisition_id: string | null;
  request_id: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Suggest a stand-in for a seat on your requisition. It does nothing until the person whose seat it is (or Admin) approves it. */
export const delegationPropose = (input: DelegationProposeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DelegationProposeOutput>("delegation.propose", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DelegationRevokeInput = {
  delegation_id: string;
  reason: string;
};

export type DelegationRevokeOutput = {
  id: string;
  status: string;
  seat: string;
  requisition_id: string | null;
  request_id: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** End a stand-in now, with a reason — the person who gave it, the person who holds it, Admin, or (while it is still a suggestion) the Recruiter who suggested it. Anything already recorded stays as it was. */
export const delegationRevoke = (input: DelegationRevokeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DelegationRevokeOutput>("delegation.revoke", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DispositionAddKeepWarmInput = {
  requisition_id: string;
  application_id: string;
  why: string;
};

export type DispositionAddKeepWarmOutput = {
  application_id: string;
  disposition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Keep warm? — the Recruiter writes the flag and the why on the application (an eligibility basis for the talent community, never a membership). */
export const dispositionAddKeepWarm = (input: DispositionAddKeepWarmInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DispositionAddKeepWarmOutput>("disposition.add_keep_warm", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DispositionDraftClosingNoteInput = {
  requisition_id: string;
  application_id: string;
};

export type DispositionDraftClosingNoteOutput = {
  application_id: string;
  subject: string;
  body: string;
  template: string;
};

/** The closing note for one candidate, rendered by reason code and furthest stage for the Recruiter to send from the applicant tracking system — nothing leaves the app. */
export const dispositionDraftClosingNote = (input: DispositionDraftClosingNoteInput = {} as DispositionDraftClosingNoteInput) => callTool<DispositionDraftClosingNoteOutput>("disposition.draft_closing_note", input);

export type DispositionOpenWorklistInput = {
  requisition_id: string;
};

export type CountRow = {
  code: string | null;
  code_label: string | null;
  count: number;
};

export type ReasonOut = {
  slug: string;
  label: string | null;
  config_version_id: string | null;
};

export type Row = {
  application_id: string;
  candidate: string;
  token: string;
  furthest_stage: string;
  furthest_stage_label: string;
  stage: string;
  carried_reason: string | null;
  code: string | null;
  code_label: string | null;
  effective_at: string | null;
  obligation_due_at: string | null;
  overdue: boolean;
  note_sent: Record<string, unknown> | null;
  done: boolean;
  automatic: boolean;
  keep_warm: boolean;
  withdrawal_reason?: ReasonOut | null;
  decline_reason?: ReasonOut | null;
  eligibility?: string | null;
  member?: boolean;
};

export type DispositionOpenWorklistOutput = {
  requisition_id: string;
  requisition_status: string;
  projection: string;
  target_days: number;
  open_count: number;
  recorded_count: number;
  overdue_count: number;
  codes: Record<string, unknown>[];
  rows: Row[];
  by_code: CountRow[];
  keep_warm: Record<string, unknown>[];
  can: Record<string, boolean>;
};

/** The disposition worklist: every candidate who left the live set — furthest stage, carried reason, code, date, closing note sent, done, the withdrawal or offer-decline reason with its list version, talent-community eligibility and membership (full for the Recruiter; counts and codes for the Hiring Manager, the HR Business Partner and the closeout seats: Coordinator, TA Manager, Admin). */
export const dispositionOpenWorklist = (input: DispositionOpenWorklistInput = {} as DispositionOpenWorklistInput) => callTool<DispositionOpenWorklistOutput>("disposition.open_worklist", input);

export type DispositionRecordInput = {
  requisition_id: string;
  application_id: string;
  code: string;
  effective_at?: string | null;
  note?: string | null;
};

export type DispositionRecordOutput = {
  application_id: string;
  disposition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Record the disposition: a reason code from the configured dictionary, the effective date (defaults to the decision that stopped the candidate), a note; the application becomes dispositioned. */
export const dispositionRecord = (input: DispositionRecordInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DispositionRecordOutput>("disposition.record", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DispositionRecordNoteSentInput = {
  requisition_id: string;
  application_id: string;
  sent_at?: string | null;
  reference?: string | null;
};

export type DispositionRecordNoteSentOutput = {
  application_id: string;
  disposition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** The closing note went from the applicant tracking system: the date (and its reference) — communication evidence, separate from the record. */
export const dispositionRecordNoteSent = (input: DispositionRecordNoteSentInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DispositionRecordNoteSentOutput>("disposition.record_note_sent", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DispositionRemindWorklistInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type DispositionRemindWorklistOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** A-8 (amber): the disposition worklist is behind its target — re-raised on the Recruiter's Today with the count. */
export const dispositionRemindWorklist = (input: DispositionRemindWorklistInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<DispositionRemindWorklistOutput>("disposition.remind_worklist", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseAssignReviewersInput = {
  requisition_id: string;
  reviewer_ids: string[];
  reason?: string | null;
};

export type ExerciseAssignReviewersOutput = {
  requisition_id: string;
  reviewer_ids: string[];
  receipt_id?: number | null;
};

/** Name the assessment's reviewers (at most six: the Hiring Manager, the Recruiter or people seated on the panel); the Hiring Manager or the Recruiter, before or after the plan is finalized. Removing a reviewer after work has come back needs a reason. */
export const exerciseAssignReviewers = (input: ExerciseAssignReviewersInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseAssignReviewersOutput>("exercise.assign_reviewers", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseCompleteInput = {
  requisition_id: string;
  application_id: string;
  completion_outcome: string;
};

export type ExerciseCompleteOutput = {
  application_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Record a Reviewed assessment as Completed with its outcome; the Hiring Manager or the Recruiter records it, and the Hiring Manager, the Recruiter, the owner and the reviewers are told. */
export const exerciseComplete = (input: ExerciseCompleteInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseCompleteOutput>("exercise.complete", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseCoverageCheckInput = {
  requisition_id: string;
};

export type ExerciseCoverageCheckOutput = {
  requisition_id: string;
  required: boolean | null;
  advancing: number;
  sent: number;
  not_sent: Record<string, unknown>[];
};

/** Equity check: advancing candidates who have not been sent the exercise the intake requires. */
export const exerciseCoverageCheck = (input: ExerciseCoverageCheckInput = {} as ExerciseCoverageCheckInput) => callTool<ExerciseCoverageCheckOutput>("exercise.coverage_check", input);

export type ExerciseDecideLapseInput = {
  requisition_id: string;
  application_id: string;
  outcome: "extend" | "proceed_without" | "pass";
  reason: string;
  due_at?: string | null;
};

export type ExerciseDecideLapseOutput = {
  application_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** The work did not come back by the due date: extend with a new due, proceed without it, or pass — with a reason. */
export const exerciseDecideLapse = (input: ExerciseDecideLapseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseDecideLapseOutput>("exercise.decide_lapse", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseEvaluateInput = {
  requisition_id: string;
  application_id: string;
  criteria_findings: Record<string, unknown>[];
  strengths?: string | null;
  gaps?: string | null;
  evaluation_text: string;
  advisory_rating?: number | null;
};

export type ExerciseEvaluateOutput = {
  application_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** The Hiring Manager's review when they are an assigned reviewer: findings per criterion, strengths, gaps, text; an optional advisory rating that never enters any average or the two-2s count. */
export const exerciseEvaluate = (input: ExerciseEvaluateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseEvaluateOutput>("exercise.evaluate", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseExtendInput = {
  requisition_id: string;
  application_id: string;
  due_at: string;
  reason: string;
};

export type ExerciseExtendOutput = {
  application_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** An accommodation or extension: a new due date with a reason (the HR Business Partner consulted). */
export const exerciseExtend = (input: ExerciseExtendInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseExtendOutput>("exercise.extend", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseOpenInput = {
  requisition_id: string;
  application_id: string;
};

export type PersonOut = {
  person_id: string;
  name: string;
};

export type ReviewOut = {
  reviewer_id: string;
  reviewer: string;
  criteria_findings: Record<string, unknown>[];
  evaluation_text: string;
  advisory_rating: number | null;
  submitted_at: string;
};

export type ReviewerOut = {
  person_id: string;
  name: string;
  reviewed_at: string | null;
};

export type ExerciseOpenOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  projection: string;
  required: boolean | null;
  not_required_reason: string | null;
  brief_version: number | null;
  brief_text: string | null;
  criteria: string[];
  expected_effort: string | null;
  due_window_days: number | null;
  status: string;
  sent_at: string | null;
  due_at: string | null;
  returned_at: string | null;
  return_file_ids: string[];
  evaluated_at: string | null;
  evaluated_by: string | null;
  evaluation: Record<string, unknown> | null;
  outcome: string | null;
  extension_reason: string | null;
  can: Record<string, boolean>;
  display: string;
  display_reason: string | null;
  assessment_type: string | null;
  owner: PersonOut | null;
  stage_round_key: string | null;
  stage_round_label: string | null;
  reviewers: ReviewerOut[];
  reviews_in: number;
  reviews_total: number;
  my_review: ReviewOut | null;
  reviews: ReviewOut[];
  completed_at: string | null;
  completion_outcome: string | null;
  withdrawn_at: string | null;
  withdrawal_reason: string | null;
};

/** The assessment for one candidate on its chain (Not Required or Required, Draft, Sent, Submitted, Reviewed, Completed or Withdrawn): in full for the Hiring Manager and the Recruiter; the brief, the returned work and their own review for an assigned reviewer; the status and dates only for the HR Business Partner, the Coordinator and the TA Manager; the brief for an interviewer with a round on the candidate. */
export const exerciseOpen = (input: ExerciseOpenInput = {} as ExerciseOpenInput) => callTool<ExerciseOpenOutput>("exercise.open", input);

export type ExercisePromptLapseDecisionInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type ExercisePromptLapseDecisionOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** D-32: the assessment was not returned by the due date — the Hiring Manager decides (extend · proceed without · pass); the Recruiter and the owner are told it is late. */
export const exercisePromptLapseDecision = (input: ExercisePromptLapseDecisionInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExercisePromptLapseDecisionOutput>("exercise.prompt_lapse_decision", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExercisePromptReminderInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type ExercisePromptReminderOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** D-32 reminder: the assessment is due tomorrow — the Recruiter and the assessment's owner nudge the candidate from the ATS. */
export const exercisePromptReminder = (input: ExercisePromptReminderInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExercisePromptReminderOutput>("exercise.prompt_reminder", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseRecordNotRequiredInput = {
  requisition_id: string;
  reason: string;
};

export type ExerciseRecordNotRequiredOutput = {
  application_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Record at the finalized intake that no exercise is required for this requisition, with the reason; the Hiring Manager or the Recruiter decides it. */
export const exerciseRecordNotRequired = (input: ExerciseRecordNotRequiredInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseRecordNotRequiredOutput>("exercise.record_not_required", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseRecordReturnInput = {
  requisition_id: string;
  application_id: string;
  returned_at: string;
  return_file_ids: string[];
};

export type ExerciseRecordReturnOutput = {
  application_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** The work came back: returned-on and attachments; each assigned reviewer is asked to review it and the review timer starts (D-33). */
export const exerciseRecordReturn = (input: ExerciseRecordReturnInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseRecordReturnOutput>("exercise.record_return", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseRecordSentInput = {
  requisition_id: string;
  application_id: string;
  sent_at: string;
  due_at?: string | null;
};

export type ExerciseRecordSentOutput = {
  application_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** The brief was sent from the ATS: sent-on and due (default from the intake's window); the due timer starts (D-32) with its day-before reminder. */
export const exerciseRecordSent = (input: ExerciseRecordSentInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseRecordSentOutput>("exercise.record_sent", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseRemindEvaluationInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type ExerciseRemindEvaluationOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** D-33: the returned work is still awaiting reviews after three business days — every assigned reviewer whose review is not in is reminded. */
export const exerciseRemindEvaluation = (input: ExerciseRemindEvaluationInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseRemindEvaluationOutput>("exercise.remind_evaluation", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type FindingIn = {
  criterion: string;
  finding: "met" | "partial" | "not";
  note?: string | null;
};

export type ExerciseReviewInput = {
  requisition_id: string;
  application_id: string;
  criteria_findings: FindingIn[];
  evaluation_text: string;
  advisory_rating?: number | null;
};

export type ExerciseReviewOutput = {
  application_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** An assigned reviewer's own review of the returned work: a finding per criterion (met, partial or not, with a note), the evaluation and an optional advisory rating that never enters any average or the two-2s count. The assessment reads Reviewed once every assigned reviewer's review is in. */
export const exerciseReview = (input: ExerciseReviewInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseReviewOutput>("exercise.review", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseSkipInput = {
  requisition_id: string;
  application_id: string;
  reason: string;
};

export type ExerciseSkipOutput = {
  application_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Skip the exercise for one candidate when the intake said none was needed (the reason prints on the sheet). */
export const exerciseSkip = (input: ExerciseSkipInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseSkipOutput>("exercise.skip", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ExerciseWithdrawInput = {
  requisition_id: string;
  application_id: string;
  reason: string;
};

export type ExerciseWithdrawOutput = {
  application_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Withdraw a required assessment for one candidate, at any step before Completed, with a reason; the due and review timers stop and the open review items close. A required assessment is never skipped silently. */
export const exerciseWithdraw = (input: ExerciseWithdrawInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ExerciseWithdrawOutput>("exercise.withdraw", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type FeedbackExportInput = {
  status?: "new" | "triaged" | "in_progress" | "fixed" | "wont_fix" | "duplicate_of" | null;
  route_prefix?: string | null;
  role?: string | null;
  since?: string | null;
  until?: string | null;
  query?: string | null;
  limit?: number;
  cursor?: string | null;
  format?: "json" | "csv";
};

export type FeedbackRow = {
  public_ref: string;
  id: string;
  reporter_id: string;
  role: string;
  route: string;
  note: string;
  context: Record<string, unknown>;
  status: string;
  owner_id: string | null;
  resolution: string | null;
  created_at: string;
  updated_at: string;
  duplicate_of_id?: string | null;
  triaged_at?: string | null;
  app_version?: string | null;
  seed_version?: string | null;
  view_as_target_id?: string | null;
};

export type FeedbackExportOutput = {
  version_id: string;
  receipt_id: string;
  classification: string;
  items: FeedbackRow[];
  csv?: string | null;
  rows: number;
};

/** Export the complete filtered bug-report set as JSON or formula-neutralised CSV, with a receipt. */
export const feedbackExport = (input: FeedbackExportInput = {} as FeedbackExportInput) => callTool<FeedbackExportOutput>("feedback.export", input);

export type FeedbackListInput = {
  status?: "new" | "triaged" | "in_progress" | "fixed" | "wont_fix" | "duplicate_of" | null;
  route_prefix?: string | null;
  role?: string | null;
  since?: string | null;
  until?: string | null;
  query?: string | null;
  limit?: number;
  cursor?: string | null;
};

export type FeedbackListOutput = {
  items: FeedbackRow[];
  next_cursor?: string | null;
};

/** Read a filtered page of bug reports, with a stable cursor. */
export const feedbackList = (input: FeedbackListInput = {} as FeedbackListInput) => callTool<FeedbackListOutput>("feedback.list", input);

export type FeedbackOpenInput = {
  id: string;
};

export type FeedbackOpenOutput = {
  public_ref: string;
  id: string;
  reporter_id: string;
  role: string;
  route: string;
  note: string;
  context: Record<string, unknown>;
  status: string;
  owner_id: string | null;
  resolution: string | null;
  created_at: string;
  updated_at: string;
  duplicate_of_id?: string | null;
  triaged_at?: string | null;
  app_version?: string | null;
  seed_version?: string | null;
  view_as_target_id?: string | null;
  reporter_contact?: Record<string, string> | null;
};

/** Open a bug report and its reporter's contact with safe context. */
export const feedbackOpen = (input: FeedbackOpenInput = {} as FeedbackOpenInput) => callTool<FeedbackOpenOutput>("feedback.open", input);

export type FeedbackReportInput = {
  route: string;
  note: string;
  context?: Record<string, unknown>;
};

export type FeedbackReportOutput = {
  public_ref: string;
  id: string;
  status: string;
  created_at: string;
  receipt_id?: number | null;
};

/** Report a bug with allow-listed diagnostics; a lens report belongs to its Admin operator. */
export const feedbackReport = (input: FeedbackReportInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<FeedbackReportOutput>("feedback.report", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type FeedbackReportAnonymousInput = {
  note: string;
};

export type FeedbackReportAnonymousOutput = {
  ok?: boolean;
  receipt_id?: number | null;
};

/** Accept the rate-limited sign-in Bug report through the application's anonymous door. */
export const feedbackReportAnonymous = (input: FeedbackReportAnonymousInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<FeedbackReportAnonymousOutput>("feedback.report_anonymous", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type FeedbackTriageInput = {
  id: string;
  status: "new" | "triaged" | "in_progress" | "fixed" | "wont_fix" | "duplicate_of";
  resolution?: string | null;
  owner_id?: string | null;
  duplicate_of_id?: string | null;
};

export type FeedbackTriageOutput = {
  public_ref: string;
  id: string;
  reporter_id: string;
  role: string;
  route: string;
  note: string;
  context: Record<string, unknown>;
  status: string;
  owner_id: string | null;
  resolution: string | null;
  created_at: string;
  updated_at: string;
  duplicate_of_id?: string | null;
  triaged_at?: string | null;
  app_version?: string | null;
  seed_version?: string | null;
  view_as_target_id?: string | null;
  receipt_id?: number | null;
};

/** Set a bug's status, active Admin owner and resolution; duplicate links cannot form a cycle. */
export const feedbackTriage = (input: FeedbackTriageInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<FeedbackTriageOutput>("feedback.triage", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type FileDownloadInput = {
  file_id: string;
};

export type FileDownloadOutput = {
  file_id: string;
  kind: string;
  owner_type: string;
  owner_id: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  version: number;
  status: string;
  download_count: number;
  original_name: string;
  url: string;
};

/** Serve a stored file to a seat whose projection includes it. An audited read: every download is a disclosure and is receipted and counted. */
export const fileDownload = (input: FileDownloadInput = {} as FileDownloadInput) => callTool<FileDownloadOutput>("file.download", input);

export type FileMarkAbandonedInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type FileMarkAbandonedOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** H-60: an upload no record referenced within 24 hours becomes abandoned. The bytes are retained — nothing is ever deleted (Law 7). */
export const fileMarkAbandoned = (input: FileMarkAbandonedInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<FileMarkAbandonedOutput>("file.mark_abandoned", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type FileUploadInput = {
  pending_id: string;
  owner_type: "request" | "candidate" | "scorecard" | "assessment" | "packet_version" | "summary_sheet" | "intake" | "requisition" | "feedback" | "import" | "offer";
  owner_id: string;
  kind: "resume" | "paper_scorecard" | "exercise_return" | "packet_pdf" | "summary_export" | "intake_pdf" | "written_input" | "import_csv" | "attachment" | "comp_evidence";
  requisition_id?: string | null;
  reject?: "type_disallowed" | null;
};

export type FileUploadOutput = {
  file_id: string;
  status: string;
  kind: string;
  owner_type: string;
  owner_id: string;
  sha256: string;
  size_bytes: number;
  content_type: string;
  version: number;
  duplicate_of?: string | null;
  reject_reason?: string | null;
  scan_result?: string | null;
  receipt_id?: number | null;
};

/** Record bytes the upload door has parsed: quarantine, scan, version. A file becomes servable only when the owning write references it. */
export const fileUpload = (input: FileUploadInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<FileUploadOutput>("file.upload", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type GuideFaqInput = Record<string, unknown>;

export type FaqEntry = {
  question: string;
  answer: string;
};

export type FaqGroup = {
  title: string;
  entries?: FaqEntry[];
};

export type GuideFaqOutput = {
  version: number;
  title: string;
  subtitle?: string | null;
  footer?: string | null;
  groups?: FaqGroup[];
};

/** The published answers to the questions the panel asks most, grouped by topic. */
export const guideFaq = (input: GuideFaqInput = {} as GuideFaqInput) => callTool<GuideFaqOutput>("guide.faq", input);

export type GuideOpenInput = Record<string, unknown>;

export type GuideBlock = {
  kind: "prose" | "tiles" | "table" | "callout";
  heading?: string | null;
  note?: string | null;
  paragraphs?: string[];
  items?: GuideTile[];
  columns?: string[];
  rows?: string[][];
};

export type GuideSection = {
  number: string;
  title: string;
  summary?: string | null;
  blocks?: GuideBlock[];
};

export type GuideTile = {
  number?: string | null;
  tag?: string | null;
  title: string;
  text?: string | null;
};

export type Highlight = {
  number: string;
  label: string;
};

export type GuideOpenOutput = {
  version: number;
  title: string;
  subtitle?: string | null;
  tagline?: string | null;
  brands?: string | null;
  prepared_by?: string | null;
  audience?: string | null;
  classification?: string | null;
  footer?: string | null;
  highlights?: Highlight[];
  sections?: GuideSection[];
};

/** The Talent Acquisition Process Guide as published: phases, steps, RACI, SLAs and the debrief rules. */
export const guideOpen = (input: GuideOpenInput = {} as GuideOpenInput) => callTool<GuideOpenOutput>("guide.open", input);

export type GuideReferenceInput = Record<string, unknown>;

export type LevelReference = {
  level: string;
  level_label: string;
  guide_version: number;
  title: string;
  min_skills?: number | null;
  onsite_cap?: number | null;
  touchpoint_cap?: number | null;
  score_legend?: string | null;
  score_descriptions?: Record<string, string>;
  evidence_prompt?: string | null;
  questions_note?: string | null;
  values?: ValueReference[];
  routes?: RecommendationRoute[];
  bands?: string[];
  bands_note?: string | null;
  decision_rule_text?: string | null;
  alignment_text?: string | null;
  coc_question_text?: string | null;
};

export type RecommendationRoute = {
  key: string;
  label: string;
  description?: string | null;
};

export type ValueReference = {
  key: string;
  name: string;
  assesses: string;
  questions?: string[];
  anchors?: Record<string, string>;
};

export type GuideReferenceOutput = {
  levels?: LevelReference[];
};

/** The standing assessment guidance for every level: the ROOTS values, the rating ladder, the approved question sets and the recommendation routes. */
export const guideReference = (input: GuideReferenceInput = {} as GuideReferenceInput) => callTool<GuideReferenceOutput>("guide.reference", input);

export type HandoffRecordItemInput = {
  application_id: string;
  item_key: string;
  state: "done" | "not_required" | "pending";
  value_date?: string | null;
  value_text?: string | null;
  evidence_file_id?: string | null;
};

export type HandoffRecordItemOutput = {
  item_id: string;
  item_key: string;
  state: string;
  missing: string[];
  milestone_id?: string | null;
  receipt_id?: number | null;
};

/** Record one item of an accepted hire's handoff checklist: done, not required (with a reason) or pending. */
export const handoffRecordItem = (input: HandoffRecordItemInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<HandoffRecordItemOutput>("handoff.record_item", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type HandoffValidateInput = {
  application_id: string;
};

export type HandoffValidateOutput = {
  milestone_id: string;
  ready_for_hris_at: string;
  receipt_id?: number | null;
};

/** Validate an accepted hire's handoff: every required item is done, so the hire is Ready for HRIS. */
export const handoffValidate = (input: HandoffValidateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<HandoffValidateOutput>("handoff.validate", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type HealthEvaluateDailyInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type HealthEvaluateDailyOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** H-61 daily health pass: a health and a readiness snapshot for every live requisition whose inputs changed. */
export const healthEvaluateDaily = (input: HealthEvaluateDailyInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<HealthEvaluateDailyOutput>("health.evaluate_daily", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type HealthOpenInput = {
  requisition_id: string;
};

export type DriverOut = {
  key: string;
  ordinal: number;
  weight: number;
  value: Record<string, unknown>;
  label_key: string;
  route: string | null;
  items: number;
};

export type HealthOpenOutput = {
  requisition_id: string;
  computed_at: string;
  score: number;
  light: string;
  phase_state: string | null;
  drivers: DriverOut[];
  config_versions: Record<string, string | null>;
  stored: boolean;
};

/** Req health: numeric score, Green/Yellow/Red light and every driver, as last recorded for the requisition. */
export const healthOpen = (input: HealthOpenInput = {} as HealthOpenInput) => callTool<HealthOpenOutput>("health.open", input);

export type HomeOpenInput = Record<string, unknown>;

export type WidgetOut = {
  key: string;
  slot: string;
  title: string;
  data: Record<string, unknown>;
};

export type HomeOpenOutput = {
  first_name: string;
  role_words: string[];
  scope_words: string[];
  actions_open: number;
  widgets: WidgetOut[];
};

/** Home: the security-trimmed widgets for the union of the person's roles, in fixed order, with the breach alert. */
export const homeOpen = (input: HomeOpenInput = {} as HomeOpenInput) => callTool<HomeOpenOutput>("home.open", input);

export type HrisConfirmInput = {
  application_id: string;
  employee_id?: string | null;
  hire_date: string;
  start_date: string;
  evidence_ref?: string | null;
  evidence_file_id?: string | null;
  hris_evidence_id?: string | null;
};

export type HrisConfirmOutput = {
  confirmation_id: string;
  filled: boolean;
  archived: boolean;
  receipt_id?: number | null;
};

/** The Coordinator confirms the HRIS hire for an opening at Ready for HRIS: HRIS Hired at the hire date, Started at the start date, and Filled when every opening is HRIS Hired. The Admin may complete any one. */
export const hrisConfirm = (input: HrisConfirmInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<HrisConfirmOutput>("hris.confirm", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type HrisCorrectInput = {
  application_id: string;
  confirmation_id: string;
  reason: string;
  employee_id?: string | null;
  hire_date?: string | null;
  start_date?: string | null;
  evidence_ref?: string | null;
  evidence_file_id?: string | null;
};

export type HrisCorrectOutput = {
  confirmation_id: string;
  filled: boolean;
  archived: boolean;
  receipt_id?: number | null;
};

/** Correct the live HRIS confirmation with a reason, as a superseding record; Started, Filled and the archive snapshot follow the corrected dates, also after archive. */
export const hrisCorrect = (input: HrisCorrectInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<HrisCorrectOutput>("hris.correct", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeAcknowledgeInput = {
  requisition_id: string;
  acknowledged?: boolean;
};

export type IntakeAcknowledgeOutput = {
  requisition_id: string;
  status: string;
  panel_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** The Hiring Manager acknowledgment (replaces initials): name and time recorded. */
export const intakeAcknowledge = (input: IntakeAcknowledgeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeAcknowledgeOutput>("intake.acknowledge", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeCommentPart1Input = {
  requisition_id: string;
  section: string;
  comment: string;
};

export type IntakeCommentPart1Output = {
  requisition_id: string;
  status: string;
  panel_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** The HR Business Partner comments on a Part 1 section. */
export const intakeCommentPart1 = (input: IntakeCommentPart1Input, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeCommentPart1Output>("intake.comment_part1", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeCommentPlanInput = {
  requisition_id: string;
  text: string;
};

export type IntakeCommentPlanOutput = {
  requisition_id: string;
  comment_id: string;
  receipt_id?: number | null;
};

/** The HR Business Partner comments on the interview plan; the Hiring Manager and the Recruiter are told. */
export const intakeCommentPlan = (input: IntakeCommentPlanInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeCommentPlanOutput>("intake.comment_plan", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeFinalizeInput = {
  requisition_id: string;
  expected_plan_revision?: number | null;
};

export type IntakeFinalizeOutput = {
  requisition_id: string;
  status: string;
  panel_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Finalize the interview plan: every blocking gate must pass; the Hiring Manager or the Recruiter finalizes and the seat they finalized from is recorded; locks the panel, pins the guide version, clears the Recruiter to source. */
export const intakeFinalize = (input: IntakeFinalizeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeFinalizeOutput>("intake.finalize", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeMarkIncompleteAtKickoffInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type IntakeMarkIncompleteAtKickoffOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** C-16: Part 1 still incomplete at the kickoff date — the call goes ahead on the unfinished sections. */
export const intakeMarkIncompleteAtKickoff = (input: IntakeMarkIncompleteAtKickoffInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeMarkIncompleteAtKickoffOutput>("intake.mark_incomplete_at_kickoff", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeMarkReadyInput = {
  requisition_id: string;
};

export type IntakeMarkReadyOutput = {
  requisition_id: string;
  status: string;
  panel_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Ready for the intake call: every required Part 1 section and the acknowledgment; Recruiter and HR Business Partner are told. */
export const intakeMarkReady = (input: IntakeMarkReadyInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeMarkReadyOutput>("intake.mark_ready", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeNotifyNotHeldInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type IntakeNotifyNotHeldOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** C-20: the kickoff date passed without the call recorded as held. */
export const intakeNotifyNotHeld = (input: IntakeNotifyNotHeldInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeNotifyNotHeldOutput>("intake.notify_not_held", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeNotifyStalledInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type IntakeNotifyStalledOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** C-21 (2 bd): the HR Business Partner is told the intake is stalled. */
export const intakeNotifyStalled = (input: IntakeNotifyStalledInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeNotifyStalledOutput>("intake.notify_stalled", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeOpenInput = {
  requisition_id: string;
};

export type Gate = {
  key: string;
  passed: boolean;
  text: string;
  blocking?: boolean;
};

export type HmWindowOut = {
  date: string;
  start: string;
  duration_minutes: number;
  start_at: string;
  end_at: string;
};

export type Part1Text = {
  availability_line?: string | null;
  logistics_line?: string | null;
};

export type RoundOut = {
  id: string;
  key: string;
  kind: string;
  sequence: number;
  label: string;
  in_use: boolean;
  format: string;
  duration_minutes: number;
  values: string[];
  skill_numbers: number[];
  primary: Record<string, unknown> | null;
  backup: Record<string, unknown> | null;
  locked: boolean;
  executive_participation?: boolean;
  recommendations_text?: string | null;
};

export type IntakeOpenOutput = {
  delegation_directory?: Record<string, unknown>[];
  approval_label?: string | null;
  finalized_label?: string | null;
  requisition_id: string;
  status: string;
  status_label: string;
  panel_version: number;
  level: string;
  part0: Record<string, unknown>;
  part0_missing: string[];
  part0_locked_fields: string[];
  approved_by: string | null;
  approved_on: string | null;
  part1: Record<string, unknown>;
  part1_text?: Part1Text;
  hm_windows?: HmWindowOut[];
  part1_sections_complete: string[];
  part1_sections: string[];
  part1_sent_at: string | null;
  part1_due_at: string | null;
  part1_ready_at: string | null;
  hm_acknowledged_at: string | null;
  meeting: Record<string, unknown> | null;
  skills: Record<string, unknown>[];
  rounds: RoundOut[];
  exercise: Record<string, unknown> | null;
  coverage: Record<string, unknown>;
  gates: Gate[];
  can_finalize: boolean;
  finalized_at: string | null;
  guide_version: number;
  limits: Record<string, number>;
  can: Record<string, boolean>;
};

/** Intake & Panel: Parts 0/1/2, the meeting, skills, rounds, seats, coverage and the finalize gates (the Coordinator and the TA Manager read it without the Part 1 and Part 2 editors). */
export const intakeOpen = (input: IntakeOpenInput = {} as IntakeOpenInput) => callTool<IntakeOpenOutput>("intake.open", input);

export type IntakePlanOpenInput = {
  requisition_id: string;
};

export type GapOut = {
  gate: string;
  code: string;
  ref: string | null;
  text: string;
  blocking: boolean;
};

export type PlanAssessmentOut = {
  decided: boolean;
  required: boolean | null;
  assessment_type: string | null;
  stage_round_key: string | null;
  due_window_days: number | null;
  owner: PlanPerson | null;
  reviewers: PlanPerson[];
  criteria: string[];
  brief_text: string | null;
  reason_when_no: string | null;
  recommendations_text: string | null;
  brief_version: number | null;
  suggested_default: "optional" | "recommended" | "required" | null;
};

export type PlanAttendees = {
  required: PlanPerson[];
  optional: PlanPerson[];
  no_template_reason?: string | null;
};

export type PlanCan = {
  edit: boolean;
  finalize: boolean;
  reopen: boolean;
  validate: boolean;
  comment: boolean;
  set_variant: boolean;
  edit_strategy: boolean;
  override: boolean;
};

export type PlanComment = {
  id: string;
  person_id: string;
  name: string;
  at: string;
  text: string;
};

export type PlanFinalizedBy = {
  person_id: string;
  name: string;
  seat: string | null;
  at: string;
};

export type PlanFramework = {
  key: string;
  version: number;
  config_version_id: string;
};

export type PlanPerson = {
  person_id: string;
  name: string;
  initials: string;
};

export type PlanRoundOut = {
  id: string;
  key: string;
  kind: string;
  sequence: number;
  in_use: boolean;
  format: string;
  duration_minutes: number | null;
  executive_participation: boolean;
  values: string[];
  skill_numbers: number[];
  primary: PlanPerson | null;
  backup: PlanPerson | null;
  locked: boolean;
  recommendations_text: string | null;
  source: Record<string, "framework_default" | "edited" | null>;
};

export type PlanSkillOut = {
  number: number;
  title: string;
  what_strong_looks_like: string;
};

export type PostingStrategyCard = {
  original: Record<string, unknown> | null;
  current: Record<string, unknown> | null;
  history: Record<string, unknown>[];
  reason: string | null;
};

export type IntakePlanOpenOutput = {
  requisition_id: string;
  level: string;
  projection: "full" | "consult" | "scheduling";
  plan_status: string | null;
  plan_status_label: string | null;
  panel_version: number;
  plan_revision: number;
  framework: PlanFramework | null;
  variant: string | null;
  variant_reason: string | null;
  internal_hint: boolean;
  rounds: PlanRoundOut[];
  skills: PlanSkillOut[];
  assessment: PlanAssessmentOut | null;
  gates: Gate[];
  missing: GapOut[];
  coverage_gaps: GapOut[];
  values_matrix: Record<string, string[]>;
  skills_matrix: Record<string, string[]>;
  attendees: PlanAttendees;
  finalized_by: PlanFinalizedBy | null;
  comments: PlanComment[];
  posting_strategy: PostingStrategyCard | null;
  can: PlanCan;
};

/** The interview plan as your seat reads it: the full plan for the Hiring Manager and the Recruiter, the plan to consult for the HR Business Partner, the scheduling dependencies for the Coordinator. */
export const intakePlanOpen = (input: IntakePlanOpenInput = {} as IntakePlanOpenInput) => callTool<IntakePlanOpenOutput>("intake.plan_open", input);

export type IntakePrintInput = {
  requisition_id: string;
};

export type IntakePrintOutput = {
  requisition_id: string;
  rendition: string;
  rendition_html?: string | null;
  classification: string;
  file_id: string;
  version_id: string;
  receipt_id: string;
  printed_at: string;
};

/** The intake form as nine printable pages, saved as a version with its export receipt; it renders the record as stored (a draft prints DRAFT), never the screen. */
export const intakePrint = (input: IntakePrintInput = {} as IntakePrintInput) => callTool<IntakePrintOutput>("intake.print", input);

export type IntakePromptRescheduleInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type IntakePromptRescheduleOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** C-19: an invitee has not accepted within a business day — the Recruiter proposes alternates or books the stand-in. */
export const intakePromptReschedule = (input: IntakePromptRescheduleInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakePromptRescheduleOutput>("intake.prompt_reschedule", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeRecordHeldInput = {
  requisition_id: string;
  held_at?: string | null;
  attendance?: Record<string, unknown>[];
  absence_exception_reason?: string | null;
  held_offline?: boolean;
  offline_reason?: string | null;
};

export type IntakeRecordHeldOutput = {
  requisition_id: string;
  status: string;
  panel_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Record the intake call as held with attendance; an absent Hiring Manager or Recruiter needs an explicit exception, the HR Business Partner is optional. Opens Part 2. Held offline (no booking), with a reason: the Recruiter, the Hiring Manager, the Coordinator or the TA Manager. */
export const intakeRecordHeld = (input: IntakeRecordHeldInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeRecordHeldOutput>("intake.record_held", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeRemindFinalizeInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type IntakeRemindFinalizeOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** C-21 (1 bd): the Hiring Manager and the Recruiter are each reminded once with the outstanding gate items. */
export const intakeRemindFinalize = (input: IntakeRemindFinalizeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeRemindFinalizeOutput>("intake.remind_finalize", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeRemindPart1Input = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type IntakeRemindPart1Output = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** C-17: pre-call reminder listing the open sections. */
export const intakeRemindPart1 = (input: IntakeRemindPart1Input, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeRemindPart1Output>("intake.remind_part1", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeReopenInput = {
  requisition_id: string;
  reason: string;
};

export type IntakeReopenOutput = {
  requisition_id: string;
  status: string;
  panel_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Reopen the panel with a reason; coverage revalidates; re-finalizing increments the version. */
export const intakeReopen = (input: IntakeReopenInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeReopenOutput>("intake.reopen", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeSaveCompNotesInput = {
  requisition_id: string;
  comp_approval_notes: string;
};

export type IntakeSaveCompNotesOutput = {
  requisition_id: string;
  status: string;
  panel_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** The HR Business Partner's comp approval notes on Part 0. */
export const intakeSaveCompNotes = (input: IntakeSaveCompNotesInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeSaveCompNotesOutput>("intake.save_comp_notes", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeSavePart0Input = {
  requisition_id: string;
  job_description_file_id?: string | null;
  kickoff_date?: string | null;
  team_size_structure?: string | null;
  sourcing_notes?: string | null;
  job_description?: string | null;
  bonus_structure?: string | null;
  comp_approval_notes?: string | null;
  reason?: string | null;
};

export type IntakeSavePart0Output = {
  delegation_directory?: Record<string, unknown>[];
  approval_label?: string | null;
  finalized_label?: string | null;
  requisition_id: string;
  status: string;
  status_label: string;
  panel_version: number;
  level: string;
  part0: Record<string, unknown>;
  part0_missing: string[];
  part0_locked_fields: string[];
  approved_by: string | null;
  approved_on: string | null;
  part1: Record<string, unknown>;
  part1_text?: Part1Text;
  hm_windows?: HmWindowOut[];
  part1_sections_complete: string[];
  part1_sections: string[];
  part1_sent_at: string | null;
  part1_due_at: string | null;
  part1_ready_at: string | null;
  hm_acknowledged_at: string | null;
  meeting: Record<string, unknown> | null;
  skills: Record<string, unknown>[];
  rounds: RoundOut[];
  exercise: Record<string, unknown> | null;
  coverage: Record<string, unknown>;
  gates: Gate[];
  can_finalize: boolean;
  finalized_at: string | null;
  guide_version: number;
  limits: Record<string, number>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Part 0 (Recruiter pre-fill): kickoff date, team size and structure, sourcing notes, bonus; a change after posting needs a reason. */
export const intakeSavePart0 = (input: IntakeSavePart0Input, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeSavePart0Output>("intake.save_part0", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeSavePart1Input = {
  requisition_id: string;
  section: "preferences_logistics" | "skills_experience" | "assessment_profile" | "role_narrative" | "job_description";
  values: Record<string, unknown>;
};

export type IntakeSavePart1Output = {
  delegation_directory?: Record<string, unknown>[];
  approval_label?: string | null;
  finalized_label?: string | null;
  requisition_id: string;
  status: string;
  status_label: string;
  panel_version: number;
  level: string;
  part0: Record<string, unknown>;
  part0_missing: string[];
  part0_locked_fields: string[];
  approved_by: string | null;
  approved_on: string | null;
  part1: Record<string, unknown>;
  part1_text?: Part1Text;
  hm_windows?: HmWindowOut[];
  part1_sections_complete: string[];
  part1_sections: string[];
  part1_sent_at: string | null;
  part1_due_at: string | null;
  part1_ready_at: string | null;
  hm_acknowledged_at: string | null;
  meeting: Record<string, unknown> | null;
  skills: Record<string, unknown>[];
  rounds: RoundOut[];
  exercise: Record<string, unknown> | null;
  coverage: Record<string, unknown>;
  gates: Gate[];
  can_finalize: boolean;
  finalized_at: string | null;
  guide_version: number;
  limits: Record<string, number>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Save one Part 1 section (autosaved draft); a section is complete when its required fields are present. */
export const intakeSavePart1 = (input: IntakeSavePart1Input, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeSavePart1Output>("intake.save_part1", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SkillIn = {
  number: number;
  title: string;
  what_strong_looks_like: string;
};

export type IntakeSavePart2Input = {
  requisition_id: string;
  skills: SkillIn[];
  expected_plan_revision?: number | null;
};

export type IntakeSavePart2Output = {
  delegation_directory?: Record<string, unknown>[];
  approval_label?: string | null;
  finalized_label?: string | null;
  requisition_id: string;
  status: string;
  status_label: string;
  panel_version: number;
  level: string;
  part0: Record<string, unknown>;
  part0_missing: string[];
  part0_locked_fields: string[];
  approved_by: string | null;
  approved_on: string | null;
  part1: Record<string, unknown>;
  part1_text?: Part1Text;
  hm_windows?: HmWindowOut[];
  part1_sections_complete: string[];
  part1_sections: string[];
  part1_sent_at: string | null;
  part1_due_at: string | null;
  part1_ready_at: string | null;
  hm_acknowledged_at: string | null;
  meeting: Record<string, unknown> | null;
  skills: Record<string, unknown>[];
  rounds: RoundOut[];
  exercise: Record<string, unknown> | null;
  coverage: Record<string, unknown>;
  gates: Gate[];
  can_finalize: boolean;
  finalized_at: string | null;
  guide_version: number;
  limits: Record<string, number>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Interview plan skills: up to four role-specific skills with what strong performance looks like, written by the Hiring Manager and the Recruiter together. */
export const intakeSavePart2 = (input: IntakeSavePart2Input, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeSavePart2Output>("intake.save_part2", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeScheduleSessionInput = {
  requisition_id: string;
  start?: string | null;
  duration_minutes?: number | null;
  format?: "teams" | "in_person";
  location_or_link?: string | null;
};

export type ProposalOut = {
  start: string;
  end: string;
  source: "hm_window" | "free_busy";
};

export type IntakeScheduleSessionOutput = {
  booked: boolean;
  meeting_id: string | null;
  proposals: ProposalOut[];
  chosen_start: string | null;
  chosen_end: string | null;
  receipt_id?: number | null;
};

/** Book the intake call: the Hiring Manager's Part 1 windows first (at their window length), then the calendar's free/busy for Hiring Manager, HR Business Partner and Recruiter (proposals, then one booking). */
export const intakeScheduleSession = (input: IntakeScheduleSessionInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeScheduleSessionOutput>("intake.schedule_session", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeSendPart1Input = {
  requisition_id: string;
};

export type IntakeSendPart1Output = {
  requisition_id: string;
  status: string;
  panel_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Send Part 1 to the Hiring Manager (simulated mail); due at close of business on the kickoff date. */
export const intakeSendPart1 = (input: IntakeSendPart1Input, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeSendPart1Output>("intake.send_part1", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeSetPlanVariantInput = {
  requisition_id: string;
  variant: "standard" | "internal_flexible";
  reason: string;
  expected_plan_revision?: number | null;
};

export type IntakeSetPlanVariantOutput = {
  requisition_id: string;
  variant: string;
  plan_revision: number;
  receipt_id?: number | null;
};

/** Set the interview plan's variant with a reason: standard, or flexible for an internal candidate (extra onsite rounds may be switched off). Relaxes no gate: all five ROOTS values and the level's skills stay required. The Hiring Manager and the Recruiter. */
export const intakeSetPlanVariant = (input: IntakeSetPlanVariantInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeSetPlanVariantOutput>("intake.set_plan_variant", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntakeValidatePlanInput = {
  requisition_id: string;
  expected_plan_revision?: number | null;
};

export type IntakeValidatePlanGapOut = {
  gate: string;
  code: string;
  ref?: string | null;
  text: string;
  blocking?: boolean;
};

export type IntakeValidatePlanOutput = {
  requisition_id: string;
  plan_status: string | null;
  outcome: "ok" | "failed";
  gaps: IntakeValidatePlanGapOut[];
  plan_revision: number;
  receipt_id?: number | null;
};

/** Validate the interview plan against every Finalize gate: records the outcome (Ready, or Validation Failed with each gap named) and raises or closes the missing-field and coverage-gap alerts; changes no part of the plan. The Hiring Manager and the Recruiter. */
export const intakeValidatePlan = (input: IntakeValidatePlanInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntakeValidatePlanOutput>("intake.validate_plan", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntegrationPollBackgroundChecksInput = {
  limit?: number;
};

export type IntegrationPollBackgroundChecksOutput = {
  checked: number;
  recorded: number;
  failed: number;
  receipt_id?: number | null;
};

/** Ask the background check where each ordered check of an accepted hire stands and record every changed status through the handoff, status only. */
export const integrationPollBackgroundChecks = (input: IntegrationPollBackgroundChecksInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntegrationPollBackgroundChecksOutput>("integration.poll_background_checks", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntegrationPollHrisInput = {
  limit?: number;
};

export type IntegrationPollHrisOutput = {
  checked: number;
  evidence: number;
  failed: number;
  receipt_id?: number | null;
};

/** Look in the HRIS for the hire of every application Ready for HRIS and hand each one found to the Coordinator as evidence to confirm; never a confirmation, never Filled. */
export const integrationPollHris = (input: IntegrationPollHrisInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntegrationPollHrisOutput>("integration.poll_hris", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntegrationPollResponsesInput = {
  limit?: number;
};

export type IntegrationPollResponsesOutput = {
  polled?: number;
  updated?: number;
  failed?: number;
  no_op?: boolean;
  reason?: string | null;
  receipt_id?: number | null;
};

/** Ask the live calendar for attendees' answers to live, booked, future meetings someone required has not answered yet (at most 50 a pass, once per polling interval). */
export const integrationPollResponses = (input: IntegrationPollResponsesInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntegrationPollResponsesOutput>("integration.poll_responses", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type IntegrationSyncLapseInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type IntegrationSyncLapseOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** An integration call the worker delivered was accepted or failed: its outcome is recorded on the call, and a failure opens the recovery task of the call's owner. */
export const integrationSyncLapse = (input: IntegrationSyncLapseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<IntegrationSyncLapseOutput>("integration.sync_lapse", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type MeasuresEvaluateDailyInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type MeasuresEvaluateDailyOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** H-58 daily publication pass: promote approved configuration at the business instant without moving armed clocks. */
export const measuresEvaluateDaily = (input: MeasuresEvaluateDailyInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<MeasuresEvaluateDailyOutput>("measures.evaluate_daily", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type OfferChooseNextPathInput = {
  requisition_id: string;
  application_id: string;
  alternate_application_id?: string | null;
  final_notes?: string | null;
  reporting_category?: string | null;
  next_path: "alternate" | "continue" | "reopen" | "close";
  rationale: string;
};

export type OfferChooseNextPathOutput = {
  application_id: string;
  offer_id: string;
  status: string;
  requisition_status: string;
  branch?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** After the offer was withdrawn or an acceptance reversed: the Recruiter's next path — extend to an alternate, continue the search, reopen it (the HR Business Partner's reopen request when it is closed) or close it (the HR Business Partner's cancellation request). Once per ended offer. */
export const offerChooseNextPath = (input: OfferChooseNextPathInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<OfferChooseNextPathOutput>("offer.choose_next_path", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type OfferExtendToAlternateInput = {
  requisition_id: string;
  application_id: string;
  rationale: string;
  told_check: boolean;
};

export type OfferExtendToAlternateOutput = {
  application_id: string;
  offer_id: string;
  status: string;
  requisition_status: string;
  branch?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** After a decline, a withdrawal or a renege: the Recruiter extends the offer to the candidate the room held as the alternate. A superseding outcome — the earlier hold is kept, the alternate's eligibility and the capacity are re-checked, and the offer track opens for them. */
export const offerExtendToAlternate = (input: OfferExtendToAlternateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<OfferExtendToAlternateOutput>("offer.extend_to_alternate", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type OfferOpenInput = {
  requisition_id: string;
  application_id: string;
};

export type CapacityOut = {
  openings: number;
  fills: number;
  commitments: number;
  remaining: number;
  status: string;
};

export type OfferOpenOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  offer_id: string | null;
  status: string | null;
  stage: string;
  package: Record<string, unknown> | null;
  letter_mismatch: boolean;
  events: Record<string, unknown>[];
  capacity: CapacityOut;
  branch: string | null;
  gates: Record<string, unknown>;
  fields: Record<string, unknown>;
  can: Record<string, boolean>;
  chain?: Record<string, unknown> | null;
  negotiation?: Record<string, unknown>[];
  candidate_status?: Record<string, unknown> | null;
  decision?: Record<string, unknown> | null;
  alternates?: Record<string, unknown>[];
  decline_reasons?: Record<string, unknown>[] | null;
  withdrawal_reasons?: Record<string, unknown>[] | null;
  reporting_categories?: Record<string, unknown>[] | null;
};

/** The offer for one candidate: status, the bound package, the chain from Approved to Accepted or Declined, the counter rounds, every event, capacity and the gates (Recruiter, Hiring Manager, HR Business Partner, TA Manager); the Coordinator reads the chain, the dates and the candidate-facing status only. */
export const offerOpen = (input: OfferOpenInput = {} as OfferOpenInput) => callTool<OfferOpenOutput>("offer.open", input);

export type OfferPublishManagerHandoffInput = {
  requisition_id: string;
  application_id: string;
};

export type OfferPublishManagerHandoffOutput = {
  application_id: string;
  candidate: string;
  remarks: Record<string, unknown>[];
};

/** After the acceptance: every interviewer's manager-handoff remarks, verbatim, under their name. */
export const offerPublishManagerHandoff = (input: OfferPublishManagerHandoffInput = {} as OfferPublishManagerHandoffInput) => callTool<OfferPublishManagerHandoffOutput>("offer.publish_manager_handoff", input);

export type OfferRecordAcceptanceInput = {
  requisition_id: string;
  application_id: string;
  accepted_at?: string | null;
};

export type OfferRecordAcceptanceOutput = {
  application_id: string;
  offer_id: string;
  status: string;
  requisition_status: string;
  branch?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Accepted: fills + 1, commitments − 1, the Acceptance checkpoint for this opening; the application reads Offer Accepted; then the capacity branch — partially filled (the loop continues) or accepted at capacity (clocks stop, Time to Acceptance among them, the disposition worklist opens with its target, alternates pass on). Acceptance alone does not fill: Filled follows the HRIS hire. A second acceptance beyond the openings is refused. */
export const offerRecordAcceptance = (input: OfferRecordAcceptanceInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<OfferRecordAcceptanceOutput>("offer.record_acceptance", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type OfferRecordAcceptanceReversalInput = {
  requisition_id: string;
  application_id: string;
  reason: string;
  effective_at?: string | null;
  before_start?: boolean;
  told_check?: boolean;
};

export type OfferRecordAcceptanceReversalOutput = {
  application_id: string;
  offer_id: string;
  status: string;
  requisition_status: string;
  branch?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Accepted and did not proceed (reason required; before or after the start date): fills − 1, the Acceptance checkpoint superseded by a reversal marker (never deleted), the application → reneged with its disposition obligation; the requisition recomputes. From closed-filled it records the reversal and asks the HR Business Partner to approve the reopening, and stays closed until they do. */
export const offerRecordAcceptanceReversal = (input: OfferRecordAcceptanceReversalInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<OfferRecordAcceptanceReversalOutput>("offer.record_acceptance_reversal", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type OfferRecordDeclineInput = {
  requisition_id: string;
  application_id: string;
  alternate_application_id?: string | null;
  final_notes?: string | null;
  reporting_category?: string | null;
  reason_code: string;
  note?: string | null;
  next_path: "alternate" | "continue" | "reopen" | "close";
  effective_at?: string | null;
};

export type OfferRecordDeclineOutput = {
  application_id: string;
  offer_id: string;
  status: string;
  requisition_status: string;
  branch?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** The candidate declined: a reason from the published offer-decline reasons (a note when it asks for one) and the Recruiter's next path — extend to an alternate, continue the search, reopen it or close it (the HR Business Partner's cancellation request) — recorded together or not at all. Commitments − 1, the application → declined with its disposition obligation. */
export const offerRecordDecline = (input: OfferRecordDeclineInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<OfferRecordDeclineOutput>("offer.record_decline", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type OfferRecordLetterSentInput = {
  requisition_id: string;
  application_id: string;
  letter_ref: string;
  sent_at?: string | null;
  package_version?: number | null;
};

export type OfferRecordLetterSentOutput = {
  application_id: string;
  offer_id: string;
  status: string;
  requisition_status: string;
  branch?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** The letter went from the applicant tracking system: its reference and date; the version it states is reconciled against the bound package (a mismatch is flagged, not refused). */
export const offerRecordLetterSent = (input: OfferRecordLetterSentInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<OfferRecordLetterSentOutput>("offer.record_letter_sent", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CounterItem = {
  field: "base" | "bonus_target" | "commission" | "sign_on" | "relocation" | "lti" | "start_date" | "other";
  value?: number | string | null;
  text?: string | null;
};

export type OfferRecordResponseInput = {
  requisition_id: string;
  application_id: string;
  response: "negotiating";
  counter: CounterItem[];
  candidate_note?: string | null;
  source: "call" | "mail" | "in_person" | "other";
  received_at?: string | null;
  note?: string | null;
};

export type OfferRecordResponseOutput = {
  application_id: string;
  offer_id: string;
  status: string;
  requisition_status: string;
  branch?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** The candidate came back on terms: one structured counter round (each term asked for, how it reached you, when) — the offer moves to Negotiation, the clock keeps running, and the Recruiter revises the package as a new version that goes back through approval. */
export const offerRecordResponse = (input: OfferRecordResponseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<OfferRecordResponseOutput>("offer.record_response", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type OfferRecordVerbalInput = {
  requisition_id: string;
  application_id: string;
  extended_at?: string | null;
  extended_by?: string | null;
  terms_conveyed: string;
};

export type OfferRecordVerbalOutput = {
  application_id: string;
  offer_id: string;
  status: string;
  requisition_status: string;
  branch?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** The verbal offer: date, by whom, the terms conveyed — refused with the rule named when a gate is open or no opening remains; stamps the Offer checkpoint; the outcome becomes irreversible. */
export const offerRecordVerbal = (input: OfferRecordVerbalInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<OfferRecordVerbalOutput>("offer.record_verbal", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type OfferRemindLetterInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type OfferRemindLetterOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** G-52: two business days after the verbal offer without a letter — re-raised on the Recruiter's Today. */
export const offerRemindLetter = (input: OfferRemindLetterInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<OfferRemindLetterOutput>("offer.remind_letter", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type OfferRequestOfferCallInput = {
  requisition_id: string;
  application_id: string;
};

export type OfferRequestOfferCallOutput = {
  scheduling_request_id: string;
  status: string;
  receipt_id?: number | null;
};

/** Ask for the offer call to be booked: opens (or returns the live) offer-call scheduling request for this candidate; the Coordinator Workbench books it. */
export const offerRequestOfferCall = (input: OfferRequestOfferCallInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<OfferRequestOfferCallOutput>("offer.request_offer_call", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type OfferWithdrawInput = {
  requisition_id: string;
  application_id: string;
  reason: string;
  effective_at?: string | null;
};

export type OfferWithdrawOutput = {
  application_id: string;
  offer_id: string;
  status: string;
  requisition_status: string;
  branch?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** The business withdraws the offer (reason required): commitments − 1 when it was out, the application → passed on with its disposition obligation; the decision returns to the room. */
export const offerWithdraw = (input: OfferWithdrawInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<OfferWithdrawOutput>("offer.withdraw", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PacketApproveSetInput = {
  requisition_id: string;
};

export type PacketApproveSetOutput = {
  requisition_id: string;
  set_status: string;
  packet_ids: string[];
  effective_at: string;
  receipt_id?: number | null;
};

/** The earlier name of packet.review: the Recruiter reviews the generated packet set and certifies its coverage snapshot (E-34 review-to-release timer). */
export const packetApproveSet = (input: PacketApproveSetInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PacketApproveSetOutput>("packet.approve_set", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PacketComputeCcInput = {
  requisition_id: string;
};

export type PacketComputeCcOutput = {
  requisition_id: string;
  cc: Record<string, unknown>[];
};

/** The kickoff copy list with the reason beside each name (the Recruiter always; the HR Business Partner when any round is onsite). */
export const packetComputeCc = (input: PacketComputeCcInput = {} as PacketComputeCcInput) => callTool<PacketComputeCcOutput>("packet.compute_cc", input);

export type PacketDraftKickoffInput = {
  requisition_id: string;
};

export type PacketDraftKickoffOutput = {
  requisition_id: string;
  template_version: number;
  subject: string;
  body: string;
  placeholders: string[];
  bodies: Record<string, unknown>[];
  recipients: Record<string, unknown>[];
  cc: Record<string, unknown>[];
  ready: boolean;
  reasons: string[];
};

/** The one panel email the release sends, composed from the finalized panel: every interviewer, the Hiring Manager, the Recruiter and the HR Business Partner as recipients, each with their own links only; no copy list. */
export const packetDraftKickoff = (input: PacketDraftKickoffInput = {} as PacketDraftKickoffInput) => callTool<PacketDraftKickoffOutput>("packet.draft_kickoff", input);

export type PacketGenerateInput = {
  requisition_id: string;
};

export type PacketGenerateOutput = {
  requisition_id: string;
  set_status: string;
  packet_ids: string[];
  effective_at: string;
  receipt_id?: number | null;
};

/** The Recruiter regenerates one packet per onsite round in use at the current panel version (values pages, skill page, recommendation page from the pinned guide); the Hiring Manager sees the plan. */
export const packetGenerate = (input: PacketGenerateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PacketGenerateOutput>("packet.generate", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PacketGenerateAutoInput = {
  requisition_id?: string | null;
  release_id?: string | null;
  subject_id?: string | null;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type PacketGenerateAutoOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** P-1: generate the packet set of the draft release once the plan is finalized; a refusal is recorded on the release for the Recruiter. */
export const packetGenerateAuto = (input: PacketGenerateAutoInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PacketGenerateAutoOutput>("packet.generate_auto", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PacketIssueLinkInput = {
  requisition_id: string;
  round_def_id: string;
};

export type PacketIssueLinkOutput = {
  requisition_id: string;
  set_status: string;
  packet_ids: string[];
  effective_at: string;
  receipt_id?: number | null;
};

/** Mint the link for one onsite packet, bound to the interviewer's identity, once the Recruiter has released the set. */
export const packetIssueLink = (input: PacketIssueLinkInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PacketIssueLinkOutput>("packet.issue_link", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PacketListMineInput = {
  include_past?: boolean;
};

export type BackupRound = {
  requisition_id: string;
  requisition_title: string;
  round_def_id: string;
  round_label: string;
  format: string;
  duration_minutes: number;
  assesses: string[];
  skill_numbers: number[];
};

export type PacketRow = {
  packet_id: string;
  series_id: string;
  version: number;
  requisition_id: string;
  requisition_title: string;
  level: string;
  round_def_id: string;
  round_label: string;
  round_kind: string;
  assesses: string[];
  skill_numbers: number[];
  format: string;
  duration_minutes: number;
  status: string;
  panel_seat_status?: string | null;
  issued_at: string | null;
  first_opened_at: string | null;
  interviews: Record<string, unknown>[];
  ack_id?: string | null;
  ack_status?: string | null;
  nonresponse?: boolean;
  change_status?: string | null;
};

export type PacketListMineOutput = {
  items: PacketRow[];
  backups: BackupRound[];
};

/** My Packets / My Interviews: every packet issued to me with the interviews it covers and their scorecard status. */
export const packetListMine = (input: PacketListMineInput = {} as PacketListMineInput) => callTool<PacketListMineOutput>("packet.list_mine", input);

export type PacketOpenInput = {
  packet_id: string;
  application_id?: string | null;
};

export type DeclineNotice = {
  hours: number;
  within_window: boolean;
};

export type PacketOpenOutput = {
  packet_id: string;
  series_id: string;
  version: number;
  requisition_id: string;
  requisition_title: string;
  level: string;
  guide_version: number;
  round_def_id: string;
  round_label: string;
  round_kind: string;
  values_assigned: string[];
  skill_numbers: number[];
  format: string;
  duration_minutes: number;
  status: string;
  panel_seat_status?: string | null;
  delivery: string;
  holder_id: string;
  holder_name: string;
  projection: string;
  pages: Record<string, unknown> | null;
  interviews: Record<string, unknown>[];
  reminders: string[];
  decline_notice?: DeclineNotice | null;
};

/** One packet version: the holder's instrument with its pages and the interviews it covers; previews without scores for the Recruiter and the Hiring Manager; assignments only for the HR Business Partner. */
export const packetOpen = (input: PacketOpenInput = {} as PacketOpenInput) => callTool<PacketOpenOutput>("packet.open", input);

export type PacketPrintInput = {
  packet_id: string;
  blank?: boolean;
  application_id?: string | null;
};

export type PacketPrintOutput = {
  packet_id: string;
  holder_name: string;
  pages: Record<string, unknown>;
  rendition: string;
  rendition_html?: string | null;
  printed_at: string;
  file_id: string;
  version_id: string;
  receipt_id: string;
  classification: string;
};

/** The printable rendition of a packet (blank for an external panelist, or the holder's own); an export receipt is written; comp never appears. */
export const packetPrint = (input: PacketPrintInput = {} as PacketPrintInput) => callTool<PacketPrintOutput>("packet.print", input);

export type PacketReadinessCheckInput = {
  requisition_id: string;
};

export type AckCounts = {
  accepted?: number;
  pending?: number;
  declined?: number;
  nonresponse?: number;
};

export type AckRow = {
  acknowledgment_id: string;
  person_id: string;
  name: string | null;
  round_def_id: string;
  round_label: string | null;
  status: string;
  nonresponse: boolean;
  responded_at: string | null;
};

export type AckSummaryOut = {
  requisition_id: string;
  counts: AckCounts;
  rows: AckRow[];
  sla_risk: "none" | "at_risk" | "breached";
};

export type GridRow = {
  round_def_id: string;
  round_label: string;
  round_kind: string;
  in_use: boolean;
  format: string;
  duration_minutes: number;
  interviewer_id: string | null;
  interviewer_name: string | null;
  backup_name: string | null;
  external: boolean;
  assesses: string[];
  skill_numbers: number[];
  packet_id: string | null;
  packet_version: number | null;
  packet_status: string;
  first_opened_at: string | null;
  delivery: string | null;
  scorecards: Record<string, number>;
};

export type ReminderHistory = {
  id: string;
  at: string | null;
  outcome: string | null;
  detail: string | null;
  state: string;
};

export type ReminderOptions = {
  approved_at: string | null;
  approved_by: string | null;
  items: ScorecardReminder[];
};

export type ScorecardReminder = {
  scorecard_id: string;
  application_id: string;
  candidate_name: string;
  round_def_id: string;
  round_label: string;
  recipient_name: string;
  due_at: string | null;
  wording: string;
  history: ReminderHistory[];
};

export type PacketReadinessCheckOutput = {
  requisition_id: string;
  projection?: string;
  intake_status: string;
  panel_version: number;
  set_status: string;
  ready: boolean | null;
  reasons: string[];
  grid: GridRow[];
  kickoff: Record<string, unknown> | null;
  approved_set: Record<string, unknown> | null;
  can: Record<string, boolean>;
  reminder_options: ReminderOptions | null;
  release?: Record<string, unknown> | null;
  blockers?: string[];
  acknowledgments?: AckSummaryOut | null;
};

/** Kickoff & Packets for one requisition, shaped by the reader's seat: the packet grid (one row per round in use), the readiness list, the release journey and the acknowledgment summary. */
export const packetReadinessCheck = (input: PacketReadinessCheckInput = {} as PacketReadinessCheckInput) => callTool<PacketReadinessCheckOutput>("packet.readiness_check", input);

export type PacketReissueInput = {
  requisition_id: string;
  round_def_id: string;
  reason: string;
};

export type PacketReissueOutput = {
  requisition_id: string;
  set_status: string;
  packet_ids: string[];
  effective_at: string;
  receipt_id?: number | null;
};

/** After a panel change: the old link is revoked, a new version is generated and issued to the current seat holder, and a correction note goes into the kickoff thread. */
export const packetReissue = (input: PacketReissueInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PacketReissueOutput>("packet.reissue", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PacketReleaseInput = {
  requisition_id: string;
  expected_version?: number | null;
};

export type PacketReleaseOutput = {
  requisition_id: string;
  release_id: string;
  status: string;
  version: number;
  kickoff_email_id?: string | null;
  receipt_id?: number | null;
};

/** The Recruiter releases the reviewed set: blocked while coverage is incomplete or the set changed since the review; issues every onsite packet, sends one panel email with a delivery per recipient (simulated) and opens an acknowledgment for every loop interviewer. */
export const packetRelease = (input: PacketReleaseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PacketReleaseOutput>("packet.release", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PacketRemindKickoffApprovalInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type PacketRemindKickoffApprovalOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** E-34: the packet set was approved two business days ago and the kickoff has not gone out. */
export const packetRemindKickoffApproval = (input: PacketRemindKickoffApprovalInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PacketRemindKickoffApprovalOutput>("packet.remind_kickoff_approval", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PacketRemindUnopenedInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type PacketRemindUnopenedOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** E-38: a packet still unopened 24 hours before its interview — the Recruiter is told. */
export const packetRemindUnopened = (input: PacketRemindUnopenedInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PacketRemindUnopenedOutput>("packet.remind_unopened", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PacketReviewInput = {
  requisition_id: string;
  expected_version?: number | null;
};

export type PacketReviewOutput = {
  requisition_id: string;
  release_id: string;
  status: string;
  version: number;
  kickoff_email_id?: string | null;
  receipt_id?: number | null;
};

/** The Recruiter reviews the generated packet set: refused while readiness or coverage is incomplete; certifies the set that was reviewed and starts the release clock (E-34). */
export const packetReview = (input: PacketReviewInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PacketReviewOutput>("packet.review", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PacketRevokeLinkInput = {
  requisition_id: string;
  packet_id: string;
  reason: string;
};

export type PacketRevokeLinkOutput = {
  requisition_id: string;
  set_status: string;
  packet_ids: string[];
  effective_at: string;
  receipt_id?: number | null;
};

/** Revoke a packet's link (the seat was removed); no scorecard may be opened from it. */
export const packetRevokeLink = (input: PacketRevokeLinkInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PacketRevokeLinkOutput>("packet.revoke_link", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PacketSendKickoffInput = {
  requisition_id: string;
  subject?: string | null;
  body?: string | null;
  for_person?: string | null;
};

export type PacketSendKickoffOutput = {
  requisition_id: string;
  set_status: string;
  packet_ids: string[];
  effective_at: string;
  receipt_id?: number | null;
};

/** The earlier name of packet.release: the Recruiter releases the reviewed set in their own name; one panel email to every recipient, every onsite packet issued, one acknowledgment opened per primary seat. */
export const packetSendKickoff = (input: PacketSendKickoffInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PacketSendKickoffOutput>("packet.send_kickoff", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelAcceptInput = {
  requisition_id: string;
  acknowledgment_id: string;
  expected_version?: number | null;
};

export type PanelAcceptOutput = {
  requisition_id: string;
  acknowledgment_id: string;
  round_def_id: string;
  status: string;
  version: number;
  responded_at: string | null;
  nonresponse: boolean;
  receipt_id?: number | null;
};

/** The interviewer accepts their own panel assignment: Acknowledgment Pending becomes Accepted. */
export const panelAccept = (input: PanelAcceptInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelAcceptOutput>("panel.accept", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelAckSummaryInput = {
  requisition_id: string;
};

export type PanelAckSummaryOutput = {
  requisition_id: string;
  counts: AckCounts;
  rows: AckRow[];
  sla_risk: "none" | "at_risk" | "breached";
};

/** The panel acknowledgment summary for one requisition: Accepted, Acknowledgment Pending and Declined counts, nonresponse flags, one row per interviewer and the SLA risk. */
export const panelAckSummary = (input: PanelAckSummaryInput = {} as PanelAckSummaryInput) => callTool<PanelAckSummaryOutput>("panel.ack_summary", input);

export type PanelAnswerQuestionInput = {
  requisition_id: string;
  question_id: string;
  answer: string;
};

export type PanelAnswerQuestionOutput = {
  id: string;
  round_label: string | null;
  asker: string;
  audience: string;
  question: string;
  asked_at: string;
  answer: string | null;
  answered_by: string | null;
  answered_at: string | null;
  receipt_id?: number | null;
};

/** Answer a panelist's question — the Hiring Manager or the Recruiter, whichever it was addressed to; the asker is told; the question timer stops. */
export const panelAnswerQuestion = (input: PanelAnswerQuestionInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelAnswerQuestionOutput>("panel.answer_question", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelAskHiringManagerInput = {
  requisition_id: string;
  packet_id?: string | null;
  round_def_id?: string | null;
  question: string;
  audience?: "hiring_manager" | "recruiter";
};

export type PanelAskHiringManagerOutput = {
  id: string;
  round_label: string | null;
  asker: string;
  audience: string;
  question: string;
  asked_at: string;
  answer: string | null;
  answered_by: string | null;
  answered_at: string | null;
  receipt_id?: number | null;
};

/** Ask a question from your packet — the Hiring Manager by default, the Recruiter with audience='recruiter' (EM-10 / AF-11 'Ask your Recruiter'); unanswered after a business day it is re-raised (E-39). */
export const panelAskHiringManager = (input: PanelAskHiringManagerInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelAskHiringManagerOutput>("panel.ask_hiring_manager", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelAssignSkillsInput = {
  requisition_id: string;
  assignments: Record<string, number[]>;
  expected_plan_revision?: number | null;
};

export type PanelAssignSkillsOutput = {
  delegation_directory?: Record<string, unknown>[];
  approval_label?: string | null;
  finalized_label?: string | null;
  requisition_id: string;
  status: string;
  status_label: string;
  panel_version: number;
  level: string;
  part0: Record<string, unknown>;
  part0_missing: string[];
  part0_locked_fields: string[];
  approved_by: string | null;
  approved_on: string | null;
  part1: Record<string, unknown>;
  part1_text?: Part1Text;
  hm_windows?: HmWindowOut[];
  part1_sections_complete: string[];
  part1_sections: string[];
  part1_sent_at: string | null;
  part1_due_at: string | null;
  part1_ready_at: string | null;
  hm_acknowledged_at: string | null;
  meeting: Record<string, unknown> | null;
  skills: Record<string, unknown>[];
  rounds: RoundOut[];
  exercise: Record<string, unknown> | null;
  coverage: Record<string, unknown>;
  gates: Gate[];
  can_finalize: boolean;
  finalized_at: string | null;
  guide_version: number;
  limits: Record<string, number>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Which role-specific skill each round assesses (at most two per round); the Hiring Manager and the Recruiter. */
export const panelAssignSkills = (input: PanelAssignSkillsInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelAssignSkillsOutput>("panel.assign_skills", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelAssignValuesInput = {
  requisition_id: string;
  assignments: Record<string, string[]>;
  expected_plan_revision?: number | null;
};

export type PanelAssignValuesOutput = {
  delegation_directory?: Record<string, unknown>[];
  approval_label?: string | null;
  finalized_label?: string | null;
  requisition_id: string;
  status: string;
  status_label: string;
  panel_version: number;
  level: string;
  part0: Record<string, unknown>;
  part0_missing: string[];
  part0_locked_fields: string[];
  approved_by: string | null;
  approved_on: string | null;
  part1: Record<string, unknown>;
  part1_text?: Part1Text;
  hm_windows?: HmWindowOut[];
  part1_sections_complete: string[];
  part1_sections: string[];
  part1_sent_at: string | null;
  part1_due_at: string | null;
  part1_ready_at: string | null;
  hm_acknowledged_at: string | null;
  meeting: Record<string, unknown> | null;
  skills: Record<string, unknown>[];
  rounds: RoundOut[];
  exercise: Record<string, unknown> | null;
  coverage: Record<string, unknown>;
  gates: Gate[];
  can_finalize: boolean;
  finalized_at: string | null;
  guide_version: number;
  limits: Record<string, number>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** R·O·O·T·S per round, the HM Round included (it pre-ticks at most two); the Hiring Manager and the Recruiter. Every value is covered by some round before the plan finalizes. */
export const panelAssignValues = (input: PanelAssignValuesInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelAssignValuesOutput>("panel.assign_values", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundIn = {
  key: string;
  in_use?: boolean;
  format?: "virtual" | "onsite";
  duration_minutes?: number | null;
  primary_id?: string | null;
  backup_id?: string | null;
  primary_external_name?: string | null;
  values?: string[];
  skill_numbers?: number[];
  executive_participation?: boolean | null;
  recommendations_text?: string | null;
};

export type PanelBuildInput = {
  requisition_id: string;
  rounds: RoundIn[];
  expected_plan_revision?: number | null;
};

export type PanelBuildOutput = {
  delegation_directory?: Record<string, unknown>[];
  approval_label?: string | null;
  finalized_label?: string | null;
  requisition_id: string;
  status: string;
  status_label: string;
  panel_version: number;
  level: string;
  part0: Record<string, unknown>;
  part0_missing: string[];
  part0_locked_fields: string[];
  approved_by: string | null;
  approved_on: string | null;
  part1: Record<string, unknown>;
  part1_text?: Part1Text;
  hm_windows?: HmWindowOut[];
  part1_sections_complete: string[];
  part1_sections: string[];
  part1_sent_at: string | null;
  part1_due_at: string | null;
  part1_ready_at: string | null;
  hm_acknowledged_at: string | null;
  meeting: Record<string, unknown> | null;
  skills: Record<string, unknown>[];
  rounds: RoundOut[];
  exercise: Record<string, unknown> | null;
  coverage: Record<string, unknown>;
  gates: Gate[];
  can_finalize: boolean;
  finalized_at: string | null;
  guide_version: number;
  limits: Record<string, number>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** The interview rounds table: in use, format, duration, primary and backup interviewers, values, skill references, executive participation and interview recommendations per round (the Hiring Manager and the Recruiter). */
export const panelBuild = (input: PanelBuildInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelBuildOutput>("panel.build", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelChangeSlaBreachInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type PanelChangeSlaBreachOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** The review time of a panel change has gone: the review shows as overdue risk on the dashboard. */
export const panelChangeSlaBreach = (input: PanelChangeSlaBreachInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelChangeSlaBreachOutput>("panel.change_sla_breach", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelDecideExerciseInput = {
  requisition_id: string;
  required: boolean;
  brief_text?: string | null;
  criteria?: string[];
  expected_effort?: string | null;
  due_window_days?: number;
  attached_round_key?: string | null;
  reason_when_no?: string | null;
  expected_plan_revision?: number | null;
  assessment_type?: string | null;
  owner_id?: string | null;
  reviewer_ids?: string[] | null;
  recommendations_text?: string | null;
};

export type PanelDecideExerciseOutput = {
  delegation_directory?: Record<string, unknown>[];
  approval_label?: string | null;
  finalized_label?: string | null;
  requisition_id: string;
  status: string;
  status_label: string;
  panel_version: number;
  level: string;
  part0: Record<string, unknown>;
  part0_missing: string[];
  part0_locked_fields: string[];
  approved_by: string | null;
  approved_on: string | null;
  part1: Record<string, unknown>;
  part1_text?: Part1Text;
  hm_windows?: HmWindowOut[];
  part1_sections_complete: string[];
  part1_sections: string[];
  part1_sent_at: string | null;
  part1_due_at: string | null;
  part1_ready_at: string | null;
  hm_acknowledged_at: string | null;
  meeting: Record<string, unknown> | null;
  skills: Record<string, unknown>[];
  rounds: RoundOut[];
  exercise: Record<string, unknown> | null;
  coverage: Record<string, unknown>;
  gates: Gate[];
  can_finalize: boolean;
  finalized_at: string | null;
  guide_version: number;
  limits: Record<string, number>;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Skills assessment / work sample: Yes with brief, type, stage, criteria, owner and reviewers, or No with a reason; the Hiring Manager and the Recruiter decide it at intake. */
export const panelDecideExercise = (input: PanelDecideExerciseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelDecideExerciseOutput>("panel.decide_exercise", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelDeclineInput = {
  requisition_id: string;
  acknowledgment_id: string;
  expected_version?: number | null;
  reason: string;
  notified_manager?: boolean;
};

export type PanelDeclineOutput = {
  requisition_id: string;
  acknowledgment_id: string;
  round_def_id: string;
  status: string;
  version: number;
  responded_at: string | null;
  nonresponse: boolean;
  notice_hours: number;
  within_notice_window: boolean;
  notified_manager: boolean | null;
  receipt_id?: number | null;
};

/** The interviewer declines their own panel assignment (from Pending or Accepted); within the notice window they confirm they told their manager. The Hiring Manager, the Recruiter and the Coordinator are told and a panel change follows. */
export const panelDecline = (input: PanelDeclineInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelDeclineOutput>("panel.decline", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelDeclineRoundInput = {
  requisition_id: string;
  round_def_id: string;
  reason: string;
  notified_manager?: boolean;
};

export type PanelDeclineRoundOutput = {
  requisition_id: string;
  round_def_id: string | null;
  panel_version: number;
  effective_at: string;
  notice_hours: number;
  within_notice_window?: boolean;
  notified_manager?: boolean | null;
  receipt_id?: number | null;
};

/** A seat holder declines a round they hold (not yet held); the Recruiter and the Hiring Manager are told; unanswered after 24 hours it is flagged (E-37). */
export const panelDeclineRound = (input: PanelDeclineRoundInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelDeclineRoundOutput>("panel.decline_round", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelFlagNonresponseInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type PanelFlagNonresponseOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** P-2 lapsed: the panel assignment is still unanswered; it is marked nonresponse (still Pending), the Coordinator is flagged and the Recruiter told. */
export const panelFlagNonresponse = (input: PanelFlagNonresponseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelFlagNonresponseOutput>("panel.flag_nonresponse", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelHmReviewChangeInput = {
  request_id: string;
  expected_version: number;
  decision: "approve" | "reject";
  reason?: string | null;
};

export type CheckOut = {
  key: string;
  passed: boolean;
  gaps: string[];
};

export type RevalidationOut = {
  ok: boolean;
  checks: CheckOut[];
};

export type PanelHmReviewChangeOutput = {
  request_id: string;
  requisition_id: string;
  status: string;
  version: number;
  origin: string;
  kind: string | null;
  material: boolean;
  material_reasons: string[];
  gaps: string[];
  sla_tier: string | null;
  sla_due_at: string | null;
  revalidation?: RevalidationOut | null;
  receipt_id?: number | null;
};

/** The Hiring Manager approves or rejects a material panel change the Recruiter approved. */
export const panelHmReviewChange = (input: PanelHmReviewChangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelHmReviewChangeOutput>("panel.hm_review_change", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelListQuestionsInput = {
  requisition_id: string;
};

export type QuestionOut = {
  id: string;
  round_label: string | null;
  asker: string;
  audience: string;
  question: string;
  asked_at: string;
  answer: string | null;
  answered_by: string | null;
  answered_at: string | null;
};

export type PanelListQuestionsOutput = {
  requisition_id: string;
  items: QuestionOut[];
};

/** Questions on the requisition's panel: own questions to the asker; all to the Hiring Manager and the owning Recruiter. */
export const panelListQuestions = (input: PanelListQuestionsInput = {} as PanelListQuestionsInput) => callTool<PanelListQuestionsOutput>("panel.list_questions", input);

export type PanelMyAcknowledgmentsInput = Record<string, unknown>;

export type MyAckRow = {
  acknowledgment_id: string;
  requisition_id: string;
  round_def_id: string;
  round_label: string | null;
  status: string;
  nonresponse: boolean;
  responded_at: string | null;
  within_notice_window: boolean | null;
  interview_start: string | null;
  change_id: string | null;
  change_status: string | null;
  version: number;
};

export type RevokedPacket = {
  series_id: string;
  revoked_at: string | null;
};

export type PanelMyAcknowledgmentsOutput = {
  rows: MyAckRow[];
  revoked?: RevokedPacket[];
};

/** My panel acknowledgments: every live acknowledgment I hold with its interview start and the panel change a decline opened, and the packets withdrawn from me. */
export const panelMyAcknowledgments = (input: PanelMyAcknowledgmentsInput = {} as PanelMyAcknowledgmentsInput) => callTool<PanelMyAcknowledgmentsOutput>("panel.my_acknowledgments", input);

export type PanelNotifyQuestionUnansweredInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type PanelNotifyQuestionUnansweredOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** E-39: a panelist's question is unanswered after a business day. */
export const panelNotifyQuestionUnanswered = (input: PanelNotifyQuestionUnansweredInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelNotifyQuestionUnansweredOutput>("panel.notify_question_unanswered", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelOpenInput = {
  requisition_id: string;
};

export type PanelOpenOutput = {
  delegation_directory?: Record<string, unknown>[];
  approval_label?: string | null;
  finalized_label?: string | null;
  requisition_id: string;
  status: string;
  status_label: string;
  panel_version: number;
  level: string;
  part0: Record<string, unknown>;
  part0_missing: string[];
  part0_locked_fields: string[];
  approved_by: string | null;
  approved_on: string | null;
  part1: Record<string, unknown>;
  part1_text?: Part1Text;
  hm_windows?: HmWindowOut[];
  part1_sections_complete: string[];
  part1_sections: string[];
  part1_sent_at: string | null;
  part1_due_at: string | null;
  part1_ready_at: string | null;
  hm_acknowledged_at: string | null;
  meeting: Record<string, unknown> | null;
  skills: Record<string, unknown>[];
  rounds: RoundOut[];
  exercise: Record<string, unknown> | null;
  coverage: Record<string, unknown>;
  gates: Gate[];
  can_finalize: boolean;
  finalized_at: string | null;
  guide_version: number;
  limits: Record<string, number>;
  can: Record<string, boolean>;
};

/** The panel roster: rounds, seats, values and skills per round. */
export const panelOpen = (input: PanelOpenInput = {} as PanelOpenInput) => callTool<PanelOpenOutput>("panel.open", input);

export type PanelOpenChangeInput = {
  request_id: string;
};

export type PanelOpenChangeOutput = {
  request_id: string;
  requisition_id: string;
  status: string;
  version: number;
  origin: string;
  kind: string | null;
  material: boolean;
  material_reasons: string[];
  gaps: string[];
  sla_tier: string | null;
  sla_due_at: string | null;
  revalidation?: RevalidationOut | null;
  requested_by_id: string;
  requested_role: string;
  seat_changes: Record<string, unknown>[];
  round_changes: Record<string, unknown>[];
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  interview_start: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  from_panel_version: number;
  to_panel_version: number | null;
  rejection_reason?: string | null;
  reason?: string | null;
  people?: Record<string, string>;
  loop_rounds?: Record<string, number>;
};

/** One panel change: its status, what it changes, its gaps and its review time. */
export const panelOpenChange = (input: PanelOpenChangeInput = {} as PanelOpenChangeInput) => callTool<PanelOpenChangeOutput>("panel.open_change", input);

export type PanelPeopleInput = {
  requisition_id: string;
  q?: string | null;
};

export type PersonPick = {
  id: string;
  name: string;
  initials: string;
  role_words: string[];
};

export type PanelPeopleOutput = {
  people: PersonPick[];
};

/** The people you may seat on an interview round: active Interviewers, Hiring Managers and Recruiters by name, with their role words. Never an email. */
export const panelPeople = (input: PanelPeopleInput = {} as PanelPeopleInput) => callTool<PanelPeopleOutput>("panel.people", input);

export type RoundChangeIn = {
  round_def_id: string;
  in_use?: boolean | null;
  values?: ("respect" | "ownership" | "opportunity" | "together" | "service")[] | null;
  skill_numbers?: number[] | null;
};

export type SeatChangeIn = {
  round_def_id: string;
  seat?: "primary" | "backup";
  to_person_id?: string | null;
  external?: boolean;
};

export type PanelPreviewChangeInput = {
  seat_changes?: SeatChangeIn[];
  round_changes?: RoundChangeIn[];
  requisition_id: string;
};

export type PanelPreviewChangeOutput = {
  material: boolean;
  material_reasons: string[];
  kind: string;
  sla_tier: string;
  sla_due_at: string;
  interview_start: string | null;
  revalidation: RevalidationOut;
};

/** What a panel change would be before it is drafted: material or not, its kind, the five checks and its review time. */
export const panelPreviewChange = (input: PanelPreviewChangeInput = {} as PanelPreviewChangeInput) => callTool<PanelPreviewChangeOutput>("panel.preview_change", input);

export type PanelProgressChangeInput = {
  subject_id?: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
  request_id?: string | null;
};

export type PanelProgressChangeOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  request_id?: string | null;
  status?: string | null;
  attempt?: number;
  receipt_id?: number | null;
};

/** P-4: an approved panel change becomes Communicated when every notice is delivered and Completed when access, calendars and scorecards are updated and every task is closed; re-checked every 15 minutes, at most 8 times. */
export const panelProgressChange = (input: PanelProgressChangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelProgressChangeOutput>("panel.progress_change", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelPromoteBackupInput = {
  requisition_id: string;
  round_def_id: string;
  reason: string;
};

export type PanelPromoteBackupOutput = {
  requisition_id: string;
  round_def_id: string | null;
  panel_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Internal: the backup takes the round (not yet held) when an approved panel change names them; people ask through panel.request_change. */
export const panelPromoteBackup = (input: PanelPromoteBackupInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelPromoteBackupOutput>("panel.promote_backup", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelRecordChangeInput = {
  requisition_id: string;
  reason: string;
  seats: Record<string, unknown>[];
};

export type PanelRecordChangeOutput = {
  requisition_id: string;
  round_def_id: string | null;
  panel_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Internal: an approved panel change applied after finalize (seats replaced, panel_version +1, affected issued packets superseded); people ask through panel.request_change. */
export const panelRecordChange = (input: PanelRecordChangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelRecordChangeOutput>("panel.record_change", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelRemindAcknowledgmentInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type PanelRemindAcknowledgmentOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** P-2 half time: the interviewer has not answered their panel assignment and is reminded. */
export const panelRemindAcknowledgment = (input: PanelRemindAcknowledgmentInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelRemindAcknowledgmentOutput>("panel.remind_acknowledgment", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelRemindChangeReviewInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type PanelRemindChangeReviewOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** Half the review time of a panel change has gone, or it is nearly overdue: the reviewer is reminded (urgently near the end). */
export const panelRemindChangeReview = (input: PanelRemindChangeReviewInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelRemindChangeReviewOutput>("panel.remind_change_review", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelRequestChangeInput = {
  seat_changes?: SeatChangeIn[];
  round_changes?: RoundChangeIn[];
  requisition_id: string;
  reason: string;
};

export type PanelRequestChangeOutput = {
  request_id: string;
  requisition_id: string;
  status: string;
  version: number;
  origin: string;
  kind: string | null;
  material: boolean;
  material_reasons: string[];
  gaps: string[];
  sla_tier: string | null;
  sla_due_at: string | null;
  revalidation?: RevalidationOut | null;
  receipt_id?: number | null;
};

/** Draft a change to the finalized interview panel: replace, add or remove an interviewer, or change a round's use, values or skills. */
export const panelRequestChange = (input: PanelRequestChangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelRequestChangeOutput>("panel.request_change", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelReviewChangeInput = {
  request_id: string;
  expected_version: number;
  decision: "approve" | "reject";
  reason?: string | null;
};

export type PanelReviewChangeOutput = {
  request_id: string;
  requisition_id: string;
  status: string;
  version: number;
  origin: string;
  kind: string | null;
  material: boolean;
  material_reasons: string[];
  gaps: string[];
  sla_tier: string | null;
  sla_due_at: string | null;
  revalidation?: RevalidationOut | null;
  receipt_id?: number | null;
};

/** The Recruiter approves or rejects a panel change. A material change goes on to the Hiring Manager; every approval checks the change again first. */
export const panelReviewChange = (input: PanelReviewChangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelReviewChangeOutput>("panel.review_change", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelSubmitChangeInput = {
  request_id: string;
  expected_version: number;
};

export type PanelSubmitChangeOutput = {
  request_id: string;
  requisition_id: string;
  status: string;
  version: number;
  origin: string;
  kind: string | null;
  material: boolean;
  material_reasons: string[];
  gaps: string[];
  sla_tier: string | null;
  sla_due_at: string | null;
  revalidation?: RevalidationOut | null;
  receipt_id?: number | null;
};

/** Submit a drafted panel change: it is checked against the plan's coverage, seats, scorecards and debriefs, then goes to the Recruiter for review or is blocked with its gaps. */
export const panelSubmitChange = (input: PanelSubmitChangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelSubmitChangeOutput>("panel.submit_change", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PanelUpdateChangeInput = {
  seat_changes?: SeatChangeIn[];
  round_changes?: RoundChangeIn[];
  request_id: string;
  expected_version: number;
  reason?: string | null;
};

export type PanelUpdateChangeOutput = {
  request_id: string;
  requisition_id: string;
  status: string;
  version: number;
  origin: string;
  kind: string | null;
  material: boolean;
  material_reasons: string[];
  gaps: string[];
  sla_tier: string | null;
  sla_due_at: string | null;
  revalidation?: RevalidationOut | null;
  receipt_id?: number | null;
};

/** Edit a draft or blocked panel change; a blocked change is checked again and routed for review when its gaps are resolved. */
export const panelUpdateChange = (input: PanelUpdateChangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<PanelUpdateChangeOutput>("panel.update_change", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type PeopleOptionsInput = {
  role: "recruiter" | "coordinator" | "interviewer" | "hm" | "hrbp";
};

export type PersonOption = {
  person_id: string;
  name: string;
  initials: string;
  hue: string;
};

export type PeopleOptionsOutput = {
  role: string;
  options: PersonOption[];
};

/** The active holders of one role a screen picks from, as id, name, initials and accent only; no contact, account or status field. */
export const peopleOptions = (input: PeopleOptionsInput = {} as PeopleOptionsInput) => callTool<PeopleOptionsOutput>("people.options", input);

export type PermissionsMatrixInput = Record<string, unknown>;

export type MatrixRowOut = {
  key: string;
  label: string;
  source: string;
  cells: Record<string, string>;
};

export type PermissionsMatrixOutput = {
  title: string;
  header: string;
  roles: string[];
  columns: Record<string, string>;
  rows: MatrixRowOut[];
  version: string;
};

/** The Master Permission Matrix as the application enforces it: the eight roles, every capability row in his words with its source, and the version of the file it was read from. Read-only. */
export const permissionsMatrix = (input: PermissionsMatrixInput = {} as PermissionsMatrixInput) => callTool<PermissionsMatrixOutput>("permissions.matrix", input);

export type ProposalDenyInput = {
  proposal_id: string;
  reason: string;
};

export type ProposalDenyOutput = {
  proposal_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Turn a draft down, with the reason why. It stops waiting for you and nothing is sent. */
export const proposalDeny = (input: ProposalDenyInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ProposalDenyOutput>("proposal.deny", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ProposalOpenInput = {
  proposal_id: string;
};

export type ProposalOpenOutput = {
  proposal_id: string;
  kind: string;
  requisition_id: string | null;
  requisition_title: string | null;
  application_id: string | null;
  artifact: Record<string, unknown>;
  before_after: Record<string, unknown> | null;
  why: string | null;
  produced_by: string;
  proposed_by: string | null;
  proposed_by_role: string | null;
  drafted_at: string;
  approver_role: string;
  approver_role_label: string;
  status: string;
  decision_reason: string | null;
  decided_at: string | null;
  decided_by: string | null;
  resulting_tool: string | null;
  expires_at: string | null;
  expired: boolean;
  can: Record<string, boolean>;
};

/** Read a draft the application prepared for you: what it says, why, what it would change and where it stands. Approving one is sending it with the tool it belongs to. */
export const proposalOpen = (input: ProposalOpenInput = {} as ProposalOpenInput) => callTool<ProposalOpenOutput>("proposal.open", input);

export type ReadinessOpenInput = {
  requisition_id: string;
};

export type PrerequisiteOut = {
  key: string;
  owner_role: string;
  fed: boolean;
  met: boolean | null;
  route: string | null;
  blocking_posting: boolean;
};

export type ReadinessOpenOutput = {
  requisition_id: string;
  computed_at: string;
  percent: number | null;
  measured: number;
  total: number;
  posting_state: string;
  prerequisites: PrerequisiteOut[];
  config_versions: Record<string, string | null>;
  stored: boolean;
};

/** Search Readiness: percent over the recorded prerequisites, each with its owner role, and the posting state beside it. */
export const readinessOpen = (input: ReadinessOpenInput = {} as ReadinessOpenInput) => callTool<ReadinessOpenOutput>("readiness.open", input);

export type RecruiterAssignInput = {
  request_id?: string | null;
  requisition_id?: string | null;
  recruiter_id: string;
  expected_version: number;
  reason?: string | null;
};

export type RecruiterAssignOutput = {
  request_id: string;
  requisition_id?: string | null;
  recruiter_id: string;
  kind: "assigned" | "reassigned" | "unchanged";
  assignment_id?: string | null;
  followed_rank?: number | null;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** The TA Manager assigns the Recruiter of an approved request, before or after its requisition is created; replacing an assigned Recruiter directly needs a reason. Nobody assigns themselves. */
export const recruiterAssign = (input: RecruiterAssignInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RecruiterAssignOutput>("recruiter.assign", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RecruiterDecideReassignmentInput = {
  reassignment_request_id: string;
  expected_version: number;
  decision: "approve" | "reject";
  to_recruiter_id?: string | null;
  note?: string | null;
};

export type RecruiterDecideReassignmentOutput = {
  reassignment_request_id: string;
  request_id: string;
  requisition_id?: string | null;
  status: string;
  recruiter_id?: string | null;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** The TA Manager approves a reassignment request, naming the Recruiter who takes the search over, or rejects it; never their own request, never to themselves. */
export const recruiterDecideReassignment = (input: RecruiterDecideReassignmentInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RecruiterDecideReassignmentOutput>("recruiter.decide_reassignment", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RecruiterQueueInput = {
  page_size?: number;
};

export type AwaitingRow = {
  request_id: string;
  requisition_id?: string | null;
  title: string;
  level?: string | null;
  department?: string | null;
  confidential: boolean;
  status: string;
  row_version: number;
  posted: boolean;
  recommended: RankedRecruiter[];
};

export type RankedRecruiter = {
  person_id: string;
  name?: string | null;
  rank: number;
  open_owned: number;
  same_level: number;
  same_department: number;
  same_job_family: number;
  reason_codes: string[];
};

export type ReassignmentRow = {
  reassignment_request_id: string;
  request_id: string;
  requisition_id?: string | null;
  title: string;
  from_recruiter_id: string;
  from_recruiter_name?: string | null;
  proposed_recruiter_id?: string | null;
  proposed_recruiter_name?: string | null;
  requested_by_id: string;
  requested_by_name?: string | null;
  requested_seat: string;
  reason: string;
  requested_at: string;
  row_version: number;
};

export type RecruiterQueueOutput = {
  awaiting: AwaitingRow[];
  awaiting_total: number;
  reassignments: ReassignmentRow[];
  reassignment_total: number;
};

/** The TA Manager's assignment queue: approved requests and created requisitions awaiting a Recruiter, with the recommendation, and the reassignment requests awaiting a decision. */
export const recruiterQueue = (input: RecruiterQueueInput = {} as RecruiterQueueInput) => callTool<RecruiterQueueOutput>("recruiter.queue", input);

export type RecruiterRecommendationInput = {
  request_id?: string | null;
  requisition_id?: string | null;
};

export type RecruiterRecommendationOutput = {
  request_id: string;
  recommendation_id?: string | null;
  computed_at?: string | null;
  ranked: RankedRecruiter[];
  changed_since: boolean;
};

/** The recommended Recruiters for an approved request: workload (live searches owned) ascending, then expertise (same level, department, job family) descending; both numbers shown, no score decides. */
export const recruiterRecommendation = (input: RecruiterRecommendationInput = {} as RecruiterRecommendationInput) => callTool<RecruiterRecommendationOutput>("recruiter.recommendation", input);

export type RecruiterRequestReassignmentInput = {
  request_id?: string | null;
  requisition_id?: string | null;
  reason: string;
  proposed_recruiter_id?: string | null;
};

export type RecruiterRequestReassignmentOutput = {
  reassignment_request_id: string;
  request_id: string;
  requisition_id?: string | null;
  status: string;
  recruiter_id?: string | null;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** The Recruiter who holds the search, or its HR Business Partner, asks the TA Manager to reassign it, with a reason; one request at a time. */
export const recruiterRequestReassignment = (input: RecruiterRequestReassignmentInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RecruiterRequestReassignmentOutput>("recruiter.request_reassignment", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RecruiterWithdrawReassignmentInput = {
  reassignment_request_id: string;
  expected_version: number;
};

export type RecruiterWithdrawReassignmentOutput = {
  reassignment_request_id: string;
  request_id: string;
  requisition_id?: string | null;
  status: string;
  recruiter_id?: string | null;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** The person who asked for a reassignment withdraws it while it waits on the TA Manager. */
export const recruiterWithdrawReassignment = (input: RecruiterWithdrawReassignmentInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RecruiterWithdrawReassignmentOutput>("recruiter.withdraw_reassignment", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ReferenceCompleteInput = {
  requisition_id: string;
  application_id: string;
  outcome: "clear" | "clear_with_note" | "concern";
  notes?: string | null;
};

export type ReferenceCompleteOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  projection: string;
  status: string;
  display_status: string;
  requirement: Record<string, unknown>;
  elected: boolean | null;
  references_to_collect: string | null;
  contacts: Record<string, unknown>[];
  outcome: string | null;
  notes: string | null;
  hrbp_consulted_at: string | null;
  completed_at: string | null;
  waived_reason: string | null;
  gate_open: boolean;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Complete the reference check with an outcome: clear · clear with note · concern (notes required for the last two); the comp step opens. */
export const referenceComplete = (input: ReferenceCompleteInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ReferenceCompleteOutput>("reference.complete", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ReferenceConsultHrbpInput = {
  requisition_id: string;
  application_id: string;
  concern_text: string;
};

export type ReferenceConsultHrbpOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  projection: string;
  status: string;
  display_status: string;
  requirement: Record<string, unknown>;
  elected: boolean | null;
  references_to_collect: string | null;
  contacts: Record<string, unknown>[];
  outcome: string | null;
  notes: string | null;
  hrbp_consulted_at: string | null;
  completed_at: string | null;
  waived_reason: string | null;
  gate_open: boolean;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Take a reference concern to the HR Business Partner (their Today gets it; the text stays on the check). */
export const referenceConsultHrbp = (input: ReferenceConsultHrbpInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ReferenceConsultHrbpOutput>("reference.consult_hrbp", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ReferenceNotifyWaitingInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type ReferenceNotifyWaitingOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** G-50: the reference check is open past three business days — the Recruiter's Today shows it waiting. */
export const referenceNotifyWaiting = (input: ReferenceNotifyWaitingInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ReferenceNotifyWaitingOutput>("reference.notify_waiting", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ReferenceOpenInput = {
  requisition_id: string;
  application_id: string;
};

export type ReferenceOpenOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  projection: string;
  status: string;
  display_status: string;
  requirement: Record<string, unknown>;
  elected: boolean | null;
  references_to_collect: string | null;
  contacts: Record<string, unknown>[];
  outcome: string | null;
  notes: string | null;
  hrbp_consulted_at: string | null;
  completed_at: string | null;
  waived_reason: string | null;
  gate_open: boolean;
  can: Record<string, boolean>;
};

/** The reference check for one candidate: the room's election, the contacts, the outcome, the gate (full for the Recruiter and the HR Business Partner; decision, outcome and notes for the Hiring Manager). */
export const referenceOpen = (input: ReferenceOpenInput = {} as ReferenceOpenInput) => callTool<ReferenceOpenOutput>("reference.open", input);

export type ReferenceRecordContactInput = {
  requisition_id: string;
  application_id: string;
  name: string;
  relationship: string;
  contacted_at?: string | null;
  reachable?: boolean;
  notes?: string | null;
};

export type ReferenceRecordContactOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  projection: string;
  status: string;
  display_status: string;
  requirement: Record<string, unknown>;
  elected: boolean | null;
  references_to_collect: string | null;
  contacts: Record<string, unknown>[];
  outcome: string | null;
  notes: string | null;
  hrbp_consulted_at: string | null;
  completed_at: string | null;
  waived_reason: string | null;
  gate_open: boolean;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** One reference contacted: name, relationship, when, reachable, notes (Recruiter; the check must be elected and open). */
export const referenceRecordContact = (input: ReferenceRecordContactInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ReferenceRecordContactOutput>("reference.record_contact", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ReferenceRecordNotElectedInput = {
  requisition_id: string;
  application_id: string;
};

export type ReferenceRecordNotElectedOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  projection: string;
  status: string;
  display_status: string;
  requirement: Record<string, unknown>;
  elected: boolean | null;
  references_to_collect: string | null;
  contacts: Record<string, unknown>[];
  outcome: string | null;
  notes: string | null;
  hrbp_consulted_at: string | null;
  completed_at: string | null;
  waived_reason: string | null;
  gate_open: boolean;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** For a backfilled cycle without an election on record: the room did not elect a reference check. */
export const referenceRecordNotElected = (input: ReferenceRecordNotElectedInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ReferenceRecordNotElectedOutput>("reference.record_not_elected", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ReferenceRecordRequestedInput = {
  requisition_id: string;
  application_id: string;
  provider?: string | null;
  requested_at?: string | null;
};

export type ReferenceRecordRequestedOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  projection: string;
  status: string;
  display_status: string;
  requirement: Record<string, unknown>;
  elected: boolean | null;
  references_to_collect: string | null;
  contacts: Record<string, unknown>[];
  outcome: string | null;
  notes: string | null;
  hrbp_consulted_at: string | null;
  completed_at: string | null;
  waived_reason: string | null;
  gate_open: boolean;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Record that the references were requested, and from which provider once the provider list is published (Recruiter; the check must be required and open). */
export const referenceRecordRequested = (input: ReferenceRecordRequestedInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ReferenceRecordRequestedOutput>("reference.record_requested", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ReferenceRecordUnableInput = {
  requisition_id: string;
  application_id: string;
  reason: string;
  at?: string | null;
};

export type ReferenceRecordUnableOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  projection: string;
  status: string;
  display_status: string;
  requirement: Record<string, unknown>;
  elected: boolean | null;
  references_to_collect: string | null;
  contacts: Record<string, unknown>[];
  outcome: string | null;
  notes: string | null;
  hrbp_consulted_at: string | null;
  completed_at: string | null;
  waived_reason: string | null;
  gate_open: boolean;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Close a required reference check as Unable to Complete, with the reason (Recruiter); it shows as a warning on the offer readiness and does not block the verbal offer. */
export const referenceRecordUnable = (input: ReferenceRecordUnableInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ReferenceRecordUnableOutput>("reference.record_unable", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ReferenceSetRequirementInput = {
  requisition_id: string;
  application_id: string;
  required: boolean;
  owner_id?: string | null;
  due_at?: string | null;
};

export type ReferenceSetRequirementOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  projection: string;
  status: string;
  display_status: string;
  requirement: Record<string, unknown>;
  elected: boolean | null;
  references_to_collect: string | null;
  contacts: Record<string, unknown>[];
  outcome: string | null;
  notes: string | null;
  hrbp_consulted_at: string | null;
  completed_at: string | null;
  waived_reason: string | null;
  gate_open: boolean;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Make the reference check Required or Not Required for one candidate (Recruiter): Required opens the task with its owner and due date; Not Required never blocks the verbal offer. */
export const referenceSetRequirement = (input: ReferenceSetRequirementInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ReferenceSetRequirementOutput>("reference.set_requirement", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ReferenceWaiveGateInput = {
  requisition_id: string;
  application_id: string;
  reason: string;
};

export type ReferenceWaiveGateOutput = {
  application_id: string;
  requisition_id: string;
  candidate: string;
  projection: string;
  status: string;
  display_status: string;
  requirement: Record<string, unknown>;
  elected: boolean | null;
  references_to_collect: string | null;
  contacts: Record<string, unknown>[];
  outcome: string | null;
  notes: string | null;
  hrbp_consulted_at: string | null;
  completed_at: string | null;
  waived_reason: string | null;
  gate_open: boolean;
  can: Record<string, boolean>;
  receipt_id?: number | null;
};

/** Proceed with a typed exception (reference_gate_waived, reason required — printed on the sheet); the comp step opens. */
export const referenceWaiveGate = (input: ReferenceWaiveGateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ReferenceWaiveGateOutput>("reference.waive_gate", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type Bounds = {
  at_least?: number | null;
  at_most?: number | null;
};

export type Period = {
  start: string;
  end: string;
};

export type Sort = {
  by: string;
  descending?: boolean;
};

export type ReportDrillInput = {
  family: string;
  period?: Period | null;
  filters?: Record<string, string[] | Bounds | boolean>;
  group_by?: string | null;
  sort?: Sort | null;
  measure?: string | null;
  bucket?: string | null;
  cursor?: string | null;
  page_size?: number;
};

export type AggregateRow = {
  requisition_id: string;
  title: string;
  level: string;
  department: string;
  status: string;
  archived: boolean;
  count: number;
  route: string;
  opens: "requisition.open" | "archive.open";
};

export type ApplicationRow = unknown;

export type PeriodOut = {
  start: string;
  end: string;
};

export type RequisitionRow = unknown;

export type ReportDrillOutput = {
  family: string;
  subject?: "requisition" | "application";
  period: PeriodOut;
  as_of: string;
  scope_words: string[];
  measure?: string | null;
  bucket?: string | null;
  total: number;
  rows: RequisitionRow[];
  applications?: ApplicationRow[];
  aggregates?: AggregateRow[];
  aggregated?: number;
  next_cursor?: string | null;
};

/** The rows behind a report — all of them, those one measure counted, or one group's — a page at a time, each with where it opens and the configuration versions it used; restricted fields only where the person may see them on that requisition in a report; candidate rows only where the person may open the application, the rest as requisition counts with no candidate key. Audited: one receipt per drill-down. */
export const reportDrill = (input: ReportDrillInput = {} as ReportDrillInput) => callTool<ReportDrillOutput>("report.drill", input);

export type ReportExportInput = {
  family: string;
  period?: Period | null;
  filters?: Record<string, string[] | Bounds | boolean>;
  group_by?: string | null;
  sort?: Sort | null;
  measure?: string | null;
  bucket?: string | null;
  format?: "csv";
};

export type ReportExportOutput = {
  family: string;
  format: "csv";
  period: PeriodOut;
  as_of: string;
  scope_words: string[];
  measure?: string | null;
  bucket?: string | null;
  rows: number;
  columns: string[];
  dropped_columns: string[];
  field_profile: Record<string, string>;
  filename: string;
  csv: string;
  sha256: string;
  version_id: string;
  receipt_id: string;
  classification: string;
};

/** Export the rows behind a report — all of them, those one measure counted, or one group's — as one CSV in the drill-down's order, with every column the person may see on every row; a restricted column is left out of the whole file and named on the export receipt. One export receipt per file; no approval. */
export const reportExport = (input: ReportExportInput = {} as ReportExportInput) => callTool<ReportExportOutput>("report.export", input);

export type ReportOptionsInput = {
  family?: string | null;
};

export type DimensionOut = {
  key: string;
  label_key: string;
  words: string;
  subject: string;
  op: "in" | "range" | "bool";
  multi: boolean;
  groupable: boolean;
  sortable: boolean;
  period: boolean;
  figure?: string | null;
  default?: string | null;
  values?: ValueOut[] | null;
};

export type FamilyOut = {
  key: string;
  label_key: string;
};

export type MeasureDecl = {
  key: string;
  label_key: string;
  unit: "count" | "days" | "percent";
  status: "available" | "not_yet_available";
  producer?: string | null;
};

export type UnavailableOut = {
  key: string;
  label_key: string;
  words: string;
  producers: string[];
};

export type ValueOut = {
  value: string;
  label?: string | null;
};

export type ReportOptionsOutput = {
  families: FamilyOut[];
  family?: string | null;
  scope_words: string[];
  measures?: MeasureDecl[];
  dimensions?: DimensionOut[];
  unavailable?: UnavailableOut[];
  breakdowns?: string[];
  sorts?: string[];
  period_default: PeriodOut;
  page_size_max: number;
};

/** What a report may be asked for: the report families this person may open, and for one of them its measures, the filter dimensions available now with the values inside the person's own reporting scope, the dimensions still waiting for their column or producer, the groupings and sorts, and the default period. */
export const reportOptions = (input: ReportOptionsInput = {} as ReportOptionsInput) => callTool<ReportOptionsOutput>("report.options", input);

export type ReportPublishModelInput = {
  as_of?: string | null;
};

export type TableOut = {
  name: string;
  rows: number;
  columns: string[];
};

export type ReportPublishModelOutput = {
  dataset: string;
  schema_version: string;
  as_of: string;
  rows: number;
  tables: TableOut[];
  sha256: string;
  correlation_id: string;
  ref: string;
  published_at: string;
  simulated: boolean;
  state: "queued" | "running" | "completed" | "failed" | null;
  adapter: string;
  export_receipt_id: string;
  classification: string;
  receipt_id?: number | null;
};

/** Publish the reporting model — every requisition's facts and the dimensions they are read by, as of the end of a day, with no compensation field and no confidential title — to the organisation's BI service, under one correlation id per version; one export receipt per publish. */
export const reportPublishModel = (input: ReportPublishModelInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ReportPublishModelOutput>("report.publish_model", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ReportRunInput = {
  family: string;
  period?: Period | null;
  filters?: Record<string, string[] | Bounds | boolean>;
  group_by?: string | null;
  sort?: Sort | null;
};

export type GroupOut = {
  value: string;
  label?: string | null;
  count: number;
  measures: MeasureOut[];
};

export type MeasureOut = {
  key: string;
  label_key: string;
  unit: "count" | "days" | "percent";
  status: "available" | "not_yet_available";
  value?: number | number | null;
  detail?: Record<string, number | number | null>;
  producer?: string | null;
};

export type ReportRunOutput = {
  family: string;
  label_key: string;
  period: PeriodOut;
  as_of: string;
  scope_words: string[];
  filters: string[];
  group_by?: string | null;
  count: number;
  measures: MeasureOut[];
  groups?: GroupOut[];
};

/** Run a report family over the person's reporting scope: its measures for the filter spec and period (and each group's when the spec groups). A measure whose producer has not merged says not yet available, never 0. */
export const reportRun = (input: ReportRunInput = {} as ReportRunInput) => callTool<ReportRunOutput>("report.run", input);

export type RequestAnswerInput = {
  request_id: string;
  answer: string;
};

export type RequestAnswerOutput = {
  request_id: string;
  status: string;
  revision_no: number;
  requisition_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Answer the HR Business Partner's question; the decision clock resumes with its remaining time. */
export const requestAnswer = (input: RequestAnswerInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestAnswerOutput>("request.answer", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestApproveInput = {
  request_id: string;
  comment?: string | null;
  final_posting_validated?: boolean | null;
  payfactors_updated?: boolean | null;
  review_token: string;
  acknowledged?: string[];
};

export type RequestApproveOutput = {
  request_id: string;
  status: string;
  revision_no: number;
  requisition_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Approve the request: level, reason, openings and range freeze; the request goes to the Talent Acquisition queue. */
export const requestApprove = (input: RequestApproveInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestApproveOutput>("request.approve", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestArchiveInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequestArchiveOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** G-55: a withdrawn request archives read-only after 30 days. */
export const requestArchive = (input: RequestArchiveInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestArchiveOutput>("request.archive", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestAskInput = {
  request_id: string;
  question: string;
};

export type RequestAskOutput = {
  request_id: string;
  status: string;
  revision_no: number;
  requisition_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Ask the requester a question; the decision clock pauses until they answer (B-12). */
export const requestAsk = (input: RequestAskInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestAskOutput>("request.ask", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestAssignReviewerInput = {
  request_id: string;
  reviewer_id: string;
};

export type RequestAssignReviewerOutput = {
  request_id: string;
  status: string;
  revision_no: number;
  requisition_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Admin names the reviewing HR Business Partner for a submitted request that has none (or reassigns one that lapsed). */
export const requestAssignReviewer = (input: RequestAssignReviewerInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestAssignReviewerOutput>("request.assign_reviewer", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type CorrectionFields = {
  title?: string | null;
  level?: "manager_and_below" | "director_srdirector" | "vp_and_above" | null;
  department?: string | null;
  business_unit?: string | null;
  brands?: string[] | null;
  reports_to_title?: string | null;
  locations?: string[] | null;
  reason?: "new_headcount" | "backfill" | null;
  budget_status?: "funded" | "pending" | "unfunded" | null;
  position_no?: string | null;
  vacating_person_ref?: string | null;
  vacating_last_day?: string | null;
  level_and_title_unchanged?: boolean | null;
  openings_requested?: number | null;
  justification?: string | null;
  target_start?: string | null;
  urgency?: "normal" | "urgent" | null;
  posting_scope?: "internal_only" | "external" | "confidential" | null;
  target_comp_min?: number | string | null;
  target_comp_max?: number | string | null;
  currency?: string | null;
  is_people_manager?: boolean | null;
  direct_reports?: number | null;
  notes?: string | null;
  job_family?: string | null;
  area_of_interest?: string | null;
  job_code?: string | null;
  org_alignment?: string | null;
  cost_center?: string | null;
};

export type RequestCertifyInput = {
  request_id: string;
  expected_version?: number | null;
  corrections?: CorrectionFields | null;
  reason?: string | null;
  job_description?: string | null;
};

export type RequestCertifyOutput = {
  request_id: string;
  status: string;
  revision_no: number;
  requisition_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** The Hiring Manager certifies the complete request and job description, with any corrections and their reason; the request goes to the HR Business Partner for approval. */
export const requestCertify = (input: RequestCertifyInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestCertifyOutput>("request.certify", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestClaimReviewInput = {
  request_id: string;
};

export type RequestClaimReviewOutput = {
  request_id: string;
  status: string;
  revision_no: number;
  requisition_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Claim an unassigned submitted request as its reviewing HR Business Partner (the first to open it). */
export const requestClaimReview = (input: RequestClaimReviewInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestClaimReviewOutput>("request.claim_review", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestCreateInput = {
  title?: string | null;
  level?: "manager_and_below" | "director_srdirector" | "vp_and_above" | null;
  department?: string | null;
  business_unit?: string | null;
  brands?: string[] | null;
  reports_to_title?: string | null;
  locations?: string[] | null;
  reason?: "new_headcount" | "backfill" | null;
  budget_status?: "funded" | "pending" | "unfunded" | null;
  position_no?: string | null;
  vacating_person_ref?: string | null;
  vacating_last_day?: string | null;
  level_and_title_unchanged?: boolean | null;
  openings_requested?: number | null;
  justification?: string | null;
  target_comp_min?: number | string | null;
  target_comp_max?: number | string | null;
  currency?: string | null;
  target_start?: string | null;
  urgency?: "normal" | "urgent" | null;
  posting_scope?: "internal_only" | "external" | "confidential" | null;
  reviewing_hrbp_id?: string | null;
  reviewer_override_reason?: string | null;
  is_people_manager?: boolean | null;
  direct_reports?: number | null;
  notes?: string | null;
  job_family?: string | null;
  area_of_interest?: string | null;
  job_code?: string | null;
  org_alignment?: string | null;
  cost_center?: string | null;
  requester_id?: string | null;
  hiring_manager_id?: string | null;
  job_description?: string | null;
};

export type RequestCreateEventOut = {
  id: string;
  kind: string;
  at: string;
  by_id: string | null;
  by_name: string | null;
  comment?: string | null;
  question?: string | null;
  answer?: string | null;
  fields_flagged?: string[] | null;
  decision?: string | null;
  revision_no?: number | null;
};

export type RequestCreateOutput = {
  approval_label?: string | null;
  id: string;
  status: string;
  revision_no: number;
  requester_id: string;
  requester_name: string;
  reviewing_hrbp_id: string | null;
  reviewing_hrbp_name: string | null;
  routing_recruiter_id: string | null;
  routing_recruiter_name: string | null;
  title: string;
  level: string;
  level_label: string;
  department: string;
  business_unit: string;
  brands: string[];
  reports_to_title: string;
  locations: string[];
  reason: string;
  budget_status: string;
  position_no: string | null;
  vacating_person_ref: string | null;
  vacating_last_day: string | null;
  hris_verified: boolean;
  level_and_title_unchanged: boolean | null;
  openings_requested: number;
  justification: string | null;
  target_comp_min?: string | null;
  target_comp_max?: string | null;
  currency: string;
  target_start: string | null;
  urgency: string;
  posting_scope: string;
  confidential: boolean;
  converted_requisition_id: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  events: RequestCreateEventOut[];
  missing_for_submit: string[];
  can: Record<string, boolean>;
  origin?: string;
  row_version?: number;
  intended_hm_id?: string | null;
  intended_hm_name?: string | null;
  is_people_manager?: boolean | null;
  direct_reports?: number | null;
  notes?: string | null;
  job_family?: string | null;
  area_of_interest?: string | null;
  job_code?: string | null;
  org_alignment?: string | null;
  cost_center?: string | null;
  final_posting_validated?: boolean | null;
  job_description?: string | null;
  jd_versions?: Record<string, unknown>[];
  field_versions?: Record<string, unknown>[];
  journey?: Record<string, unknown>[];
  receipt_id?: number | null;
};

/** The HR Business Partner creates the request draft for a Hiring Manager (Admin names the originating HR Business Partner). */
export const requestCreate = (input: RequestCreateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestCreateOutput>("request.create", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestDraftOptionsInput = {
  department?: string | null;
  requester_id?: string | null;
};

export type DraftRequester = {
  person_id: string;
  name: string;
};

export type RequestDraftOptionsOutput = {
  requesters: DraftRequester[];
  reviewers: DraftRequester[];
  default_reviewer_id: string | null;
};

/** Hiring Manager choices (`requesters`), eligible HR Business Partners and the department's default HR Business Partner for the HR Business Partner's request form. Names and identifiers only. */
export const requestDraftOptions = (input: RequestDraftOptionsInput = {} as RequestDraftOptionsInput) => callTool<RequestDraftOptionsOutput>("request.draft_options", input);

export type RequestListInput = {
  page_size?: number;
};

export type RequestQueueRow = {
  id: string;
  status: string;
  title: string;
  level: string;
  level_label: string;
  department: string;
  business_unit: string;
  requester_id: string;
  requester_name: string;
  reviewing_hrbp_id: string | null;
  routing_recruiter_id: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  urgency: string;
  revision_no: number;
  clock: Record<string, unknown> | null;
  converted_requisition_id?: string | null;
};

export type RequestSection = {
  key: string;
  label: string;
  seat: string;
  total: number;
  items: RequestQueueRow[];
};

export type RequestListOutput = {
  sections: RequestSection[];
};

/** The requests I have a seat on, sectioned by my seats (Draft, Pending HM Certification, Pending HRBP Approval, Approved, Dormant; the Coordinator's Req Creation Pending). */
export const requestList = (input: RequestListInput = {} as RequestListInput) => callTool<RequestListOutput>("request.list", input);

export type RequestListMineInput = {
  status?: string | null;
  page?: number;
  page_size?: number;
};

export type RequestListMineOutput = {
  items: RequestQueueRow[];
  total: number;
  page: number;
  page_size: number;
};

/** My requests by status, with the decision clock: the ones I originated, and the ones routed to me as their Hiring Manager. */
export const requestListMine = (input: RequestListMineInput = {} as RequestListMineInput) => callTool<RequestListMineOutput>("request.list_mine", input);

export type RequestMarkDormantInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequestMarkDormantOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** B-13 lapse: a returned request untouched for three business days goes dormant (kept, revivable). */
export const requestMarkDormant = (input: RequestMarkDormantInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestMarkDormantOutput>("request.mark_dormant", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestNotifyDecisionLateInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequestNotifyDecisionLateOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** B-11 lapse: the reviewer's card turns overdue, the requester learns the decision is late and the TA Manager pool gets one escalation; never auto-decided. */
export const requestNotifyDecisionLate = (input: RequestNotifyDecisionLateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestNotifyDecisionLateOutput>("request.notify_decision_late", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestNotifyNotOpenedInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequestNotifyNotOpenedOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** B-14 lapse: an approved request has not become a requisition within one business day; the Coordinator (or their pool) is told and the TA Manager pool gets one escalation. */
export const requestNotifyNotOpened = (input: RequestNotifyNotOpenedInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestNotifyNotOpenedOutput>("request.notify_not_opened", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestNotifyQuestionUnansweredInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequestNotifyQuestionUnansweredOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** B-12 lapse: the reviewer decides on what they have; the clock resumes. */
export const requestNotifyQuestionUnanswered = (input: RequestNotifyQuestionUnansweredInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestNotifyQuestionUnansweredOutput>("request.notify_question_unanswered", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestOpenInput = {
  request_id: string;
};

export type RequestOpenEventOut = {
  id: string;
  kind: string;
  at: string;
  by_id: string | null;
  by_name: string | null;
  comment?: string | null;
  question?: string | null;
  answer?: string | null;
  fields_flagged?: string[] | null;
  decision?: string | null;
  revision_no?: number | null;
};

export type RequestOpenOutput = {
  approval_label?: string | null;
  id: string;
  status: string;
  revision_no: number;
  requester_id: string;
  requester_name: string;
  reviewing_hrbp_id: string | null;
  reviewing_hrbp_name: string | null;
  routing_recruiter_id: string | null;
  routing_recruiter_name: string | null;
  title: string;
  level: string;
  level_label: string;
  department: string;
  business_unit: string;
  brands: string[];
  reports_to_title: string;
  locations: string[];
  reason: string;
  budget_status: string;
  position_no: string | null;
  vacating_person_ref: string | null;
  vacating_last_day: string | null;
  hris_verified: boolean;
  level_and_title_unchanged: boolean | null;
  openings_requested: number;
  justification: string | null;
  target_comp_min?: string | null;
  target_comp_max?: string | null;
  currency: string;
  target_start: string | null;
  urgency: string;
  posting_scope: string;
  confidential: boolean;
  converted_requisition_id: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  events: RequestOpenEventOut[];
  missing_for_submit: string[];
  can: Record<string, boolean>;
  origin?: string;
  row_version?: number;
  intended_hm_id?: string | null;
  intended_hm_name?: string | null;
  is_people_manager?: boolean | null;
  direct_reports?: number | null;
  notes?: string | null;
  job_family?: string | null;
  area_of_interest?: string | null;
  job_code?: string | null;
  org_alignment?: string | null;
  cost_center?: string | null;
  final_posting_validated?: boolean | null;
  job_description?: string | null;
  jd_versions?: Record<string, unknown>[];
  field_versions?: Record<string, unknown>[];
  journey?: Record<string, unknown>[];
};

/** Open one request: P-RQ.full for its seats, P-RQ.queue for a queue reader. */
export const requestOpen = (input: RequestOpenInput = {} as RequestOpenInput) => callTool<RequestOpenOutput>("request.open", input);

export type RequestQueueHrbpInput = {
  status?: string | null;
  page?: number;
  page_size?: number;
};

export type RequestQueueHrbpOutput = {
  items: RequestQueueRow[];
  total: number;
  page: number;
  page_size: number;
};

/** The HR Business Partner's queue: my drafts · waiting on the Hiring Manager's certification · to decide · decided; plus unassigned requests to claim. */
export const requestQueueHrbp = (input: RequestQueueHrbpInput = {} as RequestQueueHrbpInput) => callTool<RequestQueueHrbpOutput>("request.queue_hrbp", input);

export type RequestQueueTaInput = {
  status?: string | null;
  page?: number;
  page_size?: number;
  unassigned_only?: boolean;
};

export type RequestQueueTaOutput = {
  items: RequestQueueRow[];
  total: number;
  page: number;
  page_size: number;
};

/** Admin's Talent Acquisition queue: requests without a reviewer and approved requests awaiting a Recruiter. */
export const requestQueueTa = (input: RequestQueueTaInput = {} as RequestQueueTaInput) => callTool<RequestQueueTaOutput>("request.queue_ta", input);

export type RequestRecordAfterTheFactInput = {
  operation: string;
  payload: Record<string, unknown>;
  for_person: string;
  source: "paper" | "verbal" | "call" | "mail" | "backfill";
  effective_at: string;
  source_file_id?: string | null;
  evidence_note?: string | null;
  request_id?: string | null;
};

export type RequestRecordAfterTheFactOutput = {
  receipt_id?: number | null;
  operation: string;
  result: Record<string, unknown>;
  author_id: string;
  entered_by_id: string;
  source: string;
  source_file_id: string;
  effective_at: string;
};

/** Record an earlier request or decision with the eligible author and mandatory stored evidence. */
export const requestRecordAfterTheFact = (input: RequestRecordAfterTheFactInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestRecordAfterTheFactOutput>("request.record_after_the_fact", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestRemindReviewerInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequestRemindReviewerOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** B-11 half tier: the reviewer is reminded at half the active decision window; their card is bumped. */
export const requestRemindReviewer = (input: RequestRemindReviewerInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestRemindReviewerOutput>("request.remind_reviewer", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestReturnInput = {
  request_id: string;
  fields_flagged: string[];
  note: string;
  to?: "draft" | "dormant";
};

export type RequestReturnOutput = {
  request_id: string;
  status: string;
  revision_no: number;
  requisition_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Return the request for changes to Draft or Dormant: the reviewing HR Business Partner while it waits on approval, the Hiring Manager while it waits on certification. History is kept. */
export const requestReturn = (input: RequestReturnInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestReturnOutput>("request.return", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestReviewInput = {
  request_id: string;
};

export type RequestReviewEventOut = {
  id: string;
  kind: string;
  at: string;
  by_id: string | null;
  by_name: string | null;
  comment?: string | null;
  question?: string | null;
  answer?: string | null;
  fields_flagged?: string[] | null;
  decision?: string | null;
  revision_no?: number | null;
};

export type RequestFull = {
  approval_label?: string | null;
  id: string;
  status: string;
  revision_no: number;
  requester_id: string;
  requester_name: string;
  reviewing_hrbp_id: string | null;
  reviewing_hrbp_name: string | null;
  routing_recruiter_id: string | null;
  routing_recruiter_name: string | null;
  title: string;
  level: string;
  level_label: string;
  department: string;
  business_unit: string;
  brands: string[];
  reports_to_title: string;
  locations: string[];
  reason: string;
  budget_status: string;
  position_no: string | null;
  vacating_person_ref: string | null;
  vacating_last_day: string | null;
  hris_verified: boolean;
  level_and_title_unchanged: boolean | null;
  openings_requested: number;
  justification: string | null;
  target_comp_min?: string | null;
  target_comp_max?: string | null;
  currency: string;
  target_start: string | null;
  urgency: string;
  posting_scope: string;
  confidential: boolean;
  converted_requisition_id: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  events: RequestReviewEventOut[];
  missing_for_submit: string[];
  can: Record<string, boolean>;
  origin?: string;
  row_version?: number;
  intended_hm_id?: string | null;
  intended_hm_name?: string | null;
  is_people_manager?: boolean | null;
  direct_reports?: number | null;
  notes?: string | null;
  job_family?: string | null;
  area_of_interest?: string | null;
  job_code?: string | null;
  org_alignment?: string | null;
  cost_center?: string | null;
  final_posting_validated?: boolean | null;
  job_description?: string | null;
  jd_versions?: Record<string, unknown>[];
  field_versions?: Record<string, unknown>[];
  journey?: Record<string, unknown>[];
};

export type RequestReviewOutput = {
  request: RequestFull;
  checks: Record<string, unknown>[];
  context: Record<string, unknown>;
  review_token: string;
  needs_acknowledgment: string[];
};

/** The review screen: the request, the six computed checks with evidence, and context. */
export const requestReview = (input: RequestReviewInput = {} as RequestReviewInput) => callTool<RequestReviewOutput>("request.review", input);

export type RequestSubmitInput = {
  request_id: string;
};

export type RequestSubmitOutput = {
  request_id: string;
  status: string;
  revision_no: number;
  requisition_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Route the request to the Hiring Manager for certification ("Notify HM when certification is required"); a resubmission after a return starts a new revision. */
export const requestSubmit = (input: RequestSubmitInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestSubmitOutput>("request.submit", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestUpdateInput = {
  title?: string | null;
  level?: "manager_and_below" | "director_srdirector" | "vp_and_above" | null;
  department?: string | null;
  business_unit?: string | null;
  brands?: string[] | null;
  reports_to_title?: string | null;
  locations?: string[] | null;
  reason?: "new_headcount" | "backfill" | null;
  budget_status?: "funded" | "pending" | "unfunded" | null;
  position_no?: string | null;
  vacating_person_ref?: string | null;
  vacating_last_day?: string | null;
  level_and_title_unchanged?: boolean | null;
  openings_requested?: number | null;
  justification?: string | null;
  target_comp_min?: number | string | null;
  target_comp_max?: number | string | null;
  currency?: string | null;
  target_start?: string | null;
  urgency?: "normal" | "urgent" | null;
  posting_scope?: "internal_only" | "external" | "confidential" | null;
  reviewing_hrbp_id?: string | null;
  reviewer_override_reason?: string | null;
  is_people_manager?: boolean | null;
  direct_reports?: number | null;
  notes?: string | null;
  job_family?: string | null;
  area_of_interest?: string | null;
  job_code?: string | null;
  org_alignment?: string | null;
  cost_center?: string | null;
  request_id: string;
  hiring_manager_id?: string | null;
  job_description?: string | null;
  job_description_reason?: string | null;
  change_reason?: string | null;
  expected_version?: number | null;
};

export type RequestUpdateEventOut = {
  id: string;
  kind: string;
  at: string;
  by_id: string | null;
  by_name: string | null;
  comment?: string | null;
  question?: string | null;
  answer?: string | null;
  fields_flagged?: string[] | null;
  decision?: string | null;
  revision_no?: number | null;
};

export type RequestUpdateOutput = {
  approval_label?: string | null;
  id: string;
  status: string;
  revision_no: number;
  requester_id: string;
  requester_name: string;
  reviewing_hrbp_id: string | null;
  reviewing_hrbp_name: string | null;
  routing_recruiter_id: string | null;
  routing_recruiter_name: string | null;
  title: string;
  level: string;
  level_label: string;
  department: string;
  business_unit: string;
  brands: string[];
  reports_to_title: string;
  locations: string[];
  reason: string;
  budget_status: string;
  position_no: string | null;
  vacating_person_ref: string | null;
  vacating_last_day: string | null;
  hris_verified: boolean;
  level_and_title_unchanged: boolean | null;
  openings_requested: number;
  justification: string | null;
  target_comp_min?: string | null;
  target_comp_max?: string | null;
  currency: string;
  target_start: string | null;
  urgency: string;
  posting_scope: string;
  confidential: boolean;
  converted_requisition_id: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  events: RequestUpdateEventOut[];
  missing_for_submit: string[];
  can: Record<string, boolean>;
  origin?: string;
  row_version?: number;
  intended_hm_id?: string | null;
  intended_hm_name?: string | null;
  is_people_manager?: boolean | null;
  direct_reports?: number | null;
  notes?: string | null;
  job_family?: string | null;
  area_of_interest?: string | null;
  job_code?: string | null;
  org_alignment?: string | null;
  cost_center?: string | null;
  final_posting_validated?: boolean | null;
  job_description?: string | null;
  jd_versions?: Record<string, unknown>[];
  field_versions?: Record<string, unknown>[];
  journey?: Record<string, unknown>[];
  receipt_id?: number | null;
};

/** Save the draft (or a returned or dormant request) — the originating HR Business Partner only; after the first routing every change needs a reason. */
export const requestUpdate = (input: RequestUpdateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestUpdateOutput>("request.update", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequestWithdrawInput = {
  request_id: string;
  reason: string;
};

export type RequestWithdrawOutput = {
  request_id: string;
  status: string;
  revision_no: number;
  requisition_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Withdraw the request (the originating HR Business Partner; from any non-terminal status). Archive, never purge. */
export const requestWithdraw = (input: RequestWithdrawInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequestWithdrawOutput>("request.withdraw", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionAcknowledgeFlagInput = {
  requisition_id: string;
  note: string;
};

export type RequisitionAcknowledgeFlagOutput = {
  requisition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Acknowledge a phase flag with a note; the Recruiter sees it in Waiting → Activity. */
export const requisitionAcknowledgeFlag = (input: RequisitionAcknowledgeFlagInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionAcknowledgeFlagOutput>("requisition.acknowledge_flag", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionApproveReminderWordingInput = {
  requisition_id: string;
};

export type RequisitionApproveReminderWordingOutput = {
  requisition_id: string;
  approved_at: string | null;
  approved_by: string;
  wording: string;
  receipt_id?: number | null;
};

/** Approve the reminder wording once for this requisition (A-31); timer-driven scorecard reminders then send without a further approval. */
export const requisitionApproveReminderWording = (input: RequisitionApproveReminderWordingInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionApproveReminderWordingOutput>("requisition.approve_reminder_wording", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionApproveReopenInput = {
  requisition_id: string;
  reopen_event_id?: string | null;
  state_change_request_id?: string | null;
};

export type RequisitionApproveReopenOutput = {
  requisition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** The named HR Business Partner approves the reopening (the compatibility door of `requisition.decide_state_change`): the reopen request, or the renege's reopen record it is linked to; the requisition returns to open (or partially filled). */
export const requisitionApproveReopen = (input: RequisitionApproveReopenInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionApproveReopenOutput>("requisition.approve_reopen", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionArchiveInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequisitionArchiveOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** G-54: a filled or cancelled requisition the archive policy deferred (an archive blocker, re-checked weekly, or an after-days policy) archives read-only (Law 7). */
export const requisitionArchive = (input: RequisitionArchiveInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionArchiveOutput>("requisition.archive", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionChangeOpeningsInput = {
  requisition_id: string;
  proposal_id: string;
};

export type RequisitionChangeOpeningsOutput = {
  requisition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Approve the proposed headcount change (HR Business Partner; never the proposer). */
export const requisitionChangeOpenings = (input: RequisitionChangeOpeningsInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionChangeOpeningsOutput>("requisition.change_openings", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionCloseInput = {
  requisition_id: string;
  reason: "filled" | "cancelled";
  note?: string | null;
};

export type RequisitionCloseOutput = {
  requisition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Closing is never a person's act any more: a cancellation is requested (`requisition.request_state_change`, kind cancel) and the HR Business Partner approves it, and Filled follows the HRIS hire of every opening and archives under the archive policy. Both reasons are refused with the way forward. */
export const requisitionClose = (input: RequisitionCloseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionCloseOutput>("requisition.close", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionCreateInput = {
  request_id: string;
};

export type RequisitionCreateOutput = {
  request_id: string;
  requisition_id: string;
  status: string;
  recruiter_id?: string | null;
  coordinator_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Create the requisition from an approved request — exactly once; the Recruiter may still be pending. */
export const requisitionCreate = (input: RequisitionCreateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionCreateOutput>("requisition.create", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionDecideStateChangeInput = {
  requisition_id: string;
  state_change_request_id: string;
  expected_version: number;
  decision: "approve" | "reject";
  note?: string | null;
};

export type RequisitionDecideStateChangeOutput = {
  state_change_request_id: string;
  requisition_id: string;
  kind: string;
  status: string;
  requisition_status: string;
  row_version: number;
  requisition_hold_id?: string | null;
  close_event_id?: string | null;
  reopen_event_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** The requisition's HR Business Partner approves or rejects a hold, cancel or reopen request; an approval applies it in the same call (a hold pauses the clocks now and records what paused and what continued; a cancellation needs every candidate dispositioned first; a reopening keeps the earlier closure history); never the requester's own request; the requester and the TA Managers are told. */
export const requisitionDecideStateChange = (input: RequisitionDecideStateChangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionDecideStateChangeOutput>("requisition.decide_state_change", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionDraftPhaseFlagInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequisitionDraftPhaseFlagOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** A-2..A-7 amber, C-18, D-22: draft the phase flag on the Recruiter's Today with the pending action computed. */
export const requisitionDraftPhaseFlag = (input: RequisitionDraftPhaseFlagInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionDraftPhaseFlagOutput>("requisition.draft_phase_flag", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionListInput = {
  status?: string[] | null;
  level?: string | null;
  owner?: "mine" | "anyone";
  department?: string | null;
  brand?: string | null;
  phase_state?: "on_track" | "trending_behind" | "behind" | null;
  phase_report?: boolean;
  q?: string | null;
  page?: number;
  page_size?: number;
  sort?: "elapsed" | "activity" | "id";
};

export type RequisitionListListRow = {
  id: string;
  req_no: string | null;
  title: string;
  level: string;
  level_label: string;
  department: string;
  brands: string[];
  business_unit: string;
  status: string;
  status_label: string;
  owner_id: string | null;
  owner_name: string | null;
  assignment_label?: string | null;
  hiring_manager_id: string | null;
  hiring_manager_name: string | null;
  stage: string;
  phase: PhasePill;
  assignment?: string | null;
  opened_on: string;
  last_activity_at: string;
  confidential: boolean;
  masked: boolean;
  can_open: boolean;
};

export type OperationalListRow = {
  id: string;
  req_no: string | null;
  title: string;
  level: string;
  level_label: string;
  department: string;
  brands: string[];
  business_unit: string;
  status: string;
  status_label: string;
  owner_id: string | null;
  owner_name: string | null;
  assignment_label?: string | null;
  hiring_manager_id: string | null;
  hiring_manager_name: string | null;
  stage: string;
  phase: PhasePill;
  assignment?: string | null;
  opened_on: string;
  last_activity_at: string;
  confidential: boolean;
  masked: boolean;
  can_open: boolean;
  openings: number;
  fills: number;
  panel_assigned: number;
  panel_needed: number;
  waiting_count: number;
  waiting_on: WaitingItem[];
  can_send_phase_flag: boolean;
};

export type PanelistListRow = {
  projection?: "panelist";
  id: string;
  req_no: string | null;
  title: string;
  level: string;
  level_label: string;
  department: string;
  can_open?: boolean;
};

export type PhasePill = {
  phase_key: string | null;
  label: string | null;
  elapsed_days: number | null;
  target_days: number | null;
  amber_at_days: number | null;
  held_days: number;
  state: string;
  calendar_days?: number | null;
  active_days?: number | null;
};

export type PhaseTargetsConfig = {
  version: number;
  effective_from: string;
  levels: Record<string, Record<string, number>>;
};

export type WaitingItem = {
  id: string;
  title: string;
  due_at: string | null;
};

export type RequisitionListOutput = {
  items: (OperationalListRow | RequisitionListListRow | PanelistListRow)[];
  total: number;
  page: number;
  page_size: number;
  counts: Record<string, number>;
  phase_targets: PhaseTargetsConfig | null;
  phase_counts: Record<string, number>;
};

/** Every requisition in the actor's scope with owner, stage and phase pill (Recruiters see all; others where seated; an Interviewer their assigned active ones). */
export const requisitionList = (input: RequisitionListInput = {} as RequisitionListInput) => callTool<RequisitionListOutput>("requisition.list", input);

export type RequisitionNoteDelayInput = {
  requisition_id: string;
  cause?: "candidate" | "other";
  days: number;
  note: string;
  application_id?: string | null;
};

export type RequisitionNoteDelayOutput = {
  requisition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Note a candidate-caused delay beside the running phase; the clock never pauses (A-32). */
export const requisitionNoteDelay = (input: RequisitionNoteDelayInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionNoteDelayOutput>("requisition.note_delay", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionNotifyBatchUnsentInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequisitionNotifyBatchUnsentOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** D-23: a qualified candidate has waited a business day without a batch. */
export const requisitionNotifyBatchUnsent = (input: RequisitionNotifyBatchUnsentInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionNotifyBatchUnsentOutput>("requisition.notify_batch_unsent", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionNotifyNotPostedInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequisitionNotifyNotPostedOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** C-15: open, not posted after one business day. */
export const requisitionNotifyNotPosted = (input: RequisitionNotifyNotPostedInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionNotifyNotPostedOutput>("requisition.notify_not_posted", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionNotifyPhaseBehindInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequisitionNotifyPhaseBehindOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** A-2..A-8 crossed: one automatic in-app notice to Recruiter, Hiring Manager and HR Business Partner; Behind everywhere. */
export const requisitionNotifyPhaseBehind = (input: RequisitionNotifyPhaseBehindInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionNotifyPhaseBehindOutput>("requisition.notify_phase_behind", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionNotifyTargetStartAtRiskInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequisitionNotifyTargetStartAtRiskOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** A-10: notify the owning Recruiter when the target start is at risk, once for that target date. */
export const requisitionNotifyTargetStartAtRisk = (input: RequisitionNotifyTargetStartAtRiskInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionNotifyTargetStartAtRiskOutput>("requisition.notify_target_start_at_risk", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionNotifyTtaCrossingInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequisitionNotifyTtaCrossingOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** A-1: the Time to Acceptance target crossed — one notice, never repeated, never blocks. */
export const requisitionNotifyTtaCrossing = (input: RequisitionNotifyTtaCrossingInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionNotifyTtaCrossingOutput>("requisition.notify_tta_crossing", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionNotifyTtfCrossingInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequisitionNotifyTtfCrossingOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** Retained for timers armed before the Time to Acceptance rename; the same A-1 crossing notice. */
export const requisitionNotifyTtfCrossing = (input: RequisitionNotifyTtfCrossingInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionNotifyTtfCrossingOutput>("requisition.notify_ttf_crossing", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionOpenInput = {
  requisition_id: string;
};

export type ArchiveRef = {
  snapshot_id: string;
  archived_at: string;
  content_hash: string;
};

export type CheckpointOut = {
  name: string;
  label: string;
  occurred_at: string | null;
  derived_from_tool: string | null;
  state: "done" | "current" | "future";
};

export type RequisitionOpenClockOut = {
  phase_key: string;
  label: string;
  target_days: number;
  amber_at_days: number | null;
  started_at: string | null;
  stopped_at: string | null;
  elapsed_days: number;
  held_days: number;
  state: string;
  calendar_days?: number;
  active_days?: number;
};

export type MilestoneOut = {
  key: string;
  track: string;
  label: string | null;
  status: "reached" | "ended" | "reversed";
  at: string;
  event_tz: string;
  actor_name: string | null;
  source_tool: string;
  opening_index: number | null;
};

export type PanelistKickoff = {
  subject: string;
  body: string | null;
  sent_at: string | null;
};

export type PanelistRound = {
  round_def_id: string;
  label: string;
  kind: string;
  format: string;
  duration_minutes: number;
  values: string[];
  skills: Record<string, unknown>[];
  seat: string;
  packet_id: string | null;
};

export type PendingReopen = {
  id: string;
  kind: string;
  reason: string;
  requested_by_id: string;
  requested_by_name: string;
  requested_at: string;
  state_change_request_id?: string | null;
  reopen_event_id?: string | null;
};

export type PostingStrategyOut = {
  id: string;
  version_no: number;
  track: string;
  strategy: string | null;
  strategy_config_version_id?: string | null;
  strategy_label?: string | null;
  proposed: boolean;
  posting_channels: string[] | null;
  posting_url: string | null;
  req_no: string | null;
  reason: string | null;
  author_id: string;
  effective_at: string;
};

export type PostingStrategyView = {
  current: PostingStrategyOut | null;
  history: PostingStrategyOut[];
};

export type RequisitionFull = {
  state_archive?: string;
  archived_at?: string | null;
  read_only?: boolean;
  archive?: ArchiveRef | null;
  id: string;
  req_no: string | null;
  req_no_verified: boolean;
  request_id: string;
  title: string;
  level: string;
  level_label: string;
  department: string;
  business_unit: string;
  brands: string[];
  reports_to_title: string;
  locations: string[];
  reason: string;
  posting_scope: string;
  confidential: boolean;
  status: string;
  status_label: string;
  stage: string;
  held_from_status: string | null;
  openings_approved: number;
  offer_commitments: number;
  fills: number;
  hiring_manager_id: string;
  hiring_manager_name: string;
  hrbp_id: string;
  hrbp_name: string;
  recruiter_id: string | null;
  recruiter_name: string | null;
  assignment_status: string;
  assignment_label: string;
  coordinator_id?: string | null;
  coordinator_name?: string | null;
  original_posting_type?: string | null;
  original_posting_type_label?: string | null;
  posting_strategy?: PostingStrategyView | null;
  compensation_review_status?: string | null;
  row_version?: number;
  comp_min?: string | null;
  comp_max?: string | null;
  currency?: string | null;
  bonus_structure?: string | null;
  comp_approval_notes?: string | null;
  target_start: unknown;
  urgency?: string;
  position_no?: string | null;
  posted_at: string | null;
  posting_channels: string[] | null;
  posting_url: string | null;
  guide_version: number;
  pinned_config: Record<string, unknown>;
  checkpoints: CheckpointOut[];
  clocks: RequisitionOpenClockOut[];
  time_clocks?: TimeClock[];
  milestones?: Record<string, MilestoneOut | null>;
  milestone_history?: MilestoneOut[];
  pipeline_counts: Record<string, number>;
  panel_assigned: number;
  panel_needed: number;
  interview_formats: string[];
  created_at: string;
  updated_at: string;
  pending_reopen?: PendingReopen | null;
  pending_state_change?: StateChangeView | null;
  state_change_requests?: StateChangeView[];
  undispositioned_count?: number | null;
  can: Record<string, boolean>;
};

export type RequisitionPanelist = {
  projection?: "panelist";
  id: string;
  req_no: string | null;
  title: string;
  level: string;
  level_label: string;
  department: string;
  can_open?: boolean;
  state_archive: string;
  archived_at: string | null;
  reports_to_title: string;
  guide_version: number;
  my_rounds: PanelistRound[];
  kickoff: PanelistKickoff[];
  toolkit_url?: string | null;
};

export type StateChangeView = {
  id: string;
  kind: string;
  status: string;
  reason_slug?: string | null;
  reason_label?: string | null;
  reason_text: string;
  requested_by_id: string;
  requested_by_name?: string | null;
  requested_seat: string;
  requested_at: string;
  decided_by_id?: string | null;
  decided_at?: string | null;
  decision_note?: string | null;
  decided_by_name?: string | null;
  applied_at?: string | null;
  final_notes?: string | null;
  reporting_category?: string | null;
  reporting_category_label?: string | null;
  ta_manager_notified_at?: string | null;
  held_at?: string | null;
  resumed_at?: string | null;
  paused_activities?: string[];
  continued_activities?: string[];
  row_version: number;
};

export type TimeClock = {
  clock_key: string;
  label: string;
  opening_index: number | null;
  started_at: string | null;
  stopped_at: string | null;
  calendar_days: number | null;
  active_days: number | null;
  hold_days: number | null;
  target_days: number | null;
  state: string;
  available: boolean;
  unavailable_reason: string | null;
};

export type RequisitionOpenOutput = RequisitionFull | RequisitionPanelist;

/** The requisition overview: operational header, seats, capacity and clocks (P-REQ.full); for an assigned Interviewer, their rounds and kickoff mail (P-REQ.panelist). */
export const requisitionOpen = (input: RequisitionOpenInput = {} as RequisitionOpenInput) => callTool<RequisitionOpenOutput>("requisition.open", input);

export type RequisitionPostInput = {
  requisition_id: string;
  req_no: string;
  posting_channels?: ("internal" | "external")[];
  posting_url?: string | null;
  posted_at?: string | null;
  source_file_id?: string | null;
  evidence_note?: string | null;
};

export type RequisitionPostOutput = {
  requisition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Record the posting: draft → open, the Posting checkpoint, the first phase clock and Time to Acceptance start. */
export const requisitionPost = (input: RequisitionPostInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionPostOutput>("requisition.post", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionPostUpdateInput = {
  requisition_id: string;
  expected_version: number;
  posting_channels?: ("internal" | "external")[] | null;
  posting_url?: string | null;
  req_no?: string | null;
  reason?: string | null;
};

export type RequisitionPostUpdateOutput = {
  requisition_id: string;
  version_no: number;
  track: string;
  strategy?: string | null;
  strategy_config_version_id?: string | null;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Update the posting after it went up (channels, URL, REQ #), with the reason: a new posting version. */
export const requisitionPostUpdate = (input: RequisitionPostUpdateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionPostUpdateOutput>("requisition.post_update", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionProposeOpeningsInput = {
  requisition_id: string;
  to_count: number;
  reason: string;
};

export type RequisitionProposeOpeningsOutput = {
  requisition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Propose a headcount change; the named HR Business Partner approves (never the same person). */
export const requisitionProposeOpenings = (input: RequisitionProposeOpeningsInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionProposeOpeningsOutput>("requisition.propose_openings", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionRaisePhaseFlagInput = {
  requisition_id: string;
  pending_action: string;
  message?: string | null;
};

export type RequisitionRaisePhaseFlagOutput = {
  requisition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Send the phase flag (Guide p.10) to the Hiring Manager and the HR Business Partner naming the pending action; consumes the drafted flag. */
export const requisitionRaisePhaseFlag = (input: RequisitionRaisePhaseFlagInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionRaisePhaseFlagOutput>("requisition.raise_phase_flag", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionRecordAfterTheFactInput = {
  operation: string;
  payload: Record<string, unknown>;
  for_person: string;
  source: "paper" | "verbal" | "call" | "mail" | "backfill";
  effective_at: string;
  source_file_id?: string | null;
  evidence_note?: string | null;
  requisition_id: string;
};

export type RequisitionRecordAfterTheFactOutput = {
  receipt_id?: number | null;
  operation: string;
  result: Record<string, unknown>;
  author_id: string;
  entered_by_id: string;
  source: string;
  source_file_id: string;
  effective_at: string;
};

/** Record an earlier requisition operation with its live fields, eligible author, evidence and effective date. */
export const requisitionRecordAfterTheFact = (input: RequisitionRecordAfterTheFactInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionRecordAfterTheFactOutput>("requisition.record_after_the_fact", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionReopenInput = {
  requisition_id: string;
  kind: string;
  reason: string;
};

export type RequisitionReopenOutput = {
  requisition_id: string;
  status: string;
  effective_at: string;
  state_change_request_id: string;
  reopen_event_id?: string | null;
  from_snapshot_id: string | null;
  from_status: string;
  receipt_id?: number | null;
};

/** Ask to reopen a closed or archived requisition (the compatibility door of `requisition.request_state_change`, kind reopen): the owner asks, the named HR Business Partner approves; guide version stays pinned; clocks keep their baseline. */
export const requisitionReopen = (input: RequisitionReopenInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionReopenOutput>("requisition.reopen", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionRepeatPhaseFlagInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RequisitionRepeatPhaseFlagOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** F-49: the flag repeated once to whoever has not acknowledged, then reported unacknowledged. */
export const requisitionRepeatPhaseFlag = (input: RequisitionRepeatPhaseFlagInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionRepeatPhaseFlagOutput>("requisition.repeat_phase_flag", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionRequestStateChangeInput = {
  requisition_id: string;
  kind: "hold" | "cancel" | "reopen";
  reason_slug?: string | null;
  reason_text: string;
  final_notes?: string | null;
  reporting_category?: string | null;
};

export type RequisitionRequestStateChangeOutput = {
  state_change_request_id: string;
  requisition_id: string;
  kind: string;
  status: string;
  requisition_status: string;
  row_version: number;
  requisition_hold_id?: string | null;
  close_event_id?: string | null;
  reopen_event_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** The Hiring Manager or the TA team asks the requisition's HR Business Partner to put the search on hold (a reason from the published list), to cancel it (final notes and a reporting category from the published list) or to reopen a closed or archived search (a reason from the published list), saying why; one request waits at a time; nothing changes until the HR Business Partner approves. */
export const requisitionRequestStateChange = (input: RequisitionRequestStateChangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionRequestStateChangeOutput>("requisition.request_state_change", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionResumeInput = {
  requisition_id: string;
  note?: string | null;
};

export type RequisitionResumeOutput = {
  requisition_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Resume from hold (direct, no approval): held days are written on every open clock; suspended timers re-arm with their remaining time; the hold records what resumed; the TA Managers are told. */
export const requisitionResume = (input: RequisitionResumeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionResumeOutput>("requisition.resume", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionSignalsInput = {
  requisition_ids: string[];
};

export type RequisitionSignalsDriverOut = {
  key: string;
  ordinal: number;
  weight: number;
  value: Record<string, unknown> | null;
  label_key: string | null;
  route: string | null;
};

export type HealthOut = {
  status: "available" | "not_yet_available";
  score?: number | null;
  light?: "green" | "yellow" | "red" | null;
  phase_state?: string | null;
  computed_at?: string | null;
  drivers?: RequisitionSignalsDriverOut[];
  config_versions?: Record<string, unknown>;
};

export type ReadinessOut = {
  status: "available" | "not_yet_available";
  percent?: number | null;
  measured?: number | null;
  total?: number | null;
  prerequisites?: Record<string, unknown>[];
  missing?: Record<string, unknown>[];
  computed_at?: string | null;
  config_versions?: Record<string, unknown>;
};

export type SignalRow = {
  requisition_id: string;
  health: HealthOut;
  readiness: ReadinessOut;
  posting_state: "not_posted" | "posted";
};

export type RequisitionSignalsOutput = {
  rows: SignalRow[];
};

/** Health (score, traffic light and drivers) and Search Readiness (percent and missing prerequisites) for the requisitions you may open, with whether each is posted. Requisitions you may not open are left out. */
export const requisitionSignals = (input: RequisitionSignalsInput = {} as RequisitionSignalsInput) => callTool<RequisitionSignalsOutput>("requisition.signals", input);

export type RequisitionUpdateStrategyInput = {
  requisition_id: string;
  strategy: string;
  reason: string;
  expected_version: number;
};

export type RequisitionUpdateStrategyOutput = {
  requisition_id: string;
  version_no: number;
  track: string;
  strategy?: string | null;
  strategy_config_version_id?: string | null;
  row_version: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Change the posting strategy, with the reason: a new version; the original and earlier ones are kept. */
export const requisitionUpdateStrategy = (input: RequisitionUpdateStrategyInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionUpdateStrategyOutput>("requisition.update_strategy", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RequisitionWithdrawStateChangeInput = {
  requisition_id: string;
  state_change_request_id: string;
  expected_version: number;
};

export type RequisitionWithdrawStateChangeOutput = {
  state_change_request_id: string;
  requisition_id: string;
  kind: string;
  status: string;
  requisition_status: string;
  row_version: number;
  requisition_hold_id?: string | null;
  close_event_id?: string | null;
  reopen_event_id?: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** The person who asked withdraws a hold, cancel or reopen request while it waits on the HR Business Partner. */
export const requisitionWithdrawStateChange = (input: RequisitionWithdrawStateChangeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RequisitionWithdrawStateChangeOutput>("requisition.withdraw_state_change", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundBackfillInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  held_at: string;
  evidence_ref: string;
  source?: "paper" | "verbal";
};

export type RoundBackfillOutput = {
  interview_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** A round that happened outside the application: recorded as held from evidence (paper or verbal), never bypassing the scorecard lock. */
export const roundBackfill = (input: RoundBackfillInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundBackfillOutput>("round.backfill", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundBookInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  start: string;
  location_or_link?: string | null;
  unsynced?: boolean;
  recording_consent?: "not_asked" | "given" | "refused";
};

export type RoundBookOutput = {
  interview_id: string;
  requisition_id: string;
  application_id: string;
  candidate: string;
  round_def_id: string;
  round_kind: string;
  round_label: string;
  format: string;
  duration_minutes: number;
  status: string;
  primary: Record<string, unknown>;
  backup: Record<string, unknown> | null;
  booking: Record<string, unknown> | null;
  held_at: string | null;
  not_held_reason: string | null;
  reschedule_count: number;
  scorecard_status: string | null;
  packet_id: string | null;
  packet_link_token: string | null;
  recording_consent: string;
  touchpoints: Record<string, number>;
  itinerary: string | null;
  can: Record<string, boolean>;
  scheduling_request_id?: string | null;
  held_offline?: boolean;
  offline_reason?: string | null;
  receipt_id?: number | null;
};

/** Confirm the time: the calendar event with the packet link and assignment line, state → scheduled; the Recruiter Screen and HM Round packets are generated and issued in the same call; the itinerary is returned for the Recruiter to send from the ATS. */
export const roundBook = (input: RoundBookInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundBookOutput>("round.book", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundCancelInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  reason: string;
  not_held_reason?: "cancelled_by_recruiter" | "cancelled_by_candidate" | "requisition_hold" | "other";
};

export type RoundCancelOutput = {
  interview_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Cancel a round that will not happen; interviewers and the Hiring Manager are told; the event is cancelled in the calendar. */
export const roundCancel = (input: RoundCancelInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundCancelOutput>("round.cancel", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundCheckTouchpointsInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
};

export type RoundCheckTouchpointsOutput = {
  application_id: string;
  used: number;
  maximum: number;
  rounds: Record<string, unknown>[];
};

/** Touchpoints used against the level's maximum for one candidate. */
export const roundCheckTouchpoints = (input: RoundCheckTouchpointsInput = {} as RoundCheckTouchpointsInput) => callTool<RoundCheckTouchpointsOutput>("round.check_touchpoints", input);

export type RoundCorrectHeldAtInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  held_at: string;
  reason: string;
};

export type RoundCorrectHeldAtOutput = {
  interview_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Correct a wrong held time with a reason; both instants are kept and the clocks recompute. */
export const roundCorrectHeldAt = (input: RoundCorrectHeldAtInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundCorrectHeldAtOutput>("round.correct_held_at", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundExpireHoldInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RoundExpireHoldOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** E-36: a 24-hour tentative hold expires; the calendars are released. */
export const roundExpireHold = (input: RoundExpireHoldInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundExpireHoldOutput>("round.expire_hold", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundFlagDeclineInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RoundFlagDeclineOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** E-37: a declined round has neither a promoted backup nor a new proposal after 24 hours. */
export const roundFlagDecline = (input: RoundFlagDeclineInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundFlagDeclineOutput>("round.flag_decline", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundHoldSlotInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  start: string;
};

export type RoundHoldSlotOutput = {
  interview_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Hold a proposed time for 24 hours on the panel's calendars (tentative), released by booking or by the hold lapse. */
export const roundHoldSlot = (input: RoundHoldSlotInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundHoldSlotOutput>("round.hold_slot", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundMarkHeldInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  held_at?: string | null;
  attendance?: Record<string, unknown>[] | null;
  held_offline?: boolean;
  offline_reason?: string | null;
};

export type RoundMarkHeldOutput = {
  interview_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** The round happened: held_at (default now), attendance; the 24-hour scorecard clock starts for the seat holder; the first screen stamps the First-screen checkpoint. */
export const roundMarkHeld = (input: RoundMarkHeldInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundMarkHeldOutput>("round.mark_held", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundMarkLoopScheduledInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
};

export type RoundMarkLoopScheduledOutput = {
  interview_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Every in-use round is booked for the candidate; the loop-scheduling timer stops. */
export const roundMarkLoopScheduled = (input: RoundMarkLoopScheduledInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundMarkLoopScheduledOutput>("round.mark_loop_scheduled", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundMarkNotHeldInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  reason: "candidate_no_show" | "interviewer_no_show" | "cancelled_by_recruiter" | "cancelled_by_candidate" | "requisition_hold" | "other";
  note?: string | null;
};

export type RoundMarkNotHeldOutput = {
  interview_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Booked and did not happen: a no-show or a cancellation on the day; no scorecard is due; a candidate no-show starts the follow-up prompt. */
export const roundMarkNotHeld = (input: RoundMarkNotHeldInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundMarkNotHeldOutput>("round.mark_not_held", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundMoveInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  reason: string;
  cause: "candidate" | "panel" | "process";
};

export type RoundMoveOutput = {
  interview_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Move a booked round: the booking is cancelled, the event updated, state → rescheduled; the cause (candidate · panel · process) annotates the phase. */
export const roundMove = (input: RoundMoveInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundMoveOutput>("round.move", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundNotifyNoShowInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RoundNotifyNoShowOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** E-40: a candidate no-show without a rebook, pass or withdrawal after a business day. */
export const roundNotifyNoShow = (input: RoundNotifyNoShowInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundNotifyNoShowOutput>("round.notify_no_show", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundNotifySchedulingLateInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RoundNotifySchedulingLateOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** D-26 / D-30 / E-35: a selected candidate has no booked round after the window — the Recruiter is told. */
export const roundNotifySchedulingLate = (input: RoundNotifySchedulingLateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundNotifySchedulingLateOutput>("round.notify_scheduling_late", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundOpenInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
};

export type RoundOpenOutput = {
  interview_id: string;
  requisition_id: string;
  application_id: string;
  candidate: string;
  round_def_id: string;
  round_kind: string;
  round_label: string;
  format: string;
  duration_minutes: number;
  status: string;
  primary: Record<string, unknown>;
  backup: Record<string, unknown> | null;
  booking: Record<string, unknown> | null;
  held_at: string | null;
  not_held_reason: string | null;
  reschedule_count: number;
  scorecard_status: string | null;
  packet_id: string | null;
  packet_link_token: string | null;
  recording_consent: string;
  touchpoints: Record<string, number>;
  itinerary: string | null;
  can: Record<string, boolean>;
  scheduling_request_id?: string | null;
  held_offline?: boolean;
  offline_reason?: string | null;
};

/** One round for one candidate: booking, attendees, held/not-held, scorecard status without ratings, touchpoints; the HR Business Partner sees status only; the Coordinator who operates the requisition sees what scheduling needs. */
export const roundOpen = (input: RoundOpenInput = {} as RoundOpenInput) => callTool<RoundOpenOutput>("round.open", input);

export type RoundPromptCandidateFollowupInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type RoundPromptCandidateFollowupOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** D-27: an invitation logged two business days ago has no reply — the Recruiter follows up in the ATS; a candidate delay note is appended. */
export const roundPromptCandidateFollowup = (input: RoundPromptCandidateFollowupInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundPromptCandidateFollowupOutput>("round.prompt_candidate_followup", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundProposeInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  earliest?: string | null;
  latest?: string | null;
  candidate_windows?: Record<string, unknown>[];
  count?: number;
};

export type Slot = {
  start: string;
  end: string;
  cost_days: number;
  inside_target: boolean;
  attendees_free: boolean;
};

export type RoundProposeOutput = {
  interview_id: string;
  round_label: string;
  duration_minutes: number;
  format: string;
  attendees: Record<string, unknown>[];
  slots: Slot[];
  none_inside_target: boolean;
  phase_key: string | null;
  phase_target_days: number | null;
  phase_elapsed_days: number | null;
  scheduling_request_id?: string | null;
  receipt_id?: number | null;
};

/** Propose times from the panel's free/busy inside the phase window; each slate shows its cost in days against the target. */
export const roundPropose = (input: RoundProposeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundProposeOutput>("round.propose", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundReadFreeBusyInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  window_start: string;
  window_end: string;
};

export type RoundReadFreeBusyOutput = {
  people: string[];
  busy: Record<string, Record<string, unknown>[]>;
};

/** Free/busy of the round's panel members inside a window — never subjects or locations. */
export const roundReadFreeBusy = (input: RoundReadFreeBusyInput = {} as RoundReadFreeBusyInput) => callTool<RoundReadFreeBusyOutput>("round.read_free_busy", input);

export type RoundRejectTimeInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  reason: string;
  cause: "candidate" | "panel" | "process";
};

export type RoundRejectTimeOutput = {
  interview_id: string;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** A panelist rejects the booked time; the Coordinator proposes again. */
export const roundRejectTime = (input: RoundRejectTimeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundRejectTimeOutput>("round.reject_time", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type RoundRescheduleInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  start: string;
  location_or_link?: string | null;
  unsynced?: boolean;
  recording_consent?: "not_asked" | "given" | "refused";
};

export type RoundRescheduleOutput = {
  interview_id: string;
  requisition_id: string;
  application_id: string;
  candidate: string;
  round_def_id: string;
  round_kind: string;
  round_label: string;
  format: string;
  duration_minutes: number;
  status: string;
  primary: Record<string, unknown>;
  backup: Record<string, unknown> | null;
  booking: Record<string, unknown> | null;
  held_at: string | null;
  not_held_reason: string | null;
  reschedule_count: number;
  scorecard_status: string | null;
  packet_id: string | null;
  packet_link_token: string | null;
  recording_consent: string;
  touchpoints: Record<string, number>;
  itinerary: string | null;
  can: Record<string, boolean>;
  scheduling_request_id?: string | null;
  held_offline?: boolean;
  offline_reason?: string | null;
  receipt_id?: number | null;
};

/** Rebook a moved round with a slot from a fresh proposal. */
export const roundReschedule = (input: RoundRescheduleInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<RoundRescheduleOutput>("round.reschedule", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SavedViewDeleteInput = {
  id: string;
  expected_row_version: number;
};

export type SavedViewDeleteOutput = {
  id: string;
  archived_at: string;
  receipt_id?: number | null;
};

/** Remove one of your saved views (the version you read; a stale version is refused). The view is archived, never erased, and its name may be used again. */
export const savedViewDelete = (input: SavedViewDeleteInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SavedViewDeleteOutput>("saved_view.delete", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SavedViewListInput = {
  surface: string;
};

export type SavedViewOut = {
  id: string;
  name: string;
  surface: string;
  spec: Record<string, unknown>;
  row_version: number;
  created_at: string;
  updated_at: string;
  dropped?: string[];
};

export type SavedViewListOutput = {
  surface: string;
  limit: number;
  views: SavedViewOut[];
};

/** Your saved views of one report or list, in name order: each with the filters, grouping and sort that still apply for you now and the ones no longer available (dropped), and the limit of live views per list. A view holds no rows and no scope: applied, it shows what you may see today. */
export const savedViewList = (input: SavedViewListInput = {} as SavedViewListInput) => callTool<SavedViewListOutput>("saved_view.list", input);

export type SavedViewSaveInput = {
  surface: string;
  name: string;
  spec: Record<string, unknown>;
  id?: string | null;
  expected_row_version?: number | null;
};

export type SavedViewSaveOutput = {
  view: SavedViewOut;
  created: boolean;
  receipt_id?: number | null;
};

/** Save a named view of a report or list — its filters, grouping and sort, never its rows — or rename or change one of your own (id and the version you read; a stale version is refused). The view is checked as the report or list reads it and refused if any part would not apply. At most fifty live views per list. */
export const savedViewSave = (input: SavedViewSaveInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SavedViewSaveOutput>("saved_view.save", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingAvailabilityInput = {
  scheduling_request_id: string;
  earliest: string;
  latest: string;
  round_def_id?: string | null;
};

export type AttendeeAvailabilityOut = {
  person_id: string;
  role: string;
  required: boolean;
  timezone: string;
  work_days: number[];
  work_start: string;
  work_end: string;
  busy: BusyOut[];
};

export type BusyOut = {
  start: string;
  end: string;
  kind: string;
};

export type SchedulingAvailabilityOutput = {
  scheduling_request_id: string;
  event_tz: string;
  attendees: AttendeeAvailabilityOut[];
  conflict_policy: Record<string, string>;
};

/** Read the attendees' working hours, time zones and busy, tentative and out-of-office time over a window, for the meeting a request schedules. */
export const schedulingAvailability = (input: SchedulingAvailabilityInput = {} as SchedulingAvailabilityInput) => callTool<SchedulingAvailabilityOutput>("scheduling.availability", input);

export type SlotIn = {
  start: string;
  end?: string | null;
  event_tz?: string | null;
  round_def_id?: string | null;
  format?: "virtual" | "onsite" | "hybrid" | null;
  room_slug?: string | null;
  session_kind?: "interview" | "presentation" | "assessment" | "executive_conversation" | null;
};

export type SchedulingBookInput = {
  scheduling_request_id: string;
  expected_version: number;
  rounds: SlotIn[];
  accept_conflicts?: boolean;
  reason?: string | null;
};

export type AttendeeOut = {
  person_id: string;
  name: string;
  role: string;
  required: boolean;
  response: string;
  responded_at?: string | null;
};

export type ConflictOut = {
  role: string;
  kind: string;
  required: boolean;
};

export type MeetingOut = {
  meeting_event_id: string;
  sequence: number;
  round_def_id?: string | null;
  round_label?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  event_tz: string;
  format?: string | null;
  session_kind: string;
  state: string;
  sync_state: string;
  delivery_status: string;
  simulated: boolean;
  provider_event_id?: string | null;
  teams_meeting_id?: string | null;
  teams_url?: string | null;
  room_slug?: string | null;
  template_config_version_id?: string | null;
  revision: number;
  correlation_id: string;
  conflicts: ConflictOut[];
  attendees: AttendeeOut[];
  organizer?: OrganizerOut | null;
};

export type OrganizerOut = {
  person_id: string;
  name: string;
};

export type SchedulingBookOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  meetings: MeetingOut[];
  warnings: ConflictOut[];
  receipt_id?: number | null;
};

/** Book the meeting — or every round of a Final Interview Loop in sequence, in one call — on the attendees' calendars with its Teams link and room, stamped with the meeting template, and send the invitations. Conflicts the policy asks a reason for are booked only with one; a double-booked room, an out-of-order loop and an unknown time zone are never booked; when the published rule says a debrief waits for Ready, a debrief that is not Ready is not booked. */
export const schedulingBook = (input: SchedulingBookInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingBookOutput>("scheduling.book", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingCancelInput = {
  scheduling_request_id: string;
  expected_version: number;
  reason?: string | null;
};

export type SchedulingCancelOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  meetings_cancelled: number;
  receipt_id?: number | null;
};

/** Cancel a scheduling request with a reason: its meetings come off the attendees' calendars, Teams and the room, and the attendees are told. */
export const schedulingCancel = (input: SchedulingCancelInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingCancelOutput>("scheduling.cancel", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingCompleteInput = {
  scheduling_request_id: string;
  expected_version: number;
};

export type SchedulingCompleteOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  receipt_id?: number | null;
};

/** Mark a scheduled request completed; it closes and stays readable. */
export const schedulingComplete = (input: SchedulingCompleteInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingCompleteOutput>("scheduling.complete", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingDebriefReadinessInput = {
  requisition_id: string;
  cycle_id: string;
};

export type ReadinessControlOut = {
  code: string;
  application_id?: string | null;
  candidate_id?: string | null;
  interview_id?: string | null;
  round_def_id?: string | null;
  round_label?: string | null;
  scorecard_id?: string | null;
  role?: string | null;
  state?: string | null;
  value?: string | null;
  gap?: string | null;
  kind?: string | null;
  loop_status?: string | null;
  route?: string | null;
  author_name?: string | null;
};

export type ReadinessInformationOut = {
  code: string;
  application_id?: string | null;
  candidate_id?: string | null;
  route?: string | null;
};

export type RosterOut = {
  role: string;
  person_id?: string | null;
  name?: string | null;
};

export type SchedulingDebriefReadinessOutput = {
  requisition_id: string;
  cycle_id: string;
  status: string;
  status_label?: string | null;
  ready: boolean;
  controls: ReadinessControlOut[];
  information: ReadinessInformationOut[];
  roster: RosterOut[];
  snapshot_id?: string | null;
  snapshot_at?: string | null;
  projection: "room" | "schedule";
};

/** Read a debrief cycle's readiness: Waiting for Inputs, Recruiter Review, Ready for Debrief or Blocked, every control still missing (rounds held, scorecards submitted, values covered, the Summary Sheet compiled and current, the required attendees named) with its round and route, and what the room should know. The Coordinator reads the roles and rounds, never names, scores or a concern. */
export const schedulingDebriefReadiness = (input: SchedulingDebriefReadinessInput = {} as SchedulingDebriefReadinessInput) => callTool<SchedulingDebriefReadinessOutput>("scheduling.debrief_readiness", input);

export type SchedulingDeclineRescheduleInput = {
  scheduling_request_id: string;
  expected_version: number;
  reason?: string | null;
};

export type SchedulingDeclineRescheduleOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  receipt_id?: number | null;
};

/** Keep the booked time and decline the request for a new time, with a reason; the request is scheduled again. */
export const schedulingDeclineReschedule = (input: SchedulingDeclineRescheduleInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingDeclineRescheduleOutput>("scheduling.decline_reschedule", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingExpireHoldInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type SchedulingExpireHoldOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** A tentative hold nobody confirmed in time is released from the calendars and the room. */
export const schedulingExpireHold = (input: SchedulingExpireHoldInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingExpireHoldOutput>("scheduling.expire_hold", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingFlagNoResponseInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type SchedulingFlagNoResponseOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** A required attendee has not answered an invitation in time: the Coordinator is flagged. */
export const schedulingFlagNoResponse = (input: SchedulingFlagNoResponseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingFlagNoResponseOutput>("scheduling.flag_no_response", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingFlagSchedulingLateInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type SchedulingFlagSchedulingLateOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** C-18 / D-26 / D-30 / E-35: a meeting is not booked inside its scheduling window — the requisition's Coordinator (else the Coordinator pool) is flagged. */
export const schedulingFlagSchedulingLate = (input: SchedulingFlagSchedulingLateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingFlagSchedulingLateOutput>("scheduling.flag_scheduling_late", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingHoldInput = {
  scheduling_request_id: string;
  expected_version: number;
  rounds: SlotIn[];
  accept_conflicts?: boolean;
  reason?: string | null;
};

export type SchedulingHoldOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  meetings: MeetingOut[];
  warnings: ConflictOut[];
  receipt_id?: number | null;
};

/** Hold a time tentatively on the attendees' calendars (and a room) while their responses are awaited. A Final Interview Loop is held only while its panel is ready. */
export const schedulingHold = (input: SchedulingHoldInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingHoldOutput>("scheduling.hold", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingOpenInput = {
  scheduling_request_id?: string | null;
  application_id?: string | null;
  meeting_type?: "intake" | "recruiter_screen" | "hm_interview" | "final_loop" | "debrief" | "offer_call" | null;
  requisition_id?: string | null;
  cycle_id?: string | null;
};

export type LoopOut = {
  ready: boolean;
  rounds: LoopRoundOut[];
  coverage_gaps: string[];
};

export type LoopRoundOut = {
  round_def_id: string;
  label: string;
  kind: string;
  sequence: number;
  format: string;
  duration_minutes: number;
  blocked?: string | null;
};

export type RequestHistoryOut = {
  id: string;
  kind: string;
  at: string;
  event_tz: string;
  from_status?: string | null;
  to_status?: string | null;
  cause?: string | null;
  actor_id?: string | null;
  actor_name?: string | null;
  detail: Record<string, unknown>;
};

export type RequestSyncOut = {
  integration_sync_id: string;
  meeting_event_id?: string | null;
  port: string;
  op: string;
  status: string;
  attempts: number;
  correlation_id: string;
  last_error?: string | null;
  requested_at: string;
  completed_at?: string | null;
  next_attempt_at?: string | null;
  updated_at: string;
};

export type RescheduleAskOut = {
  reschedule_request_id: string;
  meeting_event_id?: string | null;
  requested_by_id: string;
  requested_by_name: string;
  reason: string;
  at: string;
};

export type TravelOut = {
  kind: string;
  status: string;
  due_at?: string | null;
  done_at?: string | null;
};

export type SchedulingOpenOutput = {
  scheduling_request_id: string;
  requisition_id: string;
  requisition_title: string;
  application_id?: string | null;
  candidate_id?: string | null;
  candidate_display_name?: string | null;
  cycle_id?: string | null;
  meeting_type: string;
  status: string;
  status_label?: string | null;
  blocked_reason?: string | null;
  risk: string;
  due_at?: string | null;
  event_tz: string;
  version: number;
  coordinator_id?: string | null;
  requested_by_id: string;
  trigger: string;
  decided_at?: string | null;
  scheduled_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  config_versions: Record<string, unknown>;
  basis: string[];
  meetings: MeetingOut[];
  constraints?: Record<string, unknown> | null;
  candidate_availability?: Record<string, unknown>[] | null;
  room_needs?: Record<string, unknown> | null;
  travel?: TravelOut[] | null;
  syncs?: RequestSyncOut[] | null;
  history?: RequestHistoryOut[] | null;
  loop?: LoopOut | null;
  reschedule_request?: RescheduleAskOut | null;
};

/** Open a scheduling request: its status, due date, time zone, constraints, risk and every meeting with its attendees, responses, room, Teams link and delivery status; a Final Interview Loop also its rounds in sequence and whether its panel is ready. Named by id, by the application and meeting type, by the requisition of an intake or by the cycle of a debrief. An Interviewer reads only the meetings they attend. */
export const schedulingOpen = (input: SchedulingOpenInput = {} as SchedulingOpenInput) => callTool<SchedulingOpenOutput>("scheduling.open", input);

export type WindowIn = {
  start: string;
  end: string;
  tz?: string | null;
};

export type SchedulingProposeInput = {
  scheduling_request_id: string;
  expected_version: number;
  earliest: string;
  latest: string;
  round_def_id?: string | null;
  duration_minutes?: number | null;
  count?: number;
  candidate_windows?: WindowIn[];
};

export type SlotOut = {
  start: string;
  end: string;
  attendees_free: boolean;
  conflicts: ConflictOut[];
};

export type SchedulingProposeOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  proposal_id: string;
  slots: SlotOut[];
  receipt_id?: number | null;
};

/** Propose times inside every required attendee's working hours and free time; each slot shows its conflicts. The request then waits for the candidate's availability, for the interviewers, or is in progress. */
export const schedulingPropose = (input: SchedulingProposeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingProposeOutput>("scheduling.propose", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingRecordAvailabilityInput = {
  scheduling_request_id: string;
  expected_version: number;
  windows: WindowIn[];
  reason?: string | null;
};

export type SchedulingRecordAvailabilityOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  receipt_id?: number | null;
};

/** Record the windows the candidate said they are available in (typed, never asked by the app); a request waiting for the candidate moves on. */
export const schedulingRecordAvailability = (input: SchedulingRecordAvailabilityInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingRecordAvailabilityOutput>("scheduling.record_availability", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingRemindInput = {
  scheduling_request_id: string;
};

export type SchedulingRemindOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  reminded: string[];
  receipt_id?: number | null;
};

/** Remind whoever the request waits on: the Recruiter while it waits for the candidate's availability, the required attendees who have not answered while it waits for the interviewers. One reminder per person per day. */
export const schedulingRemind = (input: SchedulingRemindInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingRemindOutput>("scheduling.remind", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type Constraints = {
  earliest?: string | null;
  latest?: string | null;
  format?: "virtual" | "onsite" | "hybrid" | null;
  duration_minutes?: number | null;
  back_to_back?: boolean | null;
};

export type RoomNeeds = {
  capacity?: number | null;
  video?: boolean | null;
};

export type SchedulingRequestInput = {
  requisition_id: string;
  meeting_type: "intake" | "recruiter_screen" | "hm_interview" | "final_loop" | "debrief" | "offer_call";
  application_id?: string | null;
  cycle_id?: string | null;
  due_at?: string | null;
  constraints?: Constraints | null;
  room_needs?: RoomNeeds | null;
  reason?: string | null;
};

export type SchedulingRequestOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  opened: boolean;
  receipt_id?: number | null;
};

/** Request a meeting to be scheduled — an intake, a recruiter screen, an HM interview, a Final Interview Loop, a debrief or an offer call — with its due date, constraints and room needs. An open request for the same subject is returned instead of a duplicate. */
export const schedulingRequest = (input: SchedulingRequestInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingRequestOutput>("scheduling.request", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingRequestRescheduleInput = {
  scheduling_request_id: string;
  meeting_event_id?: string | null;
  on_behalf_of?: "self" | "candidate";
  cause: "candidate" | "panel" | "process";
  reason: string;
};

export type SchedulingRequestRescheduleOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  reschedule_request_id: string;
  receipt_id?: number | null;
};

/** Ask for a new time for a scheduled meeting, with the cause (candidate, panel or process) and a reason; the Recruiter or the Coordinator records the candidate's ask. The meeting stays booked until the Coordinator reschedules it or declines. */
export const schedulingRequestReschedule = (input: SchedulingRequestRescheduleInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingRequestRescheduleOutput>("scheduling.request_reschedule", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingRescheduleInput = {
  scheduling_request_id: string;
  expected_version: number;
  meeting_event_id?: string | null;
  start: string;
  end?: string | null;
  room_slug?: string | null;
  accept_conflicts?: boolean;
  reason?: string | null;
};

export type SchedulingRescheduleOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  meetings: MeetingOut[];
  warnings: ConflictOut[];
  receipt_id?: number | null;
};

/** Move a booked meeting to a new time (and room) with a reason: the same calendar event and Teams meeting are updated, never duplicated, the conflicts are checked again at the new time, the attendees are asked to answer again, and an open request for a new time is accepted. */
export const schedulingReschedule = (input: SchedulingRescheduleInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingRescheduleOutput>("scheduling.reschedule", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingRescheduleRoundInput = {
  requisition_id: string;
  application_id: string;
  round_kind?: "recruiter_screen" | "hm_round" | "onsite" | "additional";
  round_def_id?: string | null;
  start: string;
  location_or_link?: string | null;
  recording_consent?: "not_asked" | "given" | "refused";
  cause: "candidate" | "panel" | "process";
  reason?: string | null;
  accept_conflicts?: boolean;
};

export type SchedulingRescheduleRoundOutput = {
  interview_id: string;
  requisition_id: string;
  application_id: string;
  candidate: string;
  round_def_id: string;
  round_kind: string;
  round_label: string;
  format: string;
  duration_minutes: number;
  status: string;
  primary: Record<string, unknown>;
  backup: Record<string, unknown> | null;
  booking: Record<string, unknown> | null;
  held_at: string | null;
  not_held_reason: string | null;
  reschedule_count: number;
  scorecard_status: string | null;
  packet_id: string | null;
  packet_link_token: string | null;
  recording_consent: string;
  touchpoints: Record<string, number>;
  itinerary: string | null;
  can: Record<string, boolean>;
  scheduling_request_id?: string | null;
  held_offline?: boolean;
  offline_reason?: string | null;
  receipt_id?: number | null;
};

/** Move a booked round to a new time in one step, with the cause and a reason: the conflicts are checked at the new time (one that needs a reason is refused until it is given), then the round is moved and booked together, so a refusal leaves it booked where it was. */
export const schedulingRescheduleRound = (input: SchedulingRescheduleRoundInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingRescheduleRoundOutput>("scheduling.reschedule_round", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingResolveSyncInput = {
  scheduling_request_id: string;
  integration_sync_id: string;
  reason?: string | null;
};

export type SchedulingResolveSyncOutput = {
  integration_sync_id: string;
  scheduling_request_id: string;
  meeting_event_id: string;
  port: string;
  op: string;
  status: string;
  attempts: number;
  correlation_id: string;
  receipt_id?: number | null;
};

/** Mark a failed calendar, Teams or room call recovered after it was fixed outside the app, with a reason; the recovery task closes. */
export const schedulingResolveSync = (input: SchedulingResolveSyncInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingResolveSyncOutput>("scheduling.resolve_sync", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingRespondInput = {
  meeting_event_id: string;
  response: "accepted" | "tentative" | "declined";
};

export type SchedulingRespondOutput = {
  scheduling_request_id: string;
  meeting_event_id: string;
  person_id: string;
  response: string;
  responded_at?: string | null;
  channel?: string | null;
  receipt_id?: number | null;
};

/** Answer your own invitation to a meeting: accepted, tentative or declined. A required attendee's decline is flagged to the Coordinator. */
export const schedulingRespond = (input: SchedulingRespondInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingRespondOutput>("scheduling.respond", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingRetrySyncInput = {
  scheduling_request_id: string;
  integration_sync_id: string;
};

export type SchedulingRetrySyncOutput = {
  integration_sync_id: string;
  scheduling_request_id: string;
  meeting_event_id: string;
  port: string;
  op: string;
  status: string;
  attempts: number;
  correlation_id: string;
  receipt_id?: number | null;
};

/** Retry a failed calendar, Teams or room call; when it goes through, the recovery task closes. A call that already went through is not repeated. */
export const schedulingRetrySync = (input: SchedulingRetrySyncInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingRetrySyncOutput>("scheduling.retry_sync", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type TravelIn = {
  kind: "travel" | "lodging" | "parking";
  status: "open" | "done" | "not_needed";
  due_at?: string | null;
};

export type SchedulingSetTravelInput = {
  scheduling_request_id: string;
  expected_version: number;
  tasks: TravelIn[];
};

export type SchedulingSetTravelOutput = {
  scheduling_request_id: string;
  meeting_type: string;
  status: string;
  blocked_reason?: string | null;
  version: number;
  travel: TravelOut[];
  receipt_id?: number | null;
};

/** Keep the travel checklist of a request — travel, lodging, parking — each open, done or not needed, with its due date. No amounts and no itinerary text. */
export const schedulingSetTravel = (input: SchedulingSetTravelInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingSetTravelOutput>("scheduling.set_travel", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingSlaBreachInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type SchedulingSlaBreachOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** The time to schedule a request has gone: the request is overdue and its owner gets an overdue card. */
export const schedulingSlaBreach = (input: SchedulingSlaBreachInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingSlaBreachOutput>("scheduling.sla_breach", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingSlaHalfInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type SchedulingSlaHalfOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** Half the time to schedule a request has gone: its scheduling task is raised again. */
export const schedulingSlaHalf = (input: SchedulingSlaHalfInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingSlaHalfOutput>("scheduling.sla_half", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingSyncLapseInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type SchedulingSyncLapseOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** A live calendar, Teams or room call was delivered or failed: its outcome is recorded on the call, and a failure opens the same recovery task as any failed call. */
export const schedulingSyncLapse = (input: SchedulingSyncLapseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SchedulingSyncLapseOutput>("scheduling.sync_lapse", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SchedulingWorkbenchInput = {
  view?: "open" | "history" | "all";
  status?: ("requested" | "in_progress" | "awaiting_candidate" | "awaiting_interviewer" | "scheduled" | "reschedule_requested" | "completed" | "cancelled")[] | null;
  meeting_type?: ("intake" | "recruiter_screen" | "hm_interview" | "final_loop" | "debrief" | "offer_call")[] | null;
  risk?: ("none" | "at_risk" | "breached")[] | null;
  blocked?: boolean | null;
  sync_failed?: boolean | null;
  requisition_id?: string | null;
  offset?: number;
  limit?: number;
};

export type WorkbenchCountsOut = {
  total: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
};

export type WorkbenchRowOut = {
  key: string;
  source: string;
  scheduling_request_id?: string | null;
  requisition_id: string;
  requisition_title: string;
  meeting_type: string;
  status: string;
  status_label?: string | null;
  application_id?: string | null;
  candidate_id?: string | null;
  candidate_display_name?: string | null;
  cycle_id?: string | null;
  blocked_reason?: string | null;
  risk: string;
  due_at?: string | null;
  event_tz?: string | null;
  next_start?: string | null;
  scheduled_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  sync_failed: boolean;
  conflicts_n: number;
  reschedule_open: boolean;
  coordinator_id?: string | null;
  version?: number | null;
  route?: string | null;
};

export type SchedulingWorkbenchOutput = {
  rows: WorkbenchRowOut[];
  counts: WorkbenchCountsOut;
  tiles: Record<string, number>;
  offset: number;
  limit: number;
  next_offset?: number | null;
};

/** The Coordinator Workbench: every scheduling request in the reader's scope (a Coordinator's pool, never a confidential requisition without their name on it; a Recruiter's own requisitions; the TA Manager's and the Admin's whole organisation), open or history, filtered by status, meeting type, risk, blocked and failed sync, with counts that are exactly the rows listed and the open view's tiles. */
export const schedulingWorkbench = (input: SchedulingWorkbenchInput = {} as SchedulingWorkbenchInput) => callTool<SchedulingWorkbenchOutput>("scheduling.workbench", input);

export type ScorecardAppendAddendumInput = {
  scorecard_id: string;
  text: string;
  reason: string;
};

export type ScorecardAppendAddendumOutput = {
  scorecard_id: string;
  status: string;
  revision_no: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** After the freeze, the author's addendum beneath the original (changes no rating, recommendation or average). */
export const scorecardAppendAddendum = (input: ScorecardAppendAddendumInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardAppendAddendumOutput>("scorecard.append_addendum", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardConfirmRecordedInput = {
  scorecard_id: string;
};

export type ScorecardConfirmRecordedOutput = {
  scorecard_id: string;
  status: string;
  revision_no: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** The author's one-click confirmation of a keyed-in scorecard ("This is what I wrote"). */
export const scorecardConfirmRecorded = (input: ScorecardConfirmRecordedInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardConfirmRecordedOutput>("scorecard.confirm_recorded", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardCorrectMetadataInput = {
  requisition_id: string;
  scorecard_id: string;
  field: string;
  value?: unknown;
  revision_id?: string | null;
  reason: string;
  evidence_ref?: string | null;
};

export type ScorecardCorrectMetadataOutput = {
  correction_id: string;
  scorecard_id: string;
  field: string;
  before: unknown;
  after: unknown;
  scorecard_status: string;
  receipt_id?: number | null;
};

/** Correct a scorecard's workflow metadata by administrative correction, with a reason; never a score. */
export const scorecardCorrectMetadata = (input: ScorecardCorrectMetadataInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardCorrectMetadataOutput>("scorecard.correct_metadata", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardLiftWaiverInput = {
  requisition_id: string;
  scorecard_id: string;
  reason: string;
};

export type ScorecardLiftWaiverOutput = {
  requisition_id: string;
  scorecard_id: string;
  waiver_id: string;
  kind: "waived" | "lifted";
  scorecard_status: string;
  at: string;
  receipt_id?: number | null;
};

/** Lift a scorecard's Waived mark, with a reason; the card is missing again, and the mark stays on record. */
export const scorecardLiftWaiver = (input: ScorecardLiftWaiverInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardLiftWaiverOutput>("scorecard.lift_waiver", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardMarkUnconfirmedInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type ScorecardMarkUnconfirmedOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** F-44: a keyed-in scorecard is unconfirmed by its author after two business days — the rows keep printing 'unconfirmed'; the Recruiter is told. */
export const scorecardMarkUnconfirmed = (input: ScorecardMarkUnconfirmedInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardMarkUnconfirmedOutput>("scorecard.mark_unconfirmed", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardOpenInput = {
  scorecard_id?: string | null;
  interview_id?: string | null;
  packet_id?: string | null;
  application_id?: string | null;
};

export type GuideHeader = {
  title: string;
  subtitle?: string | null;
  guide_title?: string | null;
  target_role: string;
};

export type GuidePageWords = {
  guide_title: string;
  values_footer: string;
};

export type GuideRecoOption = {
  key: string;
  label: string;
  description?: string | null;
};

export type GuideRecommendationPage = {
  page_title?: string | null;
  options?: GuideRecoOption[];
  justification_prompt?: string | null;
  handoff_prompt?: string | null;
  consider_other_role_prompt?: string | null;
  consider_other_role_fields?: string[];
  footer?: string | null;
  decision_rule_text?: string | null;
  alignment_text?: string | null;
  coc_question_text?: string | null;
  coc_no_instruction?: string | null;
  attestation_text?: string | null;
};

export type GuideRubric = {
  lines?: string[];
  scoring_summary_text?: string | null;
  bands_note?: string | null;
};

export type GuideSkillPage = {
  number: number;
  title?: string | null;
  what_strong_looks_like?: string | null;
  prompts?: string[];
  fit_label?: string | null;
};

export type GuideValuePage = {
  key: string;
  name: string;
  page_title?: string | null;
  assesses?: string | null;
  questions?: string[];
  strong?: string | null;
  weak?: string | null;
  anchors?: Record<string, string>;
  evidence_prompt?: string | null;
};

export type MissingItem = {
  id: string;
  text: string;
};

export type RubricBand = {
  key: string;
  text: string;
  highlight: boolean;
};

export type ScorecardGuide = {
  page_words?: GuidePageWords | null;
  footer?: string;
  header: GuideHeader;
  value_pages?: GuideValuePage[];
  skill_pages?: GuideSkillPage[];
  recommendation_page: GuideRecommendationPage;
  rubric: GuideRubric;
  score_descriptions?: Record<string, string>;
  questions_note?: string | null;
  not_assessed_label?: string | null;
};

export type ScorecardLevel = {
  band: string;
  rule_text: string;
  offered: string[];
  hire_like: string[];
  not_hire: string[];
  met?: boolean;
  own_flags?: Record<string, unknown>[];
};

export type ScorecardOpenOutput = {
  receipt_id?: number | null;
  scorecard_id: string | null;
  interview_id: string;
  application_id: string;
  requisition_id: string;
  candidate: string;
  round_label: string;
  round_kind: string;
  author_id: string;
  author_name: string;
  projection: string;
  status: string;
  current_revision_no: number;
  interview_status: string;
  held_at: string | null;
  dimension_set: Record<string, unknown>[];
  draft: Record<string, unknown> | null;
  draft_version: string | null;
  draft_saved_at: string | null;
  revision: Record<string, unknown> | null;
  ratings: Record<string, unknown>[] | null;
  two_2s: Record<string, unknown> | null;
  missing: MissingItem[];
  submitted_at: string | null;
  frozen_at: string | null;
  source: string;
  entered_by_name: string | null;
  confirmed_at: string | null;
  addenda: Record<string, unknown>[];
  can: Record<string, boolean>;
  level?: ScorecardLevel | null;
  core_average?: string | null;
  core_values_scored?: number;
  rubric_band?: RubricBand | null;
  revision_id?: string | null;
  guide?: ScorecardGuide | null;
  display_status?: string | null;
  due_at?: string | null;
};

/** The scorecard as its author sees it (draft, completeness, two-2s live), as the Recruiter compiles it, sealed for others until the reveal; opening as the author on a scheduled or held round creates the draft. */
export const scorecardOpen = (input: ScorecardOpenInput = {} as ScorecardOpenInput) => callTool<ScorecardOpenOutput>("scorecard.open", input);

export type ScorecardRaiseConductConcernInput = {
  scorecard_id?: string | null;
  interview_id?: string | null;
  packet_id?: string | null;
  application_id?: string | null;
  concern_text: string;
  about?: "candidate" | "hiring_manager" | "recruiter" | "hrbp" | "other";
};

export type ScorecardRaiseConductConcernOutput = {
  receipt_id?: number | null;
  scorecard_id: string;
  status: string;
  revision_no: number;
  auto_no_hire: boolean;
  effective_at: string;
};

/** Raise a Code of Conduct concern on your own scorecard outside the submit (routed to the HR Business Partner; Admin on the alternate route). */
export const scorecardRaiseConductConcern = (input: ScorecardRaiseConductConcernInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardRaiseConductConcernOutput>("scorecard.raise_conduct_concern", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type DraftBody = {
  ratings?: RatingInput[];
  recommendation?: "hire" | "hire_with_reservations" | "no_hire" | "strong_hire" | "consider_other_role" | null;
  reservation_text?: string | null;
  consider_other_role_answer?: "yes" | "no" | null;
  consider_other_role_suggested?: string | null;
  consider_other_role_why?: string | null;
  justification?: string | null;
  manager_handoff?: string | null;
  coc_answer?: "yes" | "no" | null;
  coc_concern_text?: string | null;
  coc_concern_about?: "candidate" | "hiring_manager" | "recruiter" | "hrbp" | "other" | null;
  private_notes?: string | null;
  attested?: boolean;
};

export type RatingInput = {
  dimension_type: "value" | "skill";
  dimension_key: string;
  state?: "unanswered" | "scored" | "not_assessed";
  score?: number | null;
  evidence_text?: string | null;
  strong_observations?: string | null;
  gaps_detected?: string | null;
  notes?: string | null;
  not_assessed_reason?: string | null;
};

export type ScorecardRecordForInput = {
  interview_id: string;
  for_person: string;
  source: "paper" | "verbal";
  source_file_id?: string | null;
  evidence_ref?: string | null;
  happened_at: string;
  draft: DraftBody;
};

export type ScorecardRecordForOutput = {
  scorecard_id: string;
  status: string;
  revision_no: number;
  effective_at: string;
  receipt_id?: number | null;
};

/** Key in an interviewer's paper or verbal assessment: author = the interviewer, entered by you, source and evidence recorded; writes and submits r1 in one call; refused RECORD_LOCKED once the scorecard is frozen; the author is asked to confirm (F-44). */
export const scorecardRecordFor = (input: ScorecardRecordForInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardRecordForOutput>("scorecard.record_for", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardRemindInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type ScorecardRemindOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** F-42 / F-43: the scorecard is due (24h after the round · 48h before the debrief) — the author is reminded, the Recruiter sees it outstanding. */
export const scorecardRemind = (input: ScorecardRemindInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardRemindOutput>("scorecard.remind", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardReviseInput = {
  scorecard_id?: string | null;
  interview_id?: string | null;
  packet_id?: string | null;
  application_id?: string | null;
  draft: DraftBody;
  reason: string;
};

export type ScorecardReviseOutput = {
  receipt_id?: number | null;
  scorecard_id: string;
  status: string;
  revision_no: number;
  auto_no_hire: boolean;
  effective_at: string;
};

/** A new revision by the author before the freeze; reason required; the prior revision stays readable. */
export const scorecardRevise = (input: ScorecardReviseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardReviseOutput>("scorecard.revise", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardReviseInRoomInput = {
  requisition_id: string;
  cycle_id: string;
  application_id: string;
  scorecard_id: string;
  ratings?: RatingInput[];
  recommendation?: "hire" | "hire_with_reservations" | "no_hire" | "strong_hire" | "consider_other_role" | null;
  reservation_text?: string | null;
  consider_other_role_answer?: "yes" | "no" | null;
  consider_other_role_suggested?: string | null;
  consider_other_role_why?: string | null;
  rationale: string;
  attested?: boolean;
};

export type ScorecardReviseInRoomOutput = {
  receipt_id?: number | null;
  room_revision_id: string;
  scorecard_id: string;
  cycle_id: string;
  application_id: string;
  original_revision_id: string;
  revision_id: string;
  revision_no: number;
  original_recommendation: string;
  recommendation: string;
  auto_no_hire: boolean;
  status: string;
  effective_at: string;
};

/** Below VP, in the revealed room and before the outcome: revise your own score or recommendation with a rationale; the original stays on the sheet beside the revised. */
export const scorecardReviseInRoom = (input: ScorecardReviseInRoomInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardReviseInRoomOutput>("scorecard.revise_in_room", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardSaveDraftInput = {
  scorecard_id?: string | null;
  interview_id?: string | null;
  packet_id?: string | null;
  application_id?: string | null;
  draft: DraftBody;
  expected_version?: string | null;
};

export type ScorecardSaveDraftOutput = {
  receipt_id?: number | null;
  scorecard_id: string | null;
  interview_id: string;
  application_id: string;
  requisition_id: string;
  candidate: string;
  round_label: string;
  round_kind: string;
  author_id: string;
  author_name: string;
  projection: string;
  status: string;
  current_revision_no: number;
  interview_status: string;
  held_at: string | null;
  dimension_set: Record<string, unknown>[];
  draft: Record<string, unknown> | null;
  draft_version: string | null;
  draft_saved_at: string | null;
  revision: Record<string, unknown> | null;
  ratings: Record<string, unknown>[] | null;
  two_2s: Record<string, unknown> | null;
  missing: MissingItem[];
  submitted_at: string | null;
  frozen_at: string | null;
  source: string;
  entered_by_name: string | null;
  confirmed_at: string | null;
  addenda: Record<string, unknown>[];
  can: Record<string, boolean>;
  level?: ScorecardLevel | null;
  core_average?: string | null;
  core_values_scored?: number;
  rubric_band?: RubricBand | null;
  revision_id?: string | null;
  guide?: ScorecardGuide | null;
  display_status?: string | null;
  due_at?: string | null;
};

/** Save the server-backed draft (author only); expected_version detects a save from elsewhere (409 draft-conflict). */
export const scorecardSaveDraft = (input: ScorecardSaveDraftInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardSaveDraftOutput>("scorecard.save_draft", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardSendReminderInput = {
  scorecard_id: string;
  approve_wording?: boolean;
};

export type ScorecardSendReminderOutput = {
  scorecard_id: string;
  wording_approved: boolean;
  wording: string;
  sent: boolean;
  outbox_message_id: string | null;
  effective_at: string;
  receipt_id?: number | null;
};

/** Send the outstanding-scorecard reminder to its author (Outbox, simulated); the wording must be approved for the requisition — approve it in the same call. */
export const scorecardSendReminder = (input: ScorecardSendReminderInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardSendReminderOutput>("scorecard.send_reminder", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardSubmitInput = {
  scorecard_id?: string | null;
  interview_id?: string | null;
  packet_id?: string | null;
  application_id?: string | null;
  draft?: DraftBody | null;
  held_at?: string | null;
  revision_reason?: string | null;
};

export type ScorecardSubmitOutput = {
  receipt_id?: number | null;
  scorecard_id: string;
  status: string;
  revision_no: number;
  auto_no_hire: boolean;
  effective_at: string;
};

/** Submit revision r1: the one validation contract; the requisition's pinned level rule flags a concern below VP and forces No Hire at VP and above (two ratings ≤ 2 on a requisition finalized before it); a Code of Conduct No raises a routed concern in the same transaction; the round can be marked held in the same call. */
export const scorecardSubmit = (input: ScorecardSubmitInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardSubmitOutput>("scorecard.submit", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScorecardWaiveInput = {
  requisition_id: string;
  scorecard_id: string;
  reason: string;
};

export type ScorecardWaiveOutput = {
  requisition_id: string;
  scorecard_id: string;
  waiver_id: string;
  kind: "waived" | "lifted";
  scorecard_status: string;
  at: string;
  receipt_id?: number | null;
};

/** Mark a missing scorecard Waived for the record, with a reason; the debrief stays Not Ready. */
export const scorecardWaive = (input: ScorecardWaiveInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScorecardWaiveOutput>("scorecard.waive", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScreenDecideInput = {
  requisition_id: string;
  application_id: string;
  value: "advance" | "hold" | "pass";
  reason_text: string;
  reason_code?: string | null;
};

export type ScreenDecideOutput = {
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** The Hiring Manager's selection from the screen: advance to my interview · hold · pass, with a reason; refused when the screen scorecard's two-2s fired (no advance over the flag). */
export const screenDecide = (input: ScreenDecideInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScreenDecideOutput>("screen.decide", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScreenOpenResultInput = {
  requisition_id: string;
  application_id: string;
};

export type ScreenOpenResultOutput = {
  application_id: string;
  requisition_id: string;
  projection: string;
  interview_status: string | null;
  held_at: string | null;
  scorecard_status: string | null;
  scorecard_id: string | null;
  notes_only: boolean;
  form: Record<string, unknown>;
  must_haves: string[];
  above_range: boolean;
  comp_range?: Record<string, unknown> | null;
  submitted_at: string | null;
  complete: boolean;
  missing: string[];
  can: Record<string, boolean>;
  status?: string | null;
  outcome?: string | null;
  outcome_at?: string | null;
  scheduled_at?: string | null;
};

/** The screen form and the Recruiter's summary (no ratings, no recommendation — sealed until the reveal); the Recruiter sees their own form with completeness. */
export const screenOpenResult = (input: ScreenOpenResultInput = {} as ScreenOpenResultInput) => callTool<ScreenOpenResultOutput>("screen.open_result", input);

export type WithdrawalIn = {
  reason_code: "took_other_role" | "comp" | "location" | "timing" | "no_longer_interested" | "unreachable" | "other";
  stage: string;
  effective_at: string;
};

export type ScreenRecordOutcomeInput = {
  requisition_id: string;
  application_id: string;
  outcome: "advance" | "hold" | "pass" | "withdrawn";
  hold_reason?: string | null;
  reason_code?: "scope_mismatch" | "experience_gap" | "location" | "comp_out_of_range" | "not_a_fit_for_level" | "internal_policy" | "duplicate" | "resume_review_pass" | "screen_pass" | "passed_from_hold" | "other" | null;
  reason_text?: string | null;
  withdrawal?: WithdrawalIn | null;
};

export type ScreenRecordOutcomeOutput = {
  requisition_id: string;
  application_id: string;
  outcome: string;
  stage: string;
  status: string | null;
  outcome_at: string;
  screen_outcome_id: string;
  receipt_id?: number | null;
};

/** Record the Recruiter screen's outcome: Advance (to Pending HM Selection), Hold (a configured reason) or Pass (a disposition reason) once the screen is held, or Withdrawn (reason, stage and date) at any status. */
export const screenRecordOutcome = (input: ScreenRecordOutcomeInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScreenRecordOutcomeOutput>("screen.record_outcome", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScreenSaveFormInput = {
  requisition_id: string;
  application_id: string;
  motivation?: string | null;
  comp_expectation?: number | null;
  comp_expectation_currency?: string;
  notice_period?: string | null;
  earliest_start?: string | null;
  location?: string | null;
  mobility?: string | null;
  work_authorisation?: string | null;
  work_authorisation_problem?: boolean;
  must_have_verification?: Record<string, unknown>[] | null;
  notes?: string | null;
  summary?: string | null;
  first_contact_at?: string | null;
  follow_up_at?: string | null;
  content?: string | null;
};

export type ScreenSaveFormOutput = {
  application_id: string;
  requisition_id: string;
  projection: string;
  interview_status: string | null;
  held_at: string | null;
  scorecard_status: string | null;
  scorecard_id: string | null;
  notes_only: boolean;
  form: Record<string, unknown>;
  must_haves: string[];
  above_range: boolean;
  comp_range?: Record<string, unknown> | null;
  submitted_at: string | null;
  complete: boolean;
  missing: string[];
  can: Record<string, boolean>;
  status?: string | null;
  outcome?: string | null;
  outcome_at?: string | null;
  scheduled_at?: string | null;
  receipt_id?: number | null;
};

/** Autosave the screen form: motivation, compensation expectation (above-range flag), notice period, earliest start, location and mobility, work authorisation, must-have verification, notes, summary. */
export const screenSaveForm = (input: ScreenSaveFormInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScreenSaveFormOutput>("screen.save_form", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ScreenSubmitResultInput = {
  requisition_id: string;
  application_id: string;
  summary: string;
};

export type ScreenSubmitResultOutput = {
  application_id: string;
  stage: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Send the screen results to the Hiring Manager: the form complete, the scorecard submitted (unless notes-only), the written summary; stage → screened; the HR Business Partner is told of an above-range expectation. */
export const screenSubmitResult = (input: ScreenSubmitResultInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<ScreenSubmitResultOutput>("screen.submit_result", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type ShellNavInput = Record<string, unknown>;

export type NavItemOut = {
  key: string;
  label: string;
  route: string;
  badge?: number | null;
};

export type ShellNavPersonOut = {
  name: string;
  initials: string;
  hue: string;
  role_words: string[];
  seat_word: string;
  seat_key: string;
};

export type ShellNavOutput = {
  items: NavItemOut[];
  tabbar: string[];
  help: NavItemOut[];
  help_heading: string;
  person: ShellNavPersonOut;
  scope_words: string[];
  actions_open: number;
  actions_breach: number;
  theme_default: "dark" | "light";
  notices_unread: number;
};

/** Hiring Hub navigation: the union of the seat sets the person holds in master order with badges, the bottom tab bar, the ? menu and its heading, the signed-in person, the theme the seat opens in when none is chosen, and the count of unread notices. */
export const shellNav = (input: ShellNavInput = {} as ShellNavInput) => callTool<ShellNavOutput>("shell.nav", input);

export type ShellNoticesInput = {
  limit?: number;
};

export type NoticeOut = {
  id: string;
  title: string;
  body: string | null;
  route: string | null;
  created_at: string;
  read: boolean;
};

export type ShellNoticesOutput = {
  items: NoticeOut[];
  unread: number;
};

/** The bell: the person's own read-only notices, newest first; opening it marks the unread ones it shows read (a view-as read marks nothing). */
export const shellNotices = (input: ShellNoticesInput = {} as ShellNoticesInput) => callTool<ShellNoticesOutput>("shell.notices", input);

export type ShellSearchInput = {
  q: string;
  limit?: number;
};

export type SearchHit = {
  id: string;
  title: string;
  detail: string | null;
  route: string | null;
};

export type ShellSearchOutput = {
  requisitions: SearchHit[];
  candidates: SearchHit[];
};

/** Search from the top bar: requisitions by R-id, REQ ID or title within the person's list scope, and candidates by name or Candidate ID for a seat that searches candidates; each hit trimmed to what the person may see. */
export const shellSearch = (input: ShellSearchInput = {} as ShellSearchInput) => callTool<ShellSearchOutput>("shell.search", input);

export type SlaTierReachedInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
  tier?: "half" | "near_breach" | null;
};

export type SlaTierReachedOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** SLA tier: half or near-breach of the active target is recorded on the episode and shown on the open items; the requisition's health is recomputed; nobody new is told. */
export const slaTierReached = (input: SlaTierReachedInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SlaTierReachedOutput>("sla.tier_reached", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SpineRebuildInput = {
  mode?: "verify" | "repair";
  requisition_id?: string | null;
};

export type SpineRebuildOutput = {
  mode: string;
  requisition_id: string | null;
  total: number;
  counts: Record<string, number>;
  unowned_native: string[];
  receipt_id?: number | null;
};

/** Rebuild: verify reports how far the timeline, reporting, health and readiness projections and the open action set differ from their events; repair re-derives them. Never deletes; the receipt carries the counts. */
export const spineRebuild = (input: SpineRebuildInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SpineRebuildOutput>("spine.rebuild", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SummaryAttachExerciseEvaluationInput = {
  requisition_id: string;
  application_id: string;
  cycle_id?: string | null;
};

export type SummaryAttachExerciseEvaluationOutput = {
  sheet_id: string | null;
  cycle_id: string | null;
  application_id: string;
  candidate: string;
  projection: string;
  status: string;
  compiled_at: string | null;
  revealed_at: string | null;
  scorecards_in: number;
  scorecards_due: number;
  snapshot: Record<string, unknown> | null;
  annotations: Record<string, unknown>[];
  outcome: Record<string, unknown> | null;
  can: Record<string, boolean>;
  release?: Record<string, unknown> | null;
  notes?: Record<string, unknown> | null;
  receipt_id?: number | null;
};

/** Attach the recorded exercise evaluation to the sheet as evidence (recompiles a sealed sheet). */
export const summaryAttachExerciseEvaluation = (input: SummaryAttachExerciseEvaluationInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SummaryAttachExerciseEvaluationOutput>("summary.attach_exercise_evaluation", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SummaryCompileInput = {
  requisition_id: string;
  application_id: string;
  cycle_id?: string | null;
};

export type SummaryCompileOutput = {
  sheet_id: string | null;
  cycle_id: string | null;
  application_id: string;
  candidate: string;
  projection: string;
  status: string;
  compiled_at: string | null;
  revealed_at: string | null;
  scorecards_in: number;
  scorecards_due: number;
  snapshot: Record<string, unknown> | null;
  annotations: Record<string, unknown>[];
  outcome: Record<string, unknown> | null;
  can: Record<string, boolean>;
  release?: Record<string, unknown> | null;
  notes?: Record<string, unknown> | null;
  receipt_id?: number | null;
};

/** Compile the sheet from the submitted scorecards (matrix, averages, divergences, coverage, flags, exceptions); sealed until the reveal; recompiles as scorecards land. */
export const summaryCompile = (input: SummaryCompileInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SummaryCompileOutput>("summary.compile", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SummaryExportInput = {
  requisition_id: string;
  application_id: string;
  cycle_id?: string | null;
  format?: "print" | "csv";
};

export type SummaryExportOutput = {
  sheet_id: string;
  rendition: string;
  rendition_html?: string | null;
  classification: string;
  exported_at: string;
  export_attribution: string;
  file_id: string;
  version_id: string;
  receipt_id: string;
  format: string;
};

/** Export the revealed sheet (Confidential, Internal Use Only) — never before the reveal; an export receipt is written. */
export const summaryExport = (input: SummaryExportInput = {} as SummaryExportInput) => callTool<SummaryExportOutput>("summary.export", input);

export type SummaryOpenInput = {
  requisition_id: string;
  application_id: string;
  cycle_id?: string | null;
};

export type SummaryOpenOutput = {
  sheet_id: string | null;
  cycle_id: string | null;
  application_id: string;
  candidate: string;
  projection: string;
  status: string;
  compiled_at: string | null;
  revealed_at: string | null;
  scorecards_in: number;
  scorecards_due: number;
  snapshot: Record<string, unknown> | null;
  annotations: Record<string, unknown>[];
  outcome: Record<string, unknown> | null;
  can: Record<string, boolean>;
  release?: Record<string, unknown> | null;
  notes?: Record<string, unknown> | null;
};

/** The Summary Sheet: the compiler's live view for the Recruiter; the sealed status (n of m in) for everyone else until the reveal; the frozen snapshot after. */
export const summaryOpen = (input: SummaryOpenInput = {} as SummaryOpenInput) => callTool<SummaryOpenOutput>("summary.open", input);

export type SummaryReadinessCheckInput = {
  requisition_id: string;
  application_id: string;
  cycle_id?: string | null;
};

export type SummaryReadinessCheckOutput = {
  requisition_id: string;
  application_id: string;
  cycle_id: string | null;
  ready: boolean;
  rounds_not_held: Record<string, unknown>[];
  scorecards_outstanding: Record<string, unknown>[];
  evidence_unconfirmed: Record<string, unknown>[];
  values_uncovered: string[];
  concerns_open: number;
  exceptions: Record<string, unknown>[];
  gaps: string[];
  loop_status: string;
};

/** Rounds not held, scorecards outstanding, uncovered values, open concerns (existence only), exceptions recorded — the Compile and Reveal gates in plain words; never a score. */
export const summaryReadinessCheck = (input: SummaryReadinessCheckInput = {} as SummaryReadinessCheckInput) => callTool<SummaryReadinessCheckOutput>("summary.readiness_check", input);

export type SummaryRecompileInput = {
  requisition_id: string;
  application_id: string;
  cycle_id?: string | null;
};

export type SummaryRecompileOutput = {
  sheet_id: string | null;
  cycle_id: string | null;
  application_id: string;
  candidate: string;
  projection: string;
  status: string;
  compiled_at: string | null;
  revealed_at: string | null;
  scorecards_in: number;
  scorecards_due: number;
  snapshot: Record<string, unknown> | null;
  annotations: Record<string, unknown>[];
  outcome: Record<string, unknown> | null;
  can: Record<string, boolean>;
  release?: Record<string, unknown> | null;
  notes?: Record<string, unknown> | null;
  receipt_id?: number | null;
};

/** Recompile a sealed sheet after a revision or a late scorecard. */
export const summaryRecompile = (input: SummaryRecompileInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SummaryRecompileOutput>("summary.recompile", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SummaryRecordExceptionInput = {
  requisition_id: string;
  kind: "debrief_started_early" | "application_excluded_from_cycle" | "scorecard_missing_at_reveal" | "round_not_held_at_reveal" | "coverage_gap_at_reveal" | "exercise_waived" | "checkpoint_skipped" | "intake_finalized_with_absent_party" | "hold_resumed_with_changes" | "reference_gate_waived";
  reason: string;
  application_id?: string | null;
  interview_id?: string | null;
  cycle_id?: string | null;
  names_missing?: Record<string, unknown>[];
};

export type SummaryRecordExceptionOutput = {
  requisition_id: string;
  application_id: string | null;
  sheet_id: string | null;
  status: string;
  effective_at: string;
  receipt_id?: number | null;
};

/** Record a typed process exception (closed list) that passes exactly one process gate before the reveal; hard rules are never exceptable. */
export const summaryRecordException = (input: SummaryRecordExceptionInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SummaryRecordExceptionOutput>("summary.record_exception", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SummaryReleaseInput = {
  requisition_id: string;
  application_id: string;
  cycle_id?: string | null;
};

export type SummaryReleaseOutput = {
  sheet_id: string | null;
  cycle_id: string | null;
  application_id: string;
  candidate: string;
  projection: string;
  status: string;
  compiled_at: string | null;
  revealed_at: string | null;
  scorecards_in: number;
  scorecards_due: number;
  snapshot: Record<string, unknown> | null;
  annotations: Record<string, unknown>[];
  outcome: Record<string, unknown> | null;
  can: Record<string, boolean>;
  release?: Record<string, unknown> | null;
  notes?: Record<string, unknown> | null;
  receipt_id?: number | null;
};

/** Release the compiled Summary Sheet after the Recruiter's review: the readiness control that makes the debrief Ready. Scores stay sealed until the reveal. */
export const summaryRelease = (input: SummaryReleaseInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SummaryReleaseOutput>("summary.release", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SummaryRemindCompileInput = {
  subject_id: string;
  subject_type?: string | null;
  timer_id?: string | null;
  occurrence_key?: string | null;
  definition?: string | null;
  scheduled_at?: string | null;
  params?: Record<string, unknown>;
};

export type SummaryRemindCompileOutput = {
  no_op?: boolean;
  reason?: string | null;
  effect?: string | null;
  receipt_id?: number | null;
};

/** F-46: the last scorecard landed two business days ago and the sheet is not compiled. */
export const summaryRemindCompile = (input: SummaryRemindCompileInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SummaryRemindCompileOutput>("summary.remind_compile", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type SummarySaveNotesInput = {
  body?: string | null;
  source_refs?: SourceRef[];
  state?: "active" | "dismissed";
  expected_version?: number | null;
  requisition_id: string;
  application_id: string;
  cycle_id?: string | null;
};

export type SummarySaveNotesOutput = {
  subject_type: string;
  subject_id: string;
  kind: string;
  note: Record<string, unknown>;
  versions: number;
  receipt_id?: number | null;
};

/** Save the Recruiter's summary notes as a new version citing the scorecards they draw on (edit, correct or dismiss); the words are the Recruiter's own. */
export const summarySaveNotes = (input: SummarySaveNotesInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<SummarySaveNotesOutput>("summary.save_notes", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type TalentDesignateInput = {
  requisition_id: string;
  application_id: string;
  reason_added: "finalist" | "alternate" | "silver_medalist" | "timing_decline" | "timing_withdrawal";
  segment?: string | null;
  tags?: string[];
  notes?: string | null;
  last_contact_at?: string | null;
};

export type TalentDesignateOutput = {
  membership_id: string;
  candidate_id: string;
  reason_added: string;
  basis: string | null;
  receipt_id?: number | null;
};

/** Designate a candidate from this requisition into the talent community on purpose: an eligible finalist, alternate or timing-related decline or withdrawal, or a silver medalist; the same Candidate ID, with segment, tags, last contact and notes. Never automatic. */
export const talentDesignate = (input: TalentDesignateInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<TalentDesignateOutput>("talent.designate", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type TimelineRequisitionInput = {
  requisition_id: string;
  limit?: number;
  before?: string | null;
};

export type TimelineRequisitionTimelineRow = {
  id: string;
  kind: string;
  at: string;
  tz: string;
  actor_name: string | null;
  on_behalf_of_name: string | null;
  source: string;
  visibility_class: string;
  application_id: string | null;
  facts: Record<string, unknown>;
  artifact_refs: unknown[];
};

export type TimelineRequisitionOutput = {
  requisition_id: string;
  rows: TimelineRequisitionTimelineRow[];
  more: boolean;
};

/** Requisition timeline: the request through approval, posting, intake, panel, offer, hold, cancel, reopen and archive, newest first, each row with actor, time, source and artifacts, trimmed to the reader's classes. */
export const timelineRequisition = (input: TimelineRequisitionInput = {} as TimelineRequisitionInput) => callTool<TimelineRequisitionOutput>("timeline.requisition", input);

export type TimelineViewInput = {
  subject_type?: "requisition" | "candidate";
  id: string;
  kinds?: string[] | null;
  limit?: number;
  cursor?: string | null;
};

export type ArtifactLink = {
  type: string;
  id: string;
  label_key: string;
  route?: string | null;
  open_tool?: string | null;
  version?: number | null;
  status?: string | null;
};

export type TimelineRowOut = {
  id: string;
  kind: string;
  sentence_key: string;
  at: string;
  tz: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_roles: string[];
  on_behalf_of_id: string | null;
  source: string;
  requisition_id: string | null;
  request_id: string | null;
  application_id: string | null;
  candidate_id: string | null;
  facts: Record<string, unknown>;
  artifacts: ArtifactLink[];
};

export type TimelineViewOutput = {
  subject_type: string;
  id: string;
  rows: TimelineRowOut[];
  next_cursor: string | null;
};

/** The timeline of a requisition or a candidate: who did what, when and from where, with links to the records each event concerns that you may open. Events of a requisition you are not on are left out. */
export const timelineView = (input: TimelineViewInput = {} as TimelineViewInput) => callTool<TimelineViewOutput>("timeline.view", input);

export type TodayQueueInput = {
  section?: string | null;
  requisition_id?: string | null;
  limit?: number;
};

export type TodayQueueRow = {
  id: string;
  kind: string;
  glyph: string;
  title: string;
  body: string | null;
  route: string | null;
  ref_type: string | null;
  ref_id: string | null;
  due_at: string | null;
  created_at: string;
  overdue: boolean;
  family: string;
  for_person_name?: string | null;
  action_item_id?: string | null;
  category?: string | null;
  priority?: number | null;
  sla_tier?: string | null;
};

export type Tile = {
  key: string;
  label: string;
  count: number | null;
  route: string;
  note?: string | null;
};

export type TodayQueueOutput = {
  tiles: Tile[];
  needs_you: TodayQueueRow[];
  overdue: TodayQueueRow[];
  waiting: TodayQueueRow[];
  upcoming: TodayQueueRow[];
  recent: TodayQueueRow[];
  counts: Record<string, number>;
  badge: number;
  admin_badge?: number | null;
  nav_counts: Record<string, number>;
};

/** Today: Attention Required · Overdue · Pending Others · Upcoming · Recent, with the role's tiles and the nav badge count. */
export const todayQueue = (input: TodayQueueInput = {} as TodayQueueInput) => callTool<TodayQueueOutput>("today.queue", input);

export type WorkshopResetRoomInput = {
  requisition_id: string;
  confirmation: "reset";
};

export type WorkshopResetRoomOutput = {
  reset?: boolean;
  room?: number | null;
  old_requisition_id: string;
  title?: string | null;
  withdrawn?: boolean;
  new_requisition_id?: string | null;
  application_id?: string | null;
  hm_interview_id?: string | null;
  rounds?: Record<string, unknown>;
  steps?: string[];
  calls?: number;
  failed_step?: string | null;
  failure?: string | null;
  receipt_id?: number | null;
};

/** Reset a development Workshop room after typing reset: the old candidate is withdrawn and the room is built again as a new requisition through the application's own tools; a room's Admin resets only its own room. */
export const workshopResetRoom = (input: WorkshopResetRoomInput, idempotencyKey?: string, opts: CallOpts = {}) => callTool<WorkshopResetRoomOutput>("workshop.reset_room", input, { ...(idempotencyKey ? { idempotencyKey } : {}), ...opts });

export type WorkshopRoomsInput = Record<string, unknown>;

export type Room = {
  room: number | null;
  requisition_id: string;
  title: string | null;
  level: string | null;
  department: string | null;
  created_at: string;
  candidate_name: string | null;
  application_id: string | null;
  stage: string | null;
  hm_scorecard: string;
  interviewers: ScorecardState[];
  submitted: number;
  live: boolean;
};

export type ScorecardState = {
  round_label: string;
  seat_name: string;
  seat_account: string | null;
  interview_status: string;
  scorecard_status: string;
};

export type WorkshopRoomsOutput = {
  room: number | null;
  rooms: Room[];
};

/** List the development Workshop's rooms (one requisition each) with the state of every scorecard; a room's Admin sees its own room. */
export const workshopRooms = (input: WorkshopRoomsInput = {} as WorkshopRoomsInput) => callTool<WorkshopRoomsOutput>("workshop.rooms", input);


export const TOOL_NAMES = [
  "access.approve_request",
  "access.decline",
  "access.expire",
  "access.extend",
  "access.grant",
  "access.list",
  "access.list_global",
  "access.request",
  "access.revoke",
  "action_center.list",
  "admin.adapters",
  "admin.archive_requisition",
  "admin.archive_search",
  "admin.files_list",
  "admin.integration_open",
  "admin.integration_resolve",
  "admin.integration_retry",
  "admin.integrations",
  "admin.org_scopes_list",
  "admin.outbox.fault_next",
  "admin.outbox.mark_handled",
  "admin.outbox.retry",
  "admin.outbox_list",
  "admin.outbox_open",
  "admin.people_list",
  "admin.person_open",
  "admin.place_legal_hold",
  "admin.release_legal_hold",
  "admin.reset_world",
  "admin.scheduling.simulate_response",
  "admin.secrets",
  "admin.set_capabilities",
  "admin.set_hrbp_routing",
  "admin.set_org_scope",
  "admin.system",
  "admin.tick",
  "admin.time_travel",
  "admin.timers.mark_handled",
  "admin.timers.rearm",
  "admin.timers.run",
  "admin.verify_ledger",
  "admin.view_as.start",
  "admin.view_as.stop",
  "admin.worker_status",
  "archive.open",
  "audit.query",
  "candidate.abandon_parse",
  "candidate.add",
  "candidate.ask_recruiter",
  "candidate.attach_resume",
  "candidate.batch_assemble",
  "candidate.batch_decide",
  "candidate.batch_open",
  "candidate.batch_send",
  "candidate.close_at_source",
  "candidate.community_add",
  "candidate.community_remove",
  "candidate.community_touch",
  "candidate.create",
  "candidate.decide_advance",
  "candidate.decide_resume",
  "candidate.disposition_early",
  "candidate.duplicate_open",
  "candidate.hold",
  "candidate.import",
  "candidate.link_to_requisition",
  "candidate.list",
  "candidate.log_message",
  "candidate.mark_internal",
  "candidate.match",
  "candidate.merge",
  "candidate.notify_parse_result",
  "candidate.notify_results_late",
  "candidate.open",
  "candidate.parse_open",
  "candidate.parse_resume",
  "candidate.profile",
  "candidate.prompt_held_review",
  "candidate.qualify",
  "candidate.record_attestation",
  "candidate.reinstate",
  "candidate.release",
  "candidate.remind_advance_decision",
  "candidate.remind_batch_review",
  "candidate.remind_screen_decision",
  "candidate.resolve_duplicate",
  "candidate.reverse_decision",
  "candidate.search",
  "candidate.set_internal_profile",
  "candidate.slate_open",
  "candidate.timeline",
  "candidate.unmerge",
  "candidate.update_profile",
  "candidate.withdraw",
  "candidate.write_sourcing_feedback",
  "closeout.open",
  "closeout.reach_start",
  "comp.approve",
  "comp.approve_out_of_range",
  "comp.approve_total_rewards",
  "comp.open_worksheet",
  "comp.propose",
  "comp.record_internal_comparison",
  "comp.record_leader_approval",
  "comp.record_package",
  "comp.remind_all",
  "comp.reopen",
  "comp_review.assign_reviewer",
  "comp_review.fetch_market_data",
  "comp_review.finalize",
  "comp_review.open",
  "comp_review.queue",
  "comp_review.request_change",
  "comp_review.save",
  "comp_review.start",
  "conduct.acknowledge",
  "conduct.clear",
  "conduct.disclose_to_hm",
  "conduct.escalate",
  "conduct.list",
  "conduct.list_mine",
  "conduct.open",
  "conduct.remind_acknowledge",
  "conduct.reroute",
  "config.approve",
  "config.list",
  "config.open",
  "config.options",
  "config.propose",
  "config.reject",
  "config.validate",
  "coordinator.assign",
  "debrief.advance_agenda",
  "debrief.agree_additional_interview",
  "debrief.cancel",
  "debrief.close",
  "debrief.confirm_attendance",
  "debrief.confirm_outcome",
  "debrief.countersign_reversal",
  "debrief.create",
  "debrief.decline_attendance",
  "debrief.lapse_reversal",
  "debrief.list_mine",
  "debrief.notify_overdue",
  "debrief.open",
  "debrief.order_alternates",
  "debrief.record_absence",
  "debrief.record_attendance",
  "debrief.record_hrbp_note",
  "debrief.record_outcome",
  "debrief.record_reference_decision",
  "debrief.record_search_outcome",
  "debrief.request_additional_interview",
  "debrief.reschedule",
  "debrief.resolve_divergence",
  "debrief.reveal",
  "debrief.reverse_outcome",
  "debrief.save_preparation_notes",
  "debrief.schedule",
  "debrief.schedule_additional_round",
  "debrief.set_alignment",
  "debrief.submit_written_input",
  "debrief.withdraw_additional_interview",
  "debrief.withdraw_reversal",
  "delegation.approve_proposal",
  "delegation.expire",
  "delegation.grant",
  "delegation.list",
  "delegation.list_global",
  "delegation.propose",
  "delegation.revoke",
  "disposition.add_keep_warm",
  "disposition.draft_closing_note",
  "disposition.open_worklist",
  "disposition.record",
  "disposition.record_note_sent",
  "disposition.remind_worklist",
  "exercise.assign_reviewers",
  "exercise.complete",
  "exercise.coverage_check",
  "exercise.decide_lapse",
  "exercise.evaluate",
  "exercise.extend",
  "exercise.open",
  "exercise.prompt_lapse_decision",
  "exercise.prompt_reminder",
  "exercise.record_not_required",
  "exercise.record_return",
  "exercise.record_sent",
  "exercise.remind_evaluation",
  "exercise.review",
  "exercise.skip",
  "exercise.withdraw",
  "feedback.export",
  "feedback.list",
  "feedback.open",
  "feedback.report",
  "feedback.report_anonymous",
  "feedback.triage",
  "file.download",
  "file.mark_abandoned",
  "file.upload",
  "guide.faq",
  "guide.open",
  "guide.reference",
  "handoff.record_item",
  "handoff.validate",
  "health.evaluate_daily",
  "health.open",
  "home.open",
  "hris.confirm",
  "hris.correct",
  "intake.acknowledge",
  "intake.comment_part1",
  "intake.comment_plan",
  "intake.finalize",
  "intake.mark_incomplete_at_kickoff",
  "intake.mark_ready",
  "intake.notify_not_held",
  "intake.notify_stalled",
  "intake.open",
  "intake.plan_open",
  "intake.print",
  "intake.prompt_reschedule",
  "intake.record_held",
  "intake.remind_finalize",
  "intake.remind_part1",
  "intake.reopen",
  "intake.save_comp_notes",
  "intake.save_part0",
  "intake.save_part1",
  "intake.save_part2",
  "intake.schedule_session",
  "intake.send_part1",
  "intake.set_plan_variant",
  "intake.validate_plan",
  "integration.poll_background_checks",
  "integration.poll_hris",
  "integration.poll_responses",
  "integration.sync_lapse",
  "measures.evaluate_daily",
  "offer.choose_next_path",
  "offer.extend_to_alternate",
  "offer.open",
  "offer.publish_manager_handoff",
  "offer.record_acceptance",
  "offer.record_acceptance_reversal",
  "offer.record_decline",
  "offer.record_letter_sent",
  "offer.record_response",
  "offer.record_verbal",
  "offer.remind_letter",
  "offer.request_offer_call",
  "offer.withdraw",
  "packet.approve_set",
  "packet.compute_cc",
  "packet.draft_kickoff",
  "packet.generate",
  "packet.generate_auto",
  "packet.issue_link",
  "packet.list_mine",
  "packet.open",
  "packet.print",
  "packet.readiness_check",
  "packet.reissue",
  "packet.release",
  "packet.remind_kickoff_approval",
  "packet.remind_unopened",
  "packet.review",
  "packet.revoke_link",
  "packet.send_kickoff",
  "panel.accept",
  "panel.ack_summary",
  "panel.answer_question",
  "panel.ask_hiring_manager",
  "panel.assign_skills",
  "panel.assign_values",
  "panel.build",
  "panel.change_sla_breach",
  "panel.decide_exercise",
  "panel.decline",
  "panel.decline_round",
  "panel.flag_nonresponse",
  "panel.hm_review_change",
  "panel.list_questions",
  "panel.my_acknowledgments",
  "panel.notify_question_unanswered",
  "panel.open",
  "panel.open_change",
  "panel.people",
  "panel.preview_change",
  "panel.progress_change",
  "panel.promote_backup",
  "panel.record_change",
  "panel.remind_acknowledgment",
  "panel.remind_change_review",
  "panel.request_change",
  "panel.review_change",
  "panel.submit_change",
  "panel.update_change",
  "people.options",
  "permissions.matrix",
  "proposal.deny",
  "proposal.open",
  "readiness.open",
  "recruiter.assign",
  "recruiter.decide_reassignment",
  "recruiter.queue",
  "recruiter.recommendation",
  "recruiter.request_reassignment",
  "recruiter.withdraw_reassignment",
  "reference.complete",
  "reference.consult_hrbp",
  "reference.notify_waiting",
  "reference.open",
  "reference.record_contact",
  "reference.record_not_elected",
  "reference.record_requested",
  "reference.record_unable",
  "reference.set_requirement",
  "reference.waive_gate",
  "report.drill",
  "report.export",
  "report.options",
  "report.publish_model",
  "report.run",
  "request.answer",
  "request.approve",
  "request.archive",
  "request.ask",
  "request.assign_reviewer",
  "request.certify",
  "request.claim_review",
  "request.create",
  "request.draft_options",
  "request.list",
  "request.list_mine",
  "request.mark_dormant",
  "request.notify_decision_late",
  "request.notify_not_opened",
  "request.notify_question_unanswered",
  "request.open",
  "request.queue_hrbp",
  "request.queue_ta",
  "request.record_after_the_fact",
  "request.remind_reviewer",
  "request.return",
  "request.review",
  "request.submit",
  "request.update",
  "request.withdraw",
  "requisition.acknowledge_flag",
  "requisition.approve_reminder_wording",
  "requisition.approve_reopen",
  "requisition.archive",
  "requisition.change_openings",
  "requisition.close",
  "requisition.create",
  "requisition.decide_state_change",
  "requisition.draft_phase_flag",
  "requisition.list",
  "requisition.note_delay",
  "requisition.notify_batch_unsent",
  "requisition.notify_not_posted",
  "requisition.notify_phase_behind",
  "requisition.notify_target_start_at_risk",
  "requisition.notify_tta_crossing",
  "requisition.notify_ttf_crossing",
  "requisition.open",
  "requisition.post",
  "requisition.post_update",
  "requisition.propose_openings",
  "requisition.raise_phase_flag",
  "requisition.record_after_the_fact",
  "requisition.reopen",
  "requisition.repeat_phase_flag",
  "requisition.request_state_change",
  "requisition.resume",
  "requisition.signals",
  "requisition.update_strategy",
  "requisition.withdraw_state_change",
  "round.backfill",
  "round.book",
  "round.cancel",
  "round.check_touchpoints",
  "round.correct_held_at",
  "round.expire_hold",
  "round.flag_decline",
  "round.hold_slot",
  "round.mark_held",
  "round.mark_loop_scheduled",
  "round.mark_not_held",
  "round.move",
  "round.notify_no_show",
  "round.notify_scheduling_late",
  "round.open",
  "round.prompt_candidate_followup",
  "round.propose",
  "round.read_free_busy",
  "round.reject_time",
  "round.reschedule",
  "saved_view.delete",
  "saved_view.list",
  "saved_view.save",
  "scheduling.availability",
  "scheduling.book",
  "scheduling.cancel",
  "scheduling.complete",
  "scheduling.debrief_readiness",
  "scheduling.decline_reschedule",
  "scheduling.expire_hold",
  "scheduling.flag_no_response",
  "scheduling.flag_scheduling_late",
  "scheduling.hold",
  "scheduling.open",
  "scheduling.propose",
  "scheduling.record_availability",
  "scheduling.remind",
  "scheduling.request",
  "scheduling.request_reschedule",
  "scheduling.reschedule",
  "scheduling.reschedule_round",
  "scheduling.resolve_sync",
  "scheduling.respond",
  "scheduling.retry_sync",
  "scheduling.set_travel",
  "scheduling.sla_breach",
  "scheduling.sla_half",
  "scheduling.sync_lapse",
  "scheduling.workbench",
  "scorecard.append_addendum",
  "scorecard.confirm_recorded",
  "scorecard.correct_metadata",
  "scorecard.lift_waiver",
  "scorecard.mark_unconfirmed",
  "scorecard.open",
  "scorecard.raise_conduct_concern",
  "scorecard.record_for",
  "scorecard.remind",
  "scorecard.revise",
  "scorecard.revise_in_room",
  "scorecard.save_draft",
  "scorecard.send_reminder",
  "scorecard.submit",
  "scorecard.waive",
  "screen.decide",
  "screen.open_result",
  "screen.record_outcome",
  "screen.save_form",
  "screen.submit_result",
  "shell.nav",
  "shell.notices",
  "shell.search",
  "sla.tier_reached",
  "spine.rebuild",
  "summary.attach_exercise_evaluation",
  "summary.compile",
  "summary.export",
  "summary.open",
  "summary.readiness_check",
  "summary.recompile",
  "summary.record_exception",
  "summary.release",
  "summary.remind_compile",
  "summary.save_notes",
  "talent.designate",
  "timeline.requisition",
  "timeline.view",
  "today.queue",
  "workshop.reset_room",
  "workshop.rooms"
] as const;
